// TicketDetail component - Main ticket view and management
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import agentIcon from "@/assets/agent-icon.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import PaymentOrderStatus from './PaymentOrderStatus';
import LexInvoiceButton from './LexInvoiceButton';
import TrackingTimeline from './TrackingTimeline';
import { useNotesByTicket, useCreateNote } from '@/hooks/useNotes';
import type { Note } from '@/services/notes.service';
import { useSnippets, useSnippetUsage } from '@/hooks/useSnippets';
import type { Snippet } from '@/services/snippets.service';
import { useTags } from '@/hooks/useTags';
import type { Tag } from '@/services/tags.service';
import { useTicketSystemTags } from '@/hooks/useSystemTags';
import type { TicketSystemTag } from '@/services/systemTags.service';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor, { type SlashCommandState } from "@/components/RichTextEditor";
import { useVoiceActions } from "@/voice/useVoiceActions";
import Lottie from "lottie-react";
import claudeAnimation from "@/assets/lottie/claude.json";
import openaiAnimation from "@/assets/lottie/openai.json";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import EditableTabs from "@/components/EditableTabs";
import ticketsService, { type Attachment } from '@/services/tickets.service';
import shopifyService from '@/services/shopify.service';
import jtlService from '@/services/jtl.service';
import aiService, { type ConversationMessage, type AISessionMessage, type TicketInfo, type OrderInfo, SUPPORTED_LANGUAGES, type Language, type AISuggestion, type AIChatResponse } from '@/services/ai.service';
import { toast } from 'sonner';
import usersService, { type Agent } from '@/services/users.service';
import organizationsService from '@/services/organizations.service';
import { watchersService, watcherName, type Watcher } from '@/services/watchers.service';
import { pinnedTicketsService } from '@/services/pinnedTickets.service';
import signaturesService from '@/services/signatures.service';
import customViewsService from '@/services/customViews.service';
import { transformApiCommentToFrontend, transformApiTicketToFrontend, type Comment, getPriorityColor, getPriorityLabel, mapFrontendStatusToApi, getStatusColor, getStatusLabel, getSlaStatusColor, getSlaStatusLabel, formatSlaTime } from '@/lib/ticketUtils';
import { getRecentLanguages, addRecentLanguage, getDefaultTargetLanguage } from '@/lib/translationUtils';
import { fetchAttachmentObjectUrl } from '@/lib/attachments';
import { useCreateRevision } from '@/hooks/useAITraining';
import TicketListModal from '@/components/TicketListModal';
import TicketListModalWithSelection from '@/components/TicketListModalWithSelection';
import useAuthStore from '@/stores/authStore';
import useTicketTabsStore from '@/stores/ticketTabsStore';
import AIAssistantPanel from '@/components/AIAssistantPanel';
import { CaseDetail } from '@/components/AgentCasesView';
import StrobiAvatar from '@/components/StrobiAvatar';
import DOMPurify from "dompurify";

// Configure DOMPurify to open all links in new tab
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

// External Tickets (New System)
import { useExternalTicketsByParent, useCreateExternalTicket } from '@/hooks/useExternalTickets';
import type { ExternalTicket } from '@/services/external-tickets.service';
import CreateExternalTicketDialog from '@/components/CreateExternalTicketDialog';

// Legacy External Conversations (Keep for backward compatibility)
import { useExternalConversationsByTicket, useExternalConversationMessages, useSendExternalMessage } from '@/hooks/useExternalConversations';
import type { ExternalConversation, ExternalConversationMessage } from '@/services/external-conversations.service';
import CreateExternalConversationDialog from '@/components/CreateExternalConversationDialog';

// Reminders
import { useRemindersByTicket, useCreateReminder, useUpdateReminder, useDeleteReminder, useCompleteReminder, useUncompleteReminder } from '@/hooks/useReminders';
import type { Reminder } from '@/services/reminders.service';
import ReminderDialog from '@/components/ReminderDialog';
import ReminderList from '@/components/ReminderList';

// SLA Widget
import SLAWidget from '@/components/SLAWidget';
import TicketInfoGlass from '@/components/TicketInfoGlass';

// Shop Integrations
import { useShopIntegrations } from '@/hooks/useShopIntegrations';

import {
  CalendarIcon,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  ExternalLink,
  FileText,
  Forward,
  MoreHorizontal,
  PaperclipIcon,
  Plus,
  MessageSquare,
  Settings,
  Sparkles,
  Tag,
  Tag as TagIcon,
  Trash,
  User,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  AlertTriangle,
  Archive,
  X,
  Merge,
  Link2Off,
  Copy,
  Languages,
  Loader2,
  Upload,
  Image as ImageIcon,
  Package,
  Mail,
  Paperclip,
  Pin,
  PinOff,
  ArrowUpCircle,
  StickyNote,
  Zap,
  XCircle,
  CheckCircle,
  Hash,
  FileSignature,
  Eye,
  Download,
} from "lucide-react";

import { formatDate, formatDateLong, formatDateTime, formatDayTime, formatTime } from '@/lib/format';
import { dfLocale } from '@/lib/dateFnsLocale';
import { useTranslation } from 'react-i18next';

// ==================== INLINE ATTACHMENT PREVIEW COMPONENT ====================
interface InlineAttachmentPreviewProps {
  attachmentsUrl?: string;
  attachmentCount: number;
}

const InlineAttachmentPreview: React.FC<InlineAttachmentPreviewProps> = ({
  attachmentsUrl,
  attachmentCount
}) => {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    currentIndex: number;
    content: string | null;
  }>({
    isOpen: false,
    currentIndex: 0,
    content: null
  });

  const previewableAttachments = useMemo(
    () => attachments.filter((attachment) =>
      attachment.contentType.startsWith('image/') || attachment.contentType === 'application/pdf'
    ),
    [attachments]
  );

  useEffect(() => {
    const loadAttachments = async () => {
      if (!attachmentsUrl || loaded) return;

      setLoading(true);
      try {
        const response = await ticketsService.getAttachmentsByUrl(attachmentsUrl);
        setAttachments(response.data.attachments || response.data || []);
        setLoaded(true);
      } catch (error) {
        console.error('Failed to load attachments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAttachments();
  }, [attachmentsUrl, loaded]);

  // Keyboard navigation for preview modal
  useEffect(() => {
    if (!previewModal.isOpen) return;

    const hasPrev = previewModal.currentIndex > 0;
    const hasNext = previewModal.currentIndex < previewableAttachments.length - 1;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrev) {
        // Navigate to previous
        const newIndex = previewModal.currentIndex - 1;
        const newAttachment = previewableAttachments[newIndex];

        if (previewModal.content) {
          URL.revokeObjectURL(previewModal.content);
        }

        setPreviewModal({
          isOpen: true,
          currentIndex: newIndex,
          content: null
        });

        (async () => {
          try {
            const previewUrl = newAttachment.previewUrl || newAttachment.downloadUrl;
            const url = await fetchAttachmentObjectUrl(previewUrl);
            setPreviewModal(prev => ({
              ...prev,
              content: url
            }));
          } catch (error) {
            console.error('Navigation preview failed:', error);
          }
        })();
      } else if (e.key === 'ArrowRight' && hasNext) {
        // Navigate to next
        const newIndex = previewModal.currentIndex + 1;
        const newAttachment = previewableAttachments[newIndex];

        if (previewModal.content) {
          URL.revokeObjectURL(previewModal.content);
        }

        setPreviewModal({
          isOpen: true,
          currentIndex: newIndex,
          content: null
        });

        (async () => {
          try {
            const previewUrl = newAttachment.previewUrl || newAttachment.downloadUrl;
            const url = await fetchAttachmentObjectUrl(previewUrl);
            setPreviewModal(prev => ({
              ...prev,
              content: url
            }));
          } catch (error) {
            console.error('Navigation preview failed:', error);
          }
        })();
      } else if (e.key === 'Escape') {
        if (previewModal.content) {
          URL.revokeObjectURL(previewModal.content);
        }
        setPreviewModal({ isOpen: false, currentIndex: 0, content: null });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewModal.isOpen, previewModal.currentIndex, previewModal.content, previewableAttachments]);

  useEffect(() => {
    if (!previewModal.isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [previewModal.isOpen]);

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) {
      return <ImageIcon className="h-3.5 w-3.5" />;
    }
    if (
      contentType === 'application/pdf' ||
      contentType.includes('word') ||
      contentType.includes('excel') ||
      contentType.includes('spreadsheet') ||
      contentType.includes('powerpoint') ||
      contentType.includes('presentation')
    ) {
      return <FileText className="h-3.5 w-3.5" />;
    }
    return <Paperclip className="h-3.5 w-3.5" />;
  };

  const openPreview = async (attachment: Attachment, index?: number) => {
    if (attachment.contentType.startsWith('image/') || attachment.contentType === 'application/pdf') {
      // Preview in modal - use previewUrl or downloadUrl from backend
      try {
        const attachmentIndex = index !== undefined
          ? index
          : previewableAttachments.findIndex(a => a.id === attachment.id);

        setPreviewModal({
          isOpen: true,
          currentIndex: attachmentIndex,
          content: null
        });

        // previewUrl ist entweder unsere API (auth-pflichtig) oder eine
        // öffentliche R2-URL — der Helper unterscheidet das selbst.
        const previewUrl = attachment.previewUrl || attachment.downloadUrl;
        const url = await fetchAttachmentObjectUrl(previewUrl);
        setPreviewModal(prev => ({
          ...prev,
          content: url
        }));
      } catch (error) {
        console.error('Preview failed:', error);
      }
    } else {
      // Direct download for other files - use downloadUrl from backend
      try {
        // Use downloadUrl from backend (already R2 public URL with CORS)
        const downloadUrl = attachment.downloadUrl || attachment.previewUrl;

        if (!downloadUrl) {
          console.error('No download URL available for attachment:', attachment);
          alert(t('ticket:msg.noDownloadUrl', 'Download-URL nicht verfügbar'));
          return;
        }

        // // console.log('Downloading attachment:', { filename: attachment.filename, downloadUrl });

        // Schlägt der Download fehl, wirft der Helper — der catch-Block
        // unten öffnet die URL dann als Fallback in einem neuen Tab.
        const url = await fetchAttachmentObjectUrl(downloadUrl);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
        // Fallback: try to open directly in new tab
        const downloadUrl = attachment.downloadUrl || attachment.previewUrl;
        if (downloadUrl) {
          window.open(downloadUrl, '_blank');
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="mt-2 flex items-center gap-2">
        <div className="h-3 w-3 animate-spin rounded-full border border-gray-300 border-t-gray-600"></div>
        <span className="text-xs text-gray-500">{t('common:state.loading', 'Laden...')}</span>
      </div>
    );
  }

  if (attachments.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mt-2">
        <div className="flex flex-wrap gap-1.5">
          {attachments.map((attachment) => (
            <button
              key={attachment.id}
              onClick={() => openPreview(attachment)}
              className="ticket-attachment-chip group inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200/80 bg-transparent px-2.5 py-1.5 text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
              title={`${attachment.filename} (${(attachment.size / 1024).toFixed(1)} KB)`}
            >
              <span className="flex flex-shrink-0 items-center justify-center text-slate-400">
                {getFileIcon(attachment.contentType)}
              </span>
              <span className="max-w-36 truncate text-xs font-medium">{attachment.filename}</span>
              <span className="flex-shrink-0 text-[10px] font-normal tabular-nums text-slate-400">
                {attachment.size > 1024 * 1024
                  ? `${(attachment.size / (1024 * 1024)).toFixed(1)} MB`
                  : `${(attachment.size / 1024).toFixed(1)} KB`
                }
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Compact attachment quick-look drawer */}
      {previewModal.isOpen && (() => {
        const currentAttachment = previewableAttachments[previewModal.currentIndex];
        const hasPrev = previewModal.currentIndex > 0;
        const hasNext = previewModal.currentIndex < previewableAttachments.length - 1;

        const navigatePreviewTo = async (newIndex: number) => {
          if (newIndex >= 0 && newIndex < previewableAttachments.length && newIndex !== previewModal.currentIndex) {
            const newAttachment = previewableAttachments[newIndex];

            if (previewModal.content) {
              URL.revokeObjectURL(previewModal.content);
            }

            setPreviewModal({
              isOpen: true,
              currentIndex: newIndex,
              content: null
            });

            try {
              const previewUrl = newAttachment.previewUrl || newAttachment.downloadUrl;
              const url = await fetchAttachmentObjectUrl(previewUrl);
              setPreviewModal(prev => ({
                ...prev,
                content: url
              }));
            } catch (error) {
              console.error('Navigation preview failed:', error);
            }
          }
        };

        const downloadCurrent = () => {
          if (!previewModal.content || !currentAttachment) return;
          const link = document.createElement('a');
          link.href = previewModal.content;
          link.download = currentAttachment.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };

        const closeModal = () => {
          if (previewModal.content) {
            URL.revokeObjectURL(previewModal.content);
          }
          setPreviewModal({ isOpen: false, currentIndex: 0, content: null });
        };

        return (
          <div className="attachment-preview-layer">
            <button
              type="button"
              className="attachment-preview-backdrop"
              onClick={closeModal}
              aria-label={t('ticket:ui.escClose', 'Vorschau schließen')}
            />

            <aside
              className="attachment-preview-drawer"
              role="dialog"
              aria-modal="true"
              aria-label={currentAttachment?.filename || 'Dateivorschau'}
            >
              <header className="attachment-preview-topbar">
                <div className="attachment-preview-identity">
                  <span className="attachment-preview-file-icon" aria-hidden="true">
                    {currentAttachment?.contentType.startsWith('image/')
                      ? <ImageIcon className="h-4 w-4" />
                      : <FileText className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0">
                    <h3>{currentAttachment?.filename}</h3>
                    <p>
                      {currentAttachment?.size
                        ? currentAttachment.size > 1024 * 1024
                          ? `${(currentAttachment.size / (1024 * 1024)).toFixed(2)} MB`
                          : `${(currentAttachment.size / 1024).toFixed(1)} KB`
                        : '—'}
                      <span aria-hidden="true">·</span>
                      {previewModal.currentIndex + 1} / {previewableAttachments.length}
                    </p>
                  </div>
                </div>

                <div className="attachment-preview-actions">
                  <button
                    type="button"
                    onClick={downloadCurrent}
                    disabled={!previewModal.content}
                    title={t('common:action.download', 'Herunterladen')}
                    aria-label={t('common:action.download', 'Herunterladen')}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    title={t('ticket:ui.escClose', 'ESC Schließen')}
                    aria-label={t('ticket:ui.escClose', 'ESC Schließen')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </header>

              <div className={cn(
                'attachment-preview-canvas',
                currentAttachment?.contentType.startsWith('image/') ? 'is-image' : 'is-document'
              )}>
                {previewModal.content ? (
                  currentAttachment?.contentType.startsWith('image/') ? (
                    <img
                      src={previewModal.content}
                      alt={currentAttachment.filename}
                      className="attachment-preview-image"
                    />
                  ) : currentAttachment?.contentType === 'application/pdf' ? (
                    <iframe
                      src={previewModal.content}
                      className="attachment-preview-document"
                      title={t('ticket:section.pdfPreview', 'PDF Preview')}
                    />
                  ) : null
                ) : (
                  <div className="attachment-preview-loading">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>{t('ticket:state.loading', 'Wird geladen...')}</span>
                  </div>
                )}

                {hasPrev && (
                  <button
                    type="button"
                    onClick={() => navigatePreviewTo(previewModal.currentIndex - 1)}
                    className="attachment-preview-nav is-prev"
                    title={t('ticket:action.prev', 'Vorheriges (←)')}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                )}

                {hasNext && (
                  <button
                    type="button"
                    onClick={() => navigatePreviewTo(previewModal.currentIndex + 1)}
                    className="attachment-preview-nav is-next"
                    title={t('ticket:action.next', 'Nächstes (→)')}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>

              {previewableAttachments.length > 1 && (
                <footer className="attachment-preview-tray">
                  <div className="attachment-preview-tray-list">
                    {previewableAttachments.map((attachment, index) => (
                      <button
                        type="button"
                        key={attachment.id}
                        className={cn('attachment-preview-tray-item', index === previewModal.currentIndex && 'is-active')}
                        onClick={() => navigatePreviewTo(index)}
                      >
                        {attachment.contentType.startsWith('image/')
                          ? <ImageIcon className="h-3.5 w-3.5" />
                          : <FileText className="h-3.5 w-3.5" />}
                        <span>{attachment.filename}</span>
                      </button>
                    ))}
                  </div>
                  <span className="attachment-preview-shortcut">← →</span>
                </footer>
              )}
            </aside>
          </div>
        );
      })()}
    </>
  );
};

interface TicketDetailProps {
  ticketId?: string;
  onTicketSelect?: (ticketId: string) => void;
  onCreateTicket?: () => void;
}

// OpenTicket interface moved to ticketTabsStore.ts

// Helper function to format ticket number - tam format göster
const formatTicketNumber = (ticketNumber?: string): string => {
  return ticketNumber || '';
};

// Helper function to translate snippet categories to German
const getCategoryLabel = (category: string): string => {
  const categoryLabels: Record<string, string> = {
    general: 'Allgemein',
    greeting: 'Begrüßung',
    closing: 'Abschluss',
    shipping: 'Versand',
    refund: 'Rückerstattung',
    support: 'Support',
  };
  return categoryLabels[category] || category.charAt(0).toUpperCase() + category.slice(1);
};

const TicketDetail = ({
  ticketId: propTicketId,
  onTicketSelect,
  onCreateTicket,
}: TicketDetailProps = {}) => {
  const { t } = useTranslation();
  const { ticketId: routeTicketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const ticketId = propTicketId || routeTicketId || 'TKT-1234';

  // Get custom view info from sessionStorage (primary) or navigation state (fallback)
  // This keeps URLs clean while preserving view context across navigations
  const viewContext = useMemo(() => {
    // Try sessionStorage first
    const stored = sessionStorage.getItem('ticketViewContext');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse view context from sessionStorage', e);
      }
    }
    // Fallback to navigation state
    const state = location.state as any;
    if (state?.fromViewId) {
      return {
        fromViewId: state.fromViewId,
        fromViewName: state.fromViewName,
        sortField: state.sortField,
        sortDirection: state.sortDirection,
        listPosition: state.listPosition,
        totalTickets: state.totalTickets,
        viewFilterConfig: state.viewFilterConfig
      };
    }
    return null;
  }, [location.state]);

  const fromViewId = viewContext?.fromViewId;
  const fromViewName = viewContext?.fromViewName;
  const listSortField = viewContext?.sortField;
  const listSortDirection = viewContext?.sortDirection;
  const listPosition = viewContext?.listPosition;
  const listTicketNumber = (location.state as any)?.listTicketNumber;
  const totalTickets = viewContext?.totalTickets;
  const viewFilterConfig = viewContext?.viewFilterConfig;

  // Ticket tabs from persistent store
  const {
    openTickets,
    activeTabId: storeActiveTab,
    addTab,
    removeTab,
    setActiveTab,
    updateTab,
    replaceTab,
  } = useTicketTabsStore();

  // Sync activeTab with URL's ticketId - URL always takes priority
  useEffect(() => {
    if (ticketId && ticketId !== 'TKT-1234' && storeActiveTab !== ticketId) {
      setActiveTab(ticketId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]); // Only sync when URL ticketId changes

  // Use ticketId from URL as the active tab (URL is source of truth)
  const activeTab = ticketId !== 'TKT-1234' ? ticketId : storeActiveTab;

  // (Preview and modal handlers exist below in legacy code; no duplicates added)

  // // // console.log('TicketDetail - ticketId:', ticketId, 'routeTicketId:', routeTicketId);

  // API query for ticket details and conversation - use ticketId initially, activeTab when switched
  const { data: ticketData, isLoading, error } = useQuery({
    queryKey: ['ticket', activeTab || ticketId],
    queryFn: () => ticketsService.getTicketById(activeTab || ticketId),
    enabled: !!(activeTab || ticketId) && (activeTab || ticketId) !== 'TKT-1234',
    staleTime: 30000, // 30 saniye cache
    retry: 1, // Sadece 1 retry
  });

  // Query for merged tickets
  const { data: mergedTicketsData, isLoading: isLoadingMergedTickets } = useQuery({
    queryKey: ['mergedTickets', activeTab || ticketId],
    queryFn: () => ticketsService.getMergedTickets(activeTab || ticketId),
    enabled: !!(activeTab || ticketId) && (activeTab || ticketId) !== 'TKT-1234'
  });

  // Build view params for adjacent tickets query - use same buildQueryString as TicketList
  const adjacentViewParams = useMemo(() => {
    // If we have a view (fromViewId or sortField), always create params for view-based navigation
    if (!fromViewId && !listSortField) return undefined;

    // Build sort config matching TicketList format
    const sortConfig = {
      sortBy: listSortField === 'createdAt' ? 'created_at' : listSortField === 'updatedAt' ? 'updated_at' : listSortField || 'created_at',
      sortOrder: (listSortDirection || 'desc') as 'asc' | 'desc'
    };

    // Use the same buildQueryString method that TicketList uses
    // This ensures identical filtering behavior
    const queryString = viewFilterConfig
      ? customViewsService.buildQueryString(viewFilterConfig, sortConfig)
      : new URLSearchParams(sortConfig as any).toString();

    // Parse query string into params object
    const urlParams = new URLSearchParams(queryString);
    const params: Record<string, string> = {};
    urlParams.forEach((value, key) => {
      params[key] = value;
    });

    // Always add sortBy and sortOrder if not present
    if (!params.sortBy) params.sortBy = sortConfig.sortBy;
    if (!params.sortOrder) params.sortOrder = sortConfig.sortOrder;

    // // console.log('Adjacent view params:', { fromViewId, listSortField, viewFilterConfig, sortConfig, queryString, params });
    return params;
  }, [viewFilterConfig, listSortField, listSortDirection, fromViewId]);

  // Query for adjacent tickets (previous/next navigation) - supports custom view filtering
  const { data: adjacentTicketsData } = useQuery({
    queryKey: ['adjacentTickets', activeTab || ticketId, adjacentViewParams],
    queryFn: () => ticketsService.getAdjacentTickets(activeTab || ticketId, adjacentViewParams),
    enabled: !!(activeTab || ticketId) && (activeTab || ticketId) !== 'TKT-1234'
  });

  // Query for tags
  const { data: tagsData } = useTags();

  // Query for system tags (AI generated)
  const { data: systemTagsData } = useTicketSystemTags(activeTab || ticketId);

  // Query for organizations
  const { data: orgsResponse } = useQuery({
    queryKey: ['organizations-list'],
    queryFn: () => organizationsService.getOrganizations(),
    staleTime: 10 * 60 * 1000,
  });

  // // // console.log('Active tab:', activeTab, 'Base ticketId:', ticketId, 'Querying:', activeTab || ticketId);

  // Debug attachment comments
  // if (ticketData?.data?.comments) {
  //   // // console.log('API Comments:', ticketData.data.comments);
  //   const attachmentComments = ticketData.data.comments.filter(c => c.hasAttachments);
  //   // // console.log('Comments with attachments:', attachmentComments);
  //   if (attachmentComments.length > 0) {
  //     // // console.log('First attachment comment:', attachmentComments[0]);
  //     // // console.log('AttachmentsUrl:', attachmentComments[0].attachmentsUrl);
  //   }
  // }

  // Reply mutation - uses ticketId from data parameter to support tab switching
  const replyMutation = useMutation({
    mutationFn: (data: { ticketId: string; content: string; sendEmail?: boolean; attachment_ids?: string[]; status?: string; ticketStatus?: string }) =>
      ticketsService.replyToTicket(data.ticketId, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ticket', variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });
      // Clear form after successful reply
      setCustomerMessage("");
      setAttachmentIds([]);
      setUploadedFiles([]);
      setSignaturePanelOpen(false);
      setSignatureEditMode(false);
      setSignatureEditContent(signatureHtml || '');
    },
    onError: (error: any) => {
      // console.error('Fehler beim Senden der Antwort:', error);
      alert(t('ticket:msg.sendFailed', 'Fehler beim Senden der Antwort'));
    }
  });

  // Update ticket mutation for assignee changes and subject updates
  // Uses ticketId from data parameter to support tab switching
  const updateTicketMutation = useMutation({
    mutationFn: (data: { ticketId: string; subject?: string; assigneeId?: string | null; status?: string; priority?: string; tags?: string[]; organizationId?: string }) => {
      // // console.log('🚀 Updating ticket:', { ticketId: data.ticketId, data });
      return ticketsService.updateTicket(data.ticketId, data);
    },
    onSuccess: async (response, variables) => {
      // // console.log('✅ Ticket update successful:', response);
      // Invalidate and refetch immediately to update UI
      await queryClient.invalidateQueries({ queryKey: ['ticket', variables.ticketId] });
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });
      await queryClient.refetchQueries({ queryKey: ['ticket', variables.ticketId] });
    },
    onError: (error: any) => {
      console.error('❌ Fehler beim Aktualisieren des Tickets:', error);
      console.error('Error details:', error?.response?.data);
      alert(t('ticket:msg.updateFailed', 'Fehler beim Aktualisieren des Tickets: ') + (error?.response?.data?.message || error.message));
    }
  });

  // Create parent ticket and merge current as child
  const createParentTicketMutation = useMutation({
    mutationFn: async (data: { email: string; customerName: string; subject: string; priority: string; organizationId: string }) => {
      // 1. Create new parent ticket
      const newTicket = await ticketsService.createTicket({
        customerEmail: data.email,
        customerName: data.customerName || undefined,
        subject: data.subject,
        priority: data.priority as "low" | "normal" | "high" | "urgent",
        organizationId: data.organizationId,
      });

      // 2. Merge current ticket as child to new parent
      await ticketsService.mergeTickets(newTicket.data.ticket.id, [ticketId]);

      return newTicket;
    },
    onSuccess: (response) => {
      // // console.log('✅ Parent ticket created and merged:', response.data.ticket.id);
      const newParentTicketId = response.data.ticket.id;

      // Close modal and reset form
      setIsCreateParentTicketModalOpen(false);
      setParentTicketForm({ email: "", customerName: "", subject: "", priority: "normal", organizationId: "" });

      // Hard refresh to new parent ticket page
      window.location.href = `/tickets/${newParentTicketId}`;
    },
    onError: (error: any) => {
      console.error('❌ Failed to create parent ticket:', error);
      alert('Fehler: ' + (error?.response?.data?.message || error.message));
    }
  });

  // Delete ticket mutation
  const deleteTicketMutation = useMutation({
    mutationFn: (ticketId: string) => ticketsService.deleteTicket(ticketId),
    onSuccess: () => {
      // // console.log('✅ Ticket deleted successfully');
      navigate('/tickets');
    },
    onError: (error: any) => {
      console.error('❌ Failed to delete ticket:', error);
      const errorMessage = error?.response?.data?.message || 'Ticket konnte nicht gelöscht werden';
      alert(`Fehler: ${errorMessage}`);
    }
  });

  const updateSpamStatusMutation = useMutation({
    mutationFn: ({ ticketId, isSpam }: { ticketId: string; isSpam: boolean }) =>
      ticketsService.updateSpamStatus(ticketId, isSpam),
    onSuccess: () => {
      // // console.log('✅ Spam status updated successfully');
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });
      setSpamDialogOpen(false);
    },
    onError: (error: any) => {
      console.error('❌ Failed to update spam status:', error);
      const errorMessage = error?.response?.data?.message || 'Archiv-Status konnte nicht aktualisiert werden';
      alert(`Fehler: ${errorMessage}`);
    }
  });

  // Unmerge ticket mutation
  const unmergeMutation = useMutation({
    mutationFn: (ticketId: string) => ticketsService.unmergeTicket(ticketId),
    onSuccess: (response) => {
      // // console.log('✅ Ticket unmerged successfully');
      queryClient.invalidateQueries({ queryKey: ['ticket', activeTab || ticketId] });
      queryClient.invalidateQueries({ queryKey: ['mergedTickets', activeTab || ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });
      alert(t('ticket:msg.unmerged', '✅ Ticket erfolgreich getrennt!'));
    },
    onError: (error: any) => {
      console.error('❌ Failed to unmerge ticket:', error);
      const errorMessage = error?.response?.data?.message || 'Ticket konnte nicht getrennt werden';
      alert(`Fehler: ${errorMessage}`);
    }
  });

  // Promote to parent mutation
  const promoteToParentMutation = useMutation({
    mutationFn: (ticketId: string) => ticketsService.promoteToParent(ticketId),
    onSuccess: (response) => {
      // // console.log('✅ Ticket promoted to parent successfully');
      queryClient.invalidateQueries({ queryKey: ['ticket', activeTab || ticketId] });
      queryClient.invalidateQueries({ queryKey: ['mergedTickets', activeTab || ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });
      alert(`✅ Ticket erfolgreich zum Parent befördert! ${response.data.affectedTickets} Tickets aktualisiert.`);
      // Redirect to the new parent ticket
      navigate(`/tickets/${response.data.newParentId}`);
    },
    onError: (error: any) => {
      console.error('❌ Failed to promote ticket to parent:', error);
      const errorMessage = error?.response?.data?.message || 'Ticket konnte nicht zum Parent befördert werden';
      alert(`Fehler: ${errorMessage}`);
    }
  });

  // Merge tickets mutation (from detail page)
  const mergeFromDetailMutation = useMutation({
    mutationFn: ({ parentTicketId, childTicketIds }: { parentTicketId: string; childTicketIds: string[] }) =>
      ticketsService.mergeTickets(parentTicketId, childTicketIds),
    onSuccess: (response) => {
      // // console.log('✅ Tickets merged successfully from detail page');
      queryClient.invalidateQueries({ queryKey: ['ticket', activeTab || ticketId] });
      queryClient.invalidateQueries({ queryKey: ['mergedTickets', activeTab || ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });
      setShowMergeTicketModal(false);
      alert(`✅ ${response.data.mergedTickets.length} Ticket(s) erfolgreich zusammengeführt!`);
    },
    onError: (error: any) => {
      console.error('❌ Failed to merge tickets:', error);
      const errorMessage = error?.response?.data?.message || 'Tickets konnten nicht zusammengeführt werden';
      alert(`Fehler: ${errorMessage}`);
    }
  });

  // Handle merge tickets from modal
  const handleMergeTickets = (selectedTicketIds: string[]) => {
    const currentTicketId = activeTab || ticketId;
    mergeFromDetailMutation.mutate({
      parentTicketId: currentTicketId,
      childTicketIds: selectedTicketIds
    });
  };

  // Handle status and priority changes - direct update without confirmation
  const handleStatusChange = (newStatus: string) => {
    const currentTicketId = activeTab || ticketId;
    updateTicketMutation.mutate({
      ticketId: currentTicketId,
      status: newStatus
    });
  };

  const handlePriorityChange = (newPriority: string) => {
    const currentTicketId = activeTab || ticketId;
    updateTicketMutation.mutate({
      ticketId: currentTicketId,
      priority: newPriority
    });
  };

  // Use API data when available, fallback to defaults
  const ticket = ticketData?.data?.ticket ? transformApiTicketToFrontend(ticketData.data.ticket) : undefined;
  const apiComments = ticketData?.data?.comments || [];

  // // // console.log('TicketData exists:', !!ticketData);
  // // // console.log('API Comments length:', apiComments.length);
  // if (apiComments.length > 0) {
  //   // // console.log('First API comment:', apiComments[0]);
  // }

  // Use ticket requester email for order check (always use ticket owner, not comment email)
  const customerEmail = ticket?.requester?.email;


  // Get organization's shop integrations to determine platform type
  const organizationId = ticket?.organization?.id || ticket?.organization_id;
  const { data: shopIntegrationsData } = useShopIntegrations(organizationId || '');

  // Find active integration and determine platform type
  const activeIntegration = shopIntegrationsData?.data?.integrations?.find(
    (integration: any) => integration.is_active
  );
  const platformType = activeIntegration?.platform_type; // 'shopify' or 'jtl'

  // Shop orders query - conditionally call Shopify or JTL service based on platform type
  const { data: shopifyData, isLoading: isLoadingShopify, error: shopifyError } = useQuery({
    queryKey: ['shop-orders', platformType, customerEmail, organizationId],
    queryFn: async () => {
      if (!customerEmail || !organizationId) return null;

      if (platformType === 'jtl') {
        return await jtlService.getOrdersByEmail(customerEmail, organizationId);
      } else if (platformType === 'shopify') {
        return await shopifyService.getOrdersByEmail(customerEmail, organizationId);
      }

      return null;
    },
    enabled: !!customerEmail && !!organizationId && !!platformType && !(ticketData?.data?.ticket as any)?.internal_target_org_id, // internal: sipariş sorgusu anlamsız
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    retry: 1
  });


  // Agents query for assignee dropdown
  const { data: agentsData, isLoading: isLoadingAgents } = useQuery({
    queryKey: ['agents'],
    queryFn: () => usersService.getAgents(true, 'active'),
    staleTime: 10 * 60 * 1000, // 10 minutes cache
  });

  // Beobachter (Watchers): bu ticket'ı takip eden ekip üyeleri
  const watchersTicketId = activeTab || ticketId;
  const { data: watchers = [] } = useQuery({
    queryKey: ['ticket-watchers', watchersTicketId],
    queryFn: () => watchersService.list(watchersTicketId),
    enabled: !!watchersTicketId,
  });
  const addWatcherMutation = useMutation({
    mutationFn: (userId: string) => watchersService.add(watchersTicketId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-watchers', watchersTicketId] }),
  });
  const removeWatcherMutation = useMutation({
    mutationFn: (userId: string) => watchersService.remove(watchersTicketId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-watchers', watchersTicketId] }),
  });

  // Angeheftete Tickets (Pins): Pin-Status des aktiven Tabs
  const pinTicketId = activeTab || ticketId;
  const { data: pinStatus } = useQuery({
    queryKey: ['ticket-pin', pinTicketId],
    queryFn: () => pinnedTicketsService.check(pinTicketId!),
    enabled: !!pinTicketId,
  });
  const isTicketPinned = !!pinStatus?.pinned;
  const invalidatePinQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['ticket-pin', pinTicketId] });
    queryClient.invalidateQueries({ queryKey: ['pinned-tickets'] });
  };
  const pinTicketMutation = useMutation({
    mutationFn: () => pinnedTicketsService.pin(pinTicketId!),
    onSuccess: () => {
      invalidatePinQueries();
      toast.success(t('ticket:msg.pinned', 'Ticket angeheftet'));
    },
    onError: () => toast.error(t('ticket:msg.pinFailed', 'Anheften fehlgeschlagen')),
  });
  const unpinTicketMutation = useMutation({
    mutationFn: () => pinnedTicketsService.unpinByTicket(pinTicketId!),
    onSuccess: () => {
      invalidatePinQueries();
      toast.success(t('ticket:msg.unpinned', 'Anheftung entfernt'));
    },
    onError: () => toast.error(t('ticket:msg.pinFailed', 'Anheften fehlgeschlagen')),
  });
  const isPinMutating = pinTicketMutation.isPending || unpinTicketMutation.isPending;

  // Reminder hooks
  const { data: reminders = [], isLoading: isLoadingReminders } = useRemindersByTicket(activeTab || ticketId);
  const createReminderMutation = useCreateReminder();
  const updateReminderMutation = useUpdateReminder();
  const deleteReminderMutation = useDeleteReminder();
  const completeReminderMutation = useCompleteReminder();
  const uncompleteReminderMutation = useUncompleteReminder();

  // AI Revision mutation
  const revisionMutation = useCreateRevision();

  // Pre-generated AI Suggestion query
  const { data: preGeneratedSuggestion, refetch: refetchSuggestion } = useQuery({
    queryKey: ['ai-suggestion', ticketId],
    queryFn: async () => {
      const response = await aiService.getSuggestion(ticketId);
      return response.data;
    },
    enabled: !!ticketId,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  // State for dismissing the pre-generated suggestion
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);

  // State for tracking expanded long comments (collapse by default)
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const MAX_COMMENT_LENGTH = 5000; // Characters before collapsing

  const toggleCommentExpanded = (commentId: string) => {
    setExpandedComments(prev => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const isCommentLong = (content: string) => content && content.length > MAX_COMMENT_LENGTH;

  const getTruncatedContent = (content: string) => {
    if (!content || content.length <= MAX_COMMENT_LENGTH) return content;
    // Find a good break point (end of tag or space)
    const truncated = content.substring(0, MAX_COMMENT_LENGTH);
    // Try to close any open tags by finding last complete tag
    const lastTagEnd = truncated.lastIndexOf('>');
    if (lastTagEnd > MAX_COMMENT_LENGTH - 500) {
      return truncated.substring(0, lastTagEnd + 1) + '...';
    }
    return truncated + '...';
  };

  // Reset dismissed state when ticket changes
  useEffect(() => {
    setSuggestionDismissed(false);
  }, [ticketId]);

  // Function to use the pre-generated suggestion
  const handleUsePreGeneratedSuggestion = async () => {
    if (preGeneratedSuggestion?.suggestion_text) {
      setCustomerMessage(preGeneratedSuggestion.suggestion_text);
      setAiSuggestion(preGeneratedSuggestion.suggestion_text);
      setHasAIResponse(true);
      setSuggestionDismissed(true);

      // Mark as used in backend
      try {
        await aiService.markSuggestionUsed(preGeneratedSuggestion.id);
      } catch (error) {
        console.error('Failed to mark suggestion as used:', error);
      }
    }
  };

  // Function to regenerate AI suggestion
  const handleRegeneratePreSuggestion = async () => {
    if (!ticketId) return;
    setIsRegenerating(true);
    try {
      const response = await aiService.generateSuggestion(ticketId);
      if (response.data) {
        await refetchSuggestion();
        setSuggestionDismissed(false);
      }
    } catch (error) {
      console.error('Failed to regenerate suggestion:', error);
    } finally {
      setIsRegenerating(false);
    }
  };

  const [subject, setSubject] = useState(
    ticket?.subject || "Bestellung #12345 - Fehlender Artikel im Paket",
  );
  const [status, setStatus] = useState(ticket?.status || "open");
  const [replyStatus, setReplyStatus] = useState<string>("pending"); // Status for reply button
  const [isEditingSubject, setIsEditingSubject] = useState(false);

  // Ref for conversation scrolling
  const conversationRef = useRef<HTMLDivElement>(null);
  const externalConversationRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when comments load or change
  useEffect(() => {
    if (apiComments.length > 0) {
      // Wait for DOM to render and then scroll
      setTimeout(() => {
        const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
        if (scrollArea) {
          scrollArea.scrollTop = scrollArea.scrollHeight;
        }
      }, 100);
    }
  }, [apiComments.length, apiComments]);

  // Load real attachment counts when comments change
  useEffect(() => {
    const loadAllAttachmentCounts = async () => {
      const commentsWithAttachments = apiComments.filter(comment => comment.hasAttachments && comment.attachmentsUrl);

      for (const comment of commentsWithAttachments) {
        try {
          const urlKey = comment.attachmentsUrl!;
          if (realAttachmentCounts[urlKey] !== undefined) continue;

          const response = await ticketsService.getAttachmentsByUrl(urlKey);
          const attachments = response.data.attachments || response.data || [];
          const realCount = attachments.length;

          setRealAttachmentCounts(prev => ({
            ...prev,
            [urlKey]: realCount
          }));

          if (realCount !== comment.attachmentCount) {
            // // // console.log(`Fixed attachment count for ${urlKey}: ${comment.attachmentCount} → ${realCount}`);
          }
        } catch (error) {
          // console.error(`Failed to load attachment count for comment ${comment.id}:`, error);
        }
      }
    };

    if (apiComments.length > 0) {
      loadAllAttachmentCounts();
    }
  }, [apiComments]);

  // Update local state when API data loads
  useEffect(() => {
    if (ticket) {
      // Only update subject if not currently editing
      if (!isEditingSubject) {
        setSubject(ticket.subject);
      }
      setStatus(ticket.status);
      setAssignedUser(ticket.ownerId); // Set assigned user from ticket
      setPriority(ticket.priority || "normal"); // Set priority from ticket
    }
  }, [ticket, isEditingSubject]);

  // Add or replace ticket tab - only when ticket.id changes (separate effect to prevent infinite loop)
  useEffect(() => {
    if (ticket?.id) {
      const ticketData = {
        id: ticket.id,
        subject: ticket.subject,
        status: ticket.status,
        ticket_number: ticket.ticketNumber,
      };

      // Check sessionStorage for navigation flag (survives page reload)
      const isNavFlag = sessionStorage.getItem('ticket-tabs-navigating');
      const replaceId = sessionStorage.getItem('ticket-tabs-replace-id');

      if (isNavFlag === 'true' && replaceId) {
        sessionStorage.removeItem('ticket-tabs-navigating');
        sessionStorage.removeItem('ticket-tabs-replace-id');
        replaceTab(ticketData, replaceId);
      } else {
        addTab(ticketData);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id]); // Only run when ticket.id changes

  // Sync ticket tags with local state
  useEffect(() => {
    if (ticket?.tags && Array.isArray(ticket.tags)) {
      setSelectedTags(ticket.tags);
    } else {
      setSelectedTags([]);
    }
  }, [ticket?.tags]);

  const [customerMessage, setCustomerMessage] = useState("");
  const [externalMessage, setExternalMessage] = useState("");
  const [isInternalCustomer, setIsInternalCustomer] = useState(false);

  // Signature state: fetched on ticket load, shown via toggle button
  const [signatureHtml, setSignatureHtml] = useState<string | null>(null);
  const [signatureEnabled, setSignatureEnabled] = useState(true); // will signature be appended on send
  const [signaturePanelOpen, setSignaturePanelOpen] = useState(false); // is preview panel visible
  const [signatureEditMode, setSignatureEditMode] = useState(false);
  const [signatureEditContent, setSignatureEditContent] = useState('');

  useEffect(() => {
    const orgId = ticket?.organization?.id || ticket?.organization_id;
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await signaturesService.getSignature(orgId);
        if (cancelled) return;
        if (response.success && response.data && response.data.is_active && response.data.signature_html) {
          setSignatureHtml(response.data.signature_html);
          setSignatureEditContent(response.data.signature_html);
          setSignatureEnabled(true);
        } else {
          setSignatureHtml(null);
          setSignatureEnabled(false);
        }
      } catch {
        if (!cancelled) {
          setSignatureHtml(null);
          setSignatureEnabled(false);
        }
      }
      setSignaturePanelOpen(false);
      setSignatureEditMode(false);
    })();
    return () => { cancelled = true; };
  }, [activeTab, ticketId, ticket?.organization?.id, ticket?.organization_id]);

  // Append signature to any message content
  const appendSignature = useCallback((content: string) => {
    // Interne Konversation: imza YOK (şirket içi mesajda kurumsal imza gürültü)
    if ((ticketData?.data?.ticket as any)?.internal_target_org_id) return content;
    if (!signatureEnabled || !signatureHtml) return content;
    const sig = signatureEditMode ? signatureEditContent : signatureHtml;
    if (!sig) return content;
    return `${content}<!-- SIGNATURE_START --><div style="border-top:1px solid #e2e8f0;padding-top:12px;margin-top:12px">${sig}</div>`;
  }, [signatureEnabled, signatureHtml, signatureEditMode, signatureEditContent]);

  // Build final content with signature appended
  const getContentWithSignature = useCallback(() => {
    return appendSignature(customerMessage);
  }, [customerMessage, appendSignature]);

  // Send button options
  // sendAsNote: NOT persisted - always starts as false, resets after sending
  const [sendAsNote, setSendAsNote] = useState(false);
  // Navigation options: persisted in localStorage
  const [goToNextAfterSend, setGoToNextAfterSend] = useState(() => {
    const saved = localStorage.getItem('ticketDetail_goToNextAfterSend');
    return saved === 'true';
  });
  const [goToViewAfterSend, setGoToViewAfterSend] = useState(() => {
    const saved = localStorage.getItem('ticketDetail_goToViewAfterSend');
    return saved === 'true';
  });

  // Persist navigation options to localStorage (NOT sendAsNote)
  useEffect(() => {
    localStorage.setItem('ticketDetail_goToNextAfterSend', String(goToNextAfterSend));
  }, [goToNextAfterSend]);

  useEffect(() => {
    localStorage.setItem('ticketDetail_goToViewAfterSend', String(goToViewAfterSend));
  }, [goToViewAfterSend]);

  // New parent ticket modal state
  const [isCreateParentTicketModalOpen, setIsCreateParentTicketModalOpen] = useState(false);
  const [parentTicketForm, setParentTicketForm] = useState({
    email: "",
    customerName: "",
    subject: "",
    priority: "normal" as "low" | "normal" | "high" | "urgent",
    organizationId: "",
  });

  // Auto-fill parent ticket form when modal opens (only once)
  useEffect(() => {
    if (isCreateParentTicketModalOpen && ticket && ticketData?.data?.ticket) {
      const apiTicket = ticketData.data.ticket;

      setParentTicketForm({
        email: ticket.requester?.email || ticket.customerEmail || apiTicket.customer_email || "",
        customerName: ticket.requester?.name || ticket.customerName || apiTicket.customer_name || "",
        subject: ticket.subject || "",
        priority: (ticket.priority || "normal") as "low" | "normal" | "high" | "urgent",
        organizationId: ticket.organizationId || apiTicket.organization_id || "",
      });
    }
  }, [isCreateParentTicketModalOpen]);

  const [uploadedFiles, setUploadedFiles] = useState<Array<{
    id: string;
    filename: string;
    size: number;
    contentType: string;
  }>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [isInternalExternal, setIsInternalExternal] = useState(false);
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [revisionPrompt, setRevisionPrompt] = useState('');
  const [activeExternalTab, setActiveExternalTab] = useState("shipping");
  const [activeExternalTicketId, setActiveExternalTicketId] = useState<string | null>(null);
  const [showTextSnippets, setShowTextSnippets] = useState<{ isOpen: boolean; context: 'customer' | 'external' }>({
    isOpen: false,
    context: 'customer'
  });
  // Slash command snippet popover state
  const [slashCommandState, setSlashCommandState] = useState<SlashCommandState>({
    isOpen: false,
    query: '',
    position: { top: 0, left: 0 },
  });
  const [slashSelectedIndex, setSlashSelectedIndex] = useState(0);
  const slashPopoverRef = useRef<HTMLDivElement>(null);
  const [showTagPopover, setShowTagPopover] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [realAttachmentCounts, setRealAttachmentCounts] = useState<Record<string, number>>({});
  const [showAddTicketModal, setShowAddTicketModal] = useState(false);
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const [showTabLeftArrow, setShowTabLeftArrow] = useState(false);
  const [showTabRightArrow, setShowTabRightArrow] = useState(false);

  const updateTabArrows = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    setShowTabLeftArrow(el.scrollLeft > 0);
    setShowTabRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateTabArrows();
    const el = tabsScrollRef.current;
    if (el) {
      el.addEventListener("scroll", updateTabArrows);
      window.addEventListener("resize", updateTabArrows);
    }
    return () => {
      if (el) el.removeEventListener("scroll", updateTabArrows);
      window.removeEventListener("resize", updateTabArrows);
    };
  }, [updateTabArrows, openTickets]);

  const handleTabScroll = (direction: "left" | "right") => {
    const el = tabsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === "left" ? -200 : 200, behavior: "smooth" });
  };
  const [showMergeTicketModal, setShowMergeTicketModal] = useState(false);
  const [externalUploadedFiles, setExternalUploadedFiles] = useState<{ id: string; name: string; size: number }[]>([]);
  const [isUploadingExternal, setIsUploadingExternal] = useState(false);
  // Gallery states
  const [showAttachmentChoice, setShowAttachmentChoice] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryData, setGalleryData] = useState<Array<{
    ticketId: string;
    ticketNumber: string;
    ticketSubject: string;
    isExternal: boolean;
    attachments: Array<{
      id: string;
      filename: string;
      contentType: string;
      sizeBytes: number;
      s3Key: string;
      createdAt: string;
      isInline: boolean;
      previewUrl: string | null;
      downloadUrl: string;
    }>;
  }>>([]);
  const [isLoadingGallery, setIsLoadingGallery] = useState(false);
  const [selectedGalleryAttachments, setSelectedGalleryAttachments] = useState<Set<string>>(new Set());
  const [galleryTarget, setGalleryTarget] = useState<'main' | 'external'>('main');
  const [showExternalAttachmentChoice, setShowExternalAttachmentChoice] = useState(false);
  const [showKundentextPopover, setShowKundentextPopover] = useState(false);
  const [selectedKundentexts, setSelectedKundentexts] = useState<Set<string>>(new Set());
  const [kundentextDebugLogged, setKundentextDebugLogged] = useState(false);
  const [noteAttachmentPreview, setNoteAttachmentPreview] = useState<{
    isOpen: boolean;
    attachments: any[];
    currentIndex: number;
    content: string | null;
  }>({
    isOpen: false,
    attachments: [],
    currentIndex: 0,
    content: null
  });
  const { isAuthenticated, user: currentUser } = useAuthStore();
  const token = localStorage.getItem('accessToken');
  const [completionDate, setCompletionDate] = useState<Date | undefined>(
    undefined,
  );
  const [assignedUser, setAssignedUser] = useState<string | null>(null);
  const [priority, setPriority] = useState<string>(ticket?.priority || "normal");

  // Reminder states
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderListOpen, setReminderListOpen] = useState(false);
  const [reminderPopoverOpen, setReminderPopoverOpen] = useState(false);

  // Notes popover state
  const [notesPopoverOpen, setNotesPopoverOpen] = useState(false);
  const [selectedReminderDate, setSelectedReminderDate] = useState<Date | undefined>(undefined);
  const [editingReminder, setEditingReminder] = useState<Reminder | undefined>(undefined);

  // Spam status dialog state
  const [spamDialogOpen, setSpamDialogOpen] = useState(false);
  // Delete ticket dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  // Rechter Panel-Tab kontrolliert (damit der Agent den KI-Assistent-Tab zuverlässig öffnen kann)
  const [rightPanelTab, setRightPanelTab] = useState('ticket-info');
  // Vom Agenten gespeicherte, geprüfte Antwort (Mensch übernimmt/sendet)
  const [agentDraft, setAgentDraft] = useState<string | null>(null);
  useEffect(() => {
    if (!ticketId) { setAgentDraft(null); return; }
    aiService.getAgentDraft(ticketId).then(r => setAgentDraft(r?.data?.reply || null)).catch(() => setAgentDraft(null));
  }, [ticketId]);
  const [showAiSuggestionCard, setShowAiSuggestionCard] = useState(false);
  const [showAiPreviewInConversation, setShowAiPreviewInConversation] = useState(false); // AI preview in conversation area (default closed)
  const [showInlineRevision, setShowInlineRevision] = useState(false); // Inline revision input in conversation
  const [inlineRevisionPrompt, setInlineRevisionPrompt] = useState(''); // Inline revision text
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [revisionPopoverOpen, setRevisionPopoverOpen] = useState(false); // Revision popover state
  const [isGeneratingAI, setIsGeneratingAI] = useState(false); // Manual AI generation loading state
  const [aiProvider, setAiProvider] = useState<'claude' | 'gpt'>('claude'); // Cmd+K isteği hangi modele gitti (Lottie için)
  const [hasAIResponse, setHasAIResponse] = useState(false); // Track if AI has provided a response
  const [aiSessionHistory, setAiSessionHistory] = useState<AISessionMessage[]>([]); // AI chat session history
  const [currentTicketIndex, setCurrentTicketIndex] = useState(0);
  const [leftPanelWidth, setLeftPanelWidth] = useState(50);
  const [rightPanelWidth, setRightPanelWidth] = useState(35);
  const [orderPanelWidth, setOrderPanelWidth] = useState(25);
  const [isResizing, setIsResizing] = useState(false);
  const [isResizingOrder, setIsResizingOrder] = useState(false);
  // Composer yüksekliği kalıcı: kullanıcı ayırıcıyı sürükleyince localStorage'a
  // yazılır ve TÜM ticketlarda aynı kalır.
  const [customerInputHeight, setCustomerInputHeight] = useState(() => {
    const saved = Number(localStorage.getItem('ticketComposerHeight'));
    return Number.isFinite(saved) && saved >= 100 && saved <= 600 ? saved : 230;
  });
  const [externalInputHeight, setExternalInputHeight] = useState(200);
  const [isResizingCustomerInput, setIsResizingCustomerInput] = useState(false);
  const [isResizingExternalInput, setIsResizingExternalInput] = useState(false);
  const [isResizingConversationSummary, setIsResizingConversationSummary] =
    useState(false);
  const [conversationSummaryHeight, setConversationSummaryHeight] =
    useState(120);
  const [showCustomerTextMenu, setShowCustomerTextMenu] = useState(false);
  const [timelineVisibleCount, setTimelineVisibleCount] = useState(5);

  // Panel states per tab (only for Nebenkonversation now)
  const [tabPanelStates, setTabPanelStates] = useState<{
    [key: string]: {
      isNebenkonversationCollapsed: boolean;
    }
  }>({});

  // Translation states
  const [translatedMessages, setTranslatedMessages] = useState<Record<string, string>>({});
  const [translatingMessageId, setTranslatingMessageId] = useState<string | null>(null);
  const [translatePopoverOpen, setTranslatePopoverOpen] = useState<Record<string, boolean>>({});
  const [recentLanguages, setRecentLanguages] = useState<Language[]>([]);

  // Reply editor translation states
  const [translatingReplyEditor, setTranslatingReplyEditor] = useState(false);
  const [replyTranslatePopoverOpen, setReplyTranslatePopoverOpen] = useState(false);

  // Window width for responsive button visibility
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get current tab's panel states (only for Nebenkonversation now)
  const currentTabId = activeTab || ticketId;
  const currentPanelState = tabPanelStates[currentTabId] || {
    isNebenkonversationCollapsed: true
  };

  const setIsNebenkonversationCollapsed = (collapsed: boolean) => {
    setTabPanelStates(prev => ({
      ...prev,
      [currentTabId]: {
        ...prev[currentTabId],
        isNebenkonversationCollapsed: collapsed
      }
    }));
  };

  const isNebenkonversationCollapsed = currentPanelState.isNebenkonversationCollapsed;

  // Hide action buttons when window is narrow or external panel is open
  const showActionButtons = isNebenkonversationCollapsed && windowWidth > 1400;

  // Mock data
  const customer = {
    name: "Sarah Johnson",
    email: "sarah.johnson@example.com",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah",
    totalOrders: 2,
    totalSpent: "4.178,90 €",
    orders: [
      {
        id: "#12345",
        date: "2023-05-15",
        status: "Versendet",
        total: "129,99 €",
        shippingAddress: "123 Main St, Anytown, AN 12345",
        phone: "+49 123 456789",
        trackingNumber: "TRK9876543210",
        items: [
          { name: "Wireless Headphones", quantity: 1, price: "89,99 €" },
          { name: "Phone Case", quantity: 1, price: "29,99 €" },
          { name: "Screen Protector", quantity: 1, price: "10,01 €" },
        ],
      },
      {
        id: "#12346",
        date: "2023-04-20",
        status: "Geliefert",
        total: "4.048,91 €",
        shippingAddress: "123 Main St, Anytown, AN 12345",
        phone: "+49 123 456789",
        trackingNumber: "TRK9876543211",
        items: [
          { name: "Gaming Laptop", quantity: 1, price: "2.499,99 €" },
          { name: "Gaming Mouse", quantity: 1, price: "79,99 €" },
          { name: "Mechanical Keyboard", quantity: 1, price: "149,99 €" },
          { name: 'Monitor 27"4K', quantity: 1, price: "1.299,99 €" },
          { name: "Webcam HD", quantity: 1, price: "18,95 €" },
        ],
      },
    ],
  };

  const owner = {
    name: "Alex Meyer",
    email: "alex.m@support.com",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Alex",
  };

  // Always show order panel - get current ticket identifier 
  const currentTicketId = activeTab || ticketId;

  // Get all Shopify orders or fallback to mock data
  const getAllOrders = () => {
    // Return orders based on platform type
    if (shopifyData?.data?.orders && shopifyData.data.orders.length > 0) {
      // JTL backend already returns correctly formatted data with packages array
      if (platformType === 'jtl') {
        return shopifyData.data.orders;
      }
      // Shopify needs formatting
      return shopifyData.data.orders.map(order => shopifyService.formatOrderForUI(order));
    }
    return null;
  };

  // Get single order details for backwards compatibility
  const getOrderDetails = (ticketIdentifier: string) => {
    const allOrders = getAllOrders();
    if (allOrders && allOrders.length > 0) {
      return allOrders[0]; // Most recent order for summary
    }

    // Return null if no Shopify data available
    return null;
  };

  // Always generate order details regardless of API data
  const orderDetails = getOrderDetails(currentTicketId);

  // Dynamic conversation summary based on ticket
  const getConversationSummary = (ticketIdentifier: string, subject: string) => {
    const summaries = [
      `Kunde meldete ein Problem bezüglich seiner Bestellung. Agent bestätigte Problem und untersucht mit Lager.`,
      `Kunde berichtet über defektes Produkt. Rücksendung wird organisiert.`,
      `Frage zu Lieferzeiten. Agent klärt mit Versandteam ab.`,
      `Stornierungsanfrage erhalten. Prüfung des Bestellstatus läuft.`,
      `Kunde benötigt Hilfe bei der Produktkonfiguration. Support-Team wurde kontaktiert.`
    ];

    const hash = (ticketIdentifier || "").split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);

    return summaries[Math.abs(hash) % summaries.length];
  };

  // Get first message from API comments for AI summary
  const getFirstMessageContent = () => {
    if (apiComments && apiComments.length > 0) {
      // Get the first customer message
      const firstMessage = apiComments.find(comment => comment.customer_email);
      return firstMessage?.content || apiComments[0]?.content || '';
    }
    return '';
  };

  const firstMessageContent = getFirstMessageContent();

  // Format conversation for AI response suggestion
  const formatConversationForAI = (): ConversationMessage[] => {
    if (!apiComments || apiComments.length === 0) return [];

    return apiComments.map(comment => ({
      role: comment.isEmailComment ? 'customer' : 'agent',
      content: comment.content || ''
    }));
  };

  const conversationForAI = useMemo(() => formatConversationForAI(), [apiComments]);

  const sanitizeContent = useCallback((raw: string) => {
    let content = raw || '';
    // Strip signature block from conversation display
    const sigIndex = content.indexOf('<!-- SIGNATURE_START -->');
    if (sigIndex !== -1) {
      content = content.substring(0, sigIndex);
    }
    return DOMPurify.sanitize(content, { USE_PROFILES: { html: true } });
  }, []);

  const extractPlainText = (html: string) =>
    html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const customerMessagePlainText = useMemo(() => extractPlainText(customerMessage), [customerMessage]);
  const isCustomerMessageEmpty = customerMessagePlainText.length === 0;

  // AI conversation summary - manual trigger with localStorage cache (5 min)
  const [conversationSummary, setConversationSummary] = useState<string | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState<Error | null>(null);

  const SUMMARY_CACHE_KEY = `ai-summary-${ticketId}`;
  const SUMMARY_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Cleanup all expired AI summary caches on mount (prevents localStorage pollution)
  useEffect(() => {
    const cleanupExpiredAISummaries = () => {
      const keysToRemove: string[] = [];
      const now = Date.now();

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('ai-summary-')) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const { expiry } = JSON.parse(cached);
              if (now >= expiry) {
                keysToRemove.push(key);
              }
            }
          } catch {
            // Invalid JSON, remove it
            keysToRemove.push(key);
          }
        }
      }

      // Remove expired keys
      keysToRemove.forEach(key => localStorage.removeItem(key));

      if (keysToRemove.length > 0) {
        console.log(`Cleaned up ${keysToRemove.length} expired AI summary caches`);
      }
    };

    cleanupExpiredAISummaries();
  }, []); // Run once on mount

  // Check localStorage on mount for cached summary
  useEffect(() => {
    if (!ticketId) return;

    const cached = localStorage.getItem(SUMMARY_CACHE_KEY);
    if (cached) {
      try {
        const { summary, expiry } = JSON.parse(cached);
        if (Date.now() < expiry) {
          setConversationSummary(summary);
        } else {
          localStorage.removeItem(SUMMARY_CACHE_KEY);
        }
      } catch {
        localStorage.removeItem(SUMMARY_CACHE_KEY);
      }
    }
  }, [ticketId, SUMMARY_CACHE_KEY]);

  // Fetch conversation summary on demand
  const fetchConversationSummary = async () => {
    if (!ticketId || !firstMessageContent) return;

    setIsLoadingSummary(true);
    setSummaryError(null);

    try {
      const response = await aiService.generateConversationSummary(firstMessageContent, ticketId);
      const summary = response?.data?.summary;

      if (summary) {
        setConversationSummary(summary);
        // Cache in localStorage with expiry
        localStorage.setItem(SUMMARY_CACHE_KEY, JSON.stringify({
          summary,
          expiry: Date.now() + SUMMARY_CACHE_DURATION
        }));
      }
    } catch (error) {
      setSummaryError(error as Error);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  // AI response suggestion mutation (manual trigger instead of auto-load)
  const aiSuggestionMutation = useMutation({
    mutationFn: () => aiService.generateResponseSuggestion(conversationForAI),
    onSuccess: (data) => {
      if (data?.data?.suggestion) {
        setAiSuggestion(data.data.suggestion);
        setCustomerMessage(data.data.suggestion);
        setShowAiPreviewInConversation(true);
        setHasAIResponse(true);
      }
      setIsGeneratingAI(false);
    },
    onError: (error) => {
      console.error('AI suggestion error:', error);
      setIsGeneratingAI(false);
    }
  });

  // Function to manually trigger AI suggestion (legacy - keeping for now)
  const handleGenerateAISuggestion = () => {
    if (conversationForAI.length > 0 && !isGeneratingAI) {
      setIsGeneratingAI(true);
      aiSuggestionMutation.mutate();
    }
  };

  // AI Chat with Cmd+K - user types instructions, AI generates response
  const handleAIChat = async () => {
    const rawPrompt = extractPlainText(customerMessage);

    if (!rawPrompt || rawPrompt.trim().length === 0) {
      return;
    }

    if (isGeneratingAI) {
      return;
    }

    // @chatgpt → GPT-5.5 (Kie), @claude veya etiketsiz → Claude. Etiketi prompt'tan temizle.
    const hasGpt = /@chat\s?gpt\b/i.test(rawPrompt) || /@gpt\b/i.test(rawPrompt);
    const provider: 'claude' | 'gpt' = hasGpt ? 'gpt' : 'claude';
    const userPrompt = rawPrompt
      .replace(/@chat\s?gpt\b/gi, '')
      .replace(/@gpt\b/gi, '')
      .replace(/@claude\b/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

    if (!userPrompt) {
      return;
    }

    setAiProvider(provider);
    setIsGeneratingAI(true);

    try {
      // Build ticket info
      const ticketInfo: TicketInfo = {
        subject: ticket?.subject,
        status: ticket?.status,
        priority: ticket?.priority,
        organizationName: ticket?.organization?.name || orgsResponse?.data?.find((o: any) => o.id === ticket?.organizationId)?.name,
        customerEmail: ticket?.requester?.email,
        customerName: `${ticket?.requester?.firstName || ''} ${ticket?.requester?.lastName || ''}`.trim() || undefined,
        ticketNumber: ticket?.ticketNumber
      };

      // Build orders info
      const allOrders = getAllOrders();
      const orders: OrderInfo[] = allOrders ? allOrders.map((order: any) => ({
        orderNumber: order.orderNumber,
        date: order.date || (order.createdAt ? formatDate(order.createdAt) : undefined),
        total: order.total,
        status: order.fulfillmentStatus,
        items: order.lineItems?.filter((item: any) => item.isMainProduct !== false)?.map((item: any) => ({
          name: item.name || item.title,
          quantity: item.quantity || item.orderedQuantity || 1,
          price: item.price ? `${item.price} ${order.currency || 'EUR'}` : undefined
        }))
      })) : [];

      const response = await aiService.chat({
        userPrompt: userPrompt,
        sessionHistory: aiSessionHistory,
        ticketConversation: conversationForAI,
        ticketId: ticketId,
        ticketInfo,
        orders,
        provider
      });

      // KI blockiert (deaktiviert / kein Paket / Kontingent) → Notify, keine Modell-Anfrage
      if ((response as AIChatResponse).blocked) {
        toast.info((response as AIChatResponse).message || t('ticket:msg.aiUnavailable', 'KI ist momentan nicht verfügbar.'));
        return;
      }

      if (response.success && response.data?.response) {
        // Add to session history
        setAiSessionHistory(prev => [
          ...prev,
          { role: 'user', content: userPrompt },
          { role: 'assistant', content: response.data.response }
        ]);

        // Replace editor content with AI response.
        // Kie liefert manchmal HTML escaped zurück (&lt;br&gt;) → in echtes HTML dekodieren,
        // sonst zeigt der Editor die Tags als Text an.
        let aiHtml = response.data.response as string;
        if (/&lt;|&gt;|&quot;|&#39;|&amp;/.test(aiHtml)) {
          aiHtml = aiHtml
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"').replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');
        }
        setCustomerMessage(aiHtml);
        setHasAIResponse(true);
      }
    } catch (error) {
      console.error('AI Chat error:', error);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Reset AI session when ticket changes
  React.useEffect(() => {
    setAiSessionHistory([]);
  }, [ticketId]);

  // Cmd+K keyboard shortcut for AI chat
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        handleAIChat();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [customerMessage, aiSessionHistory, conversationForAI, ticketId, isGeneratingAI]);

  // Notes query
  const notesQuery = useNotesByTicket(ticket?.id || '');
  const createNoteMutation = useCreateNote();

  // External ticket notes query
  const externalNotesQuery = useNotesByTicket(activeExternalTicketId || '');

  // External tickets queries (New System)
  const { data: externalTickets = [], isLoading: loadingExternalTickets } = useExternalTicketsByParent(ticket?.id || '');
  const createExternalTicketMutation = useCreateExternalTicket();


  // Set first external ticket as active if none selected
  React.useEffect(() => {
    if (externalTickets.length > 0 && !activeExternalTicketId) {
      setActiveExternalTicketId(externalTickets[0].id);
    }
  }, [externalTickets, activeExternalTicketId]);

  // Count open external tickets
  const openExternalTicketsCount = useMemo(() => {
    return externalTickets.filter(ticket => ticket.status === 'open').length;
  }, [externalTickets]);

  // External ticket update mutation (for auto-opening on tab click)
  const updateExternalTicketMutation = useMutation({
    mutationFn: (data: { ticketId: string; status: string }) => {
      // // console.log('🔄 Auto-updating external ticket status:', data);
      return ticketsService.updateTicket(data.ticketId, { status: data.status });
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches - use correct query key
      await queryClient.cancelQueries({ queryKey: ['external-tickets', 'parent', ticket?.id] });

      // Snapshot previous value
      const previousTickets = queryClient.getQueryData(['external-tickets', 'parent', ticket?.id]);

      // Optimistically update to the new status
      queryClient.setQueryData(['external-tickets', 'parent', ticket?.id], (old: any) => {
        if (!old) return old;
        return old.map((t: any) =>
          t.id === variables.ticketId ? { ...t, status: variables.status } : t
        );
      });

      // // console.log('⚡ Optimistic update: Status changed to', variables.status);

      return { previousTickets };
    },
    onError: (error: any, variables, context: any) => {
      // Rollback on error
      if (context?.previousTickets) {
        queryClient.setQueryData(['external-tickets', 'parent', ticket?.id], context.previousTickets);
      }
      console.error('❌ Failed to auto-update external ticket status:', error);
    },
    onSuccess: (_, variables) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['ticket', variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['external-tickets', 'parent', ticket?.id] });
      // // console.log('✅ External ticket status auto-updated to pending');
    }
  });

  // No auto-update on initial load - removed

  // Get active external ticket's comments
  const activeExternalTicket = externalTickets.find(t => t.id === activeExternalTicketId);
  const { data: activeExternalTicketData, isLoading: loadingActiveExternalTicket } = useQuery({
    queryKey: ['ticket', activeExternalTicketId],
    queryFn: () => ticketsService.getTicketById(activeExternalTicketId!),
    enabled: !!activeExternalTicketId
  });

  // Scroll to bottom when external conversation changes or tab switches or panel opens
  useEffect(() => {
    if (activeExternalTicketId && activeExternalTicketData?.data?.comments && externalConversationRef.current && !isNebenkonversationCollapsed) {
      // Wait for DOM to render and then scroll
      setTimeout(() => {
        // Find viewport inside the external conversation ref
        const viewport = externalConversationRef.current?.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          viewport.scrollTop = viewport.scrollHeight;
        }
      }, 100);
    }
  }, [activeExternalTicketId, activeExternalTicketData?.data?.comments?.length, isNebenkonversationCollapsed]);

  // Get latest external ticket's data for summary
  const latestExternalTicket = useMemo(() => {
    if (externalTickets.length === 0) return null;
    const sorted = [...externalTickets].sort((a, b) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
    return sorted[0];
  }, [externalTickets]);

  const { data: latestExternalTicketData } = useQuery({
    queryKey: ['ticket', latestExternalTicket?.id],
    queryFn: () => ticketsService.getTicketById(latestExternalTicket!.id),
    enabled: !!latestExternalTicket?.id
  });

  // Legacy External conversations queries (Keep for backward compatibility)
  const { data: externalConversations = [], isLoading: loadingExternalConversations } = useExternalConversationsByTicket(ticket?.id || '');
  const currentExternalConversation = externalConversations.find(conv => conv.type === activeExternalTab);
  const { data: externalMessages = [], isLoading: loadingExternalMessages } = useExternalConversationMessages(
    currentExternalConversation?.id || ''
  );
  const sendExternalMessageMutation = useSendExternalMessage();

  // Set initial active external tab when conversations load
  useEffect(() => {
    if (externalConversations.length > 0 && !activeExternalTab) {
      setActiveExternalTab(externalConversations[0].type);
    }
  }, [externalConversations, activeExternalTab]);


  const customerMessages = [
    {
      id: 1,
      sender: "customer",
      name: "SYSTEM",
      email: customer.email,
      avatar: customer.avatar,
      content:
        "Der Inhalt dieses Tickets scheint leer zu sein. Möglicherweise wurde in dieser E-Mail eine vom System nicht zugelassene Situation festgestellt. Wenn Sie glauben, dass ein Problem vorliegt, überprüfen Sie bitte die betreffende E-Mail über Webmail und informieren Sie den Entwickler.",
      timestamp: "2023-05-18T10:30:00",
      attachments: [],
    },
  ];

  const mockExternalMessages = {
    shipping: [
      {
        id: 1,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content:
          "Können Sie bestätigen, ob Bestellung #12345 einen Displayschutz im Paket enthalten hat?",
        timestamp: "2023-05-18T11:25:00",
        attachments: [],
      },
      {
        id: 2,
        sender: "external",
        name: "Versand Support",
        email: "support@versand.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Shipping",
        content:
          "Laut unseren Unterlagen waren alle auf dem Packzettel aufgeführten Artikel enthalten. Wir werden das nochmal mit dem Lagerteam überprüfen.",
        timestamp: "2023-05-18T13:45:00",
        attachments: [],
      },
      {
        id: 3,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content:
          "Vielen Dank für die Überprüfung. Der Kunde hat bestätigt, dass der Displayschutz fehlt.",
        timestamp: "2023-05-18T14:00:00",
        attachments: [],
      },
      {
        id: 4,
        sender: "external",
        name: "Versand Support",
        email: "support@versand.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Shipping",
        content:
          "Wir haben das Lager kontaktiert. Es gab einen Fehler beim Verpacken. Der Artikel wurde vergessen.",
        timestamp: "2023-05-18T14:15:00",
        attachments: [],
      },
      {
        id: 5,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content: "Verstanden. Können Sie einen Ersatzartikel versenden?",
        timestamp: "2023-05-18T14:20:00",
        attachments: [],
      },
      {
        id: 6,
        sender: "external",
        name: "Versand Support",
        email: "support@versand.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Shipping",
        content:
          "Ja, der Ersatzartikel wird heute noch versendet. Tracking-Nummer: DE123456789",
        timestamp: "2023-05-18T14:30:00",
        attachments: [],
      },
      {
        id: 7,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content: "Perfekt! Vielen Dank für die schnelle Bearbeitung.",
        timestamp: "2023-05-18T14:35:00",
        attachments: [],
      },
      {
        id: 8,
        sender: "external",
        name: "Versand Support",
        email: "support@versand.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Shipping",
        content:
          "Gern geschehen. Der Kunde sollte den Artikel morgen erhalten.",
        timestamp: "2023-05-18T14:40:00",
        attachments: [],
      },
      {
        id: 9,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content: "Artikel wurde erfolgreich zugestellt. Kunde ist zufrieden.",
        timestamp: "2023-05-19T10:00:00",
        attachments: [],
      },
      {
        id: 10,
        sender: "external",
        name: "Versand Support",
        email: "support@versand.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Shipping",
        content:
          "Sehr gut! Wir werden unsere Verpackungsprozesse überprüfen, um solche Fehler zu vermeiden.",
        timestamp: "2023-05-19T10:30:00",
        attachments: [],
      },
      {
        id: 11,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content: "Das ist eine gute Idee. Vielen Dank für die Zusammenarbeit.",
        timestamp: "2023-05-19T10:35:00",
        attachments: [],
      },
      {
        id: 12,
        sender: "external",
        name: "Versand Support",
        email: "support@versand.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Shipping",
        content: "Immer gerne. Bei weiteren Fragen stehen wir zur Verfügung.",
        timestamp: "2023-05-19T10:40:00",
        attachments: [],
      },
    ],
    supplier: [
      {
        id: 1,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content:
          "Hallo, wir haben ein Problem mit Bestellung #12345. Ein Displayschutz fehlt.",
        timestamp: "2023-05-18T12:00:00",
        attachments: [],
      },
      {
        id: 2,
        sender: "external",
        name: "Lieferant Support",
        email: "support@lieferant.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Supplier",
        content:
          "Wir überprüfen unsere Lieferung. Alle Artikel wurden korrekt geliefert.",
        timestamp: "2023-05-18T13:00:00",
        attachments: [],
      },
      {
        id: 3,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content:
          "Das Problem lag beim Verpacken, nicht bei der Lieferung. Danke für die Überprüfung.",
        timestamp: "2023-05-18T14:00:00",
        attachments: [],
      },
      {
        id: 4,
        sender: "external",
        name: "Lieferant Support",
        email: "support@lieferant.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Supplier",
        content:
          "Verstanden. Falls Sie Ersatzartikel benötigen, können wir diese schnell liefern.",
        timestamp: "2023-05-18T14:30:00",
        attachments: [],
      },
      {
        id: 5,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content:
          "Danke für das Angebot. Wir haben bereits eine Lösung gefunden.",
        timestamp: "2023-05-18T15:00:00",
        attachments: [],
      },
    ],
    warehouse: [
      {
        id: 1,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content:
          "Hallo Lagerteam, bei Bestellung #12345 fehlt ein Displayschutz. Könnt ihr das überprüfen?",
        timestamp: "2023-05-18T11:30:00",
        attachments: [],
      },
      {
        id: 2,
        sender: "external",
        name: "Lager Team",
        email: "lager@unternehmen.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Warehouse",
        content: "Wir schauen uns das an. Moment bitte.",
        timestamp: "2023-05-18T11:45:00",
        attachments: [],
      },
      {
        id: 3,
        sender: "external",
        name: "Lager Team",
        email: "lager@unternehmen.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Warehouse",
        content:
          "Wir haben den Fehler gefunden. Der Mitarbeiter hat den Displayschutz vergessen einzupacken.",
        timestamp: "2023-05-18T12:15:00",
        attachments: [],
      },
      {
        id: 4,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content: "Okay, könnt ihr einen Ersatz versenden?",
        timestamp: "2023-05-18T12:20:00",
        attachments: [],
      },
      {
        id: 5,
        sender: "external",
        name: "Lager Team",
        email: "lager@unternehmen.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Warehouse",
        content: "Ja, wird sofort bearbeitet und heute noch versendet.",
        timestamp: "2023-05-18T12:25:00",
        attachments: [],
      },
      {
        id: 6,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content: "Super, vielen Dank für die schnelle Bearbeitung!",
        timestamp: "2023-05-18T12:30:00",
        attachments: [],
      },
      {
        id: 7,
        sender: "external",
        name: "Lager Team",
        email: "lager@unternehmen.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Warehouse",
        content:
          "Artikel ist verpackt und geht heute raus. Tracking: DE123456789",
        timestamp: "2023-05-18T14:00:00",
        attachments: [],
      },
      {
        id: 8,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content: "Perfekt! Ich informiere den Kunden.",
        timestamp: "2023-05-18T14:05:00",
        attachments: [],
      },
      {
        id: 9,
        sender: "external",
        name: "Lager Team",
        email: "lager@unternehmen.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Warehouse",
        content:
          "Wir werden unsere Verpackungsprozesse überprüfen, um solche Fehler zu vermeiden.",
        timestamp: "2023-05-18T14:30:00",
        attachments: [],
      },
      {
        id: 10,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content:
          "Das ist eine sehr gute Idee. Danke für die proaktive Herangehensweise.",
        timestamp: "2023-05-18T14:35:00",
        attachments: [],
      },
    ],
    accounting: [
      {
        id: 1,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content:
          "Hallo Buchhaltung, für Bestellung #12345 müssen wir einen Ersatzartikel kostenlos versenden. Bitte entsprechend verbuchen.",
        timestamp: "2023-05-18T14:45:00",
        attachments: [],
      },
      {
        id: 2,
        sender: "external",
        name: "Buchhaltung",
        email: "buchhaltung@unternehmen.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Accounting",
        content:
          "Verstanden. Wir buchen das als Kulanzleistung. Welcher Artikelwert?",
        timestamp: "2023-05-18T15:00:00",
        attachments: [],
      },
      {
        id: 3,
        sender: "agent",
        name: owner.name,
        email: owner.email,
        avatar: owner.avatar,
        content: "Displayschutz im Wert von 10,01€ plus Versandkosten 4,99€.",
        timestamp: "2023-05-18T15:05:00",
        attachments: [],
      },
      {
        id: 4,
        sender: "external",
        name: "Buchhaltung",
        email: "buchhaltung@unternehmen.com",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Accounting",
        content:
          "Alles klar, ist verbucht. Gesamtkulanz: 15,00€ für Bestellung #12345.",
        timestamp: "2023-05-18T15:10:00",
        attachments: [],
      },
    ],
  };

  // Get snippets from API
  const { data: snippetsData, isLoading: isLoadingSnippets } = useSnippets();
  const useSnippetMutation = useSnippetUsage();

  const snippets = snippetsData?.data?.snippets || [];

  // Filtered snippets for slash command (max 5 results)
  const filteredSlashSnippets = useMemo(() => {
    if (!slashCommandState.isOpen || !snippets.length) return [];
    const query = slashCommandState.query.toLowerCase();
    return snippets.filter(s =>
      s.name.toLowerCase().includes(query) ||
      s.shortcut.toLowerCase().includes(query) ||
      s.category.toLowerCase().includes(query)
    ).slice(0, 5); // Limit to 5 results for better UX
  }, [snippets, slashCommandState.isOpen, slashCommandState.query]);

  // Check if there are more snippets than shown
  const hasMoreSnippets = useMemo(() => {
    if (!slashCommandState.isOpen || !snippets.length) return false;
    const query = slashCommandState.query.toLowerCase();
    const totalMatches = snippets.filter(s =>
      s.name.toLowerCase().includes(query) ||
      s.shortcut.toLowerCase().includes(query) ||
      s.category.toLowerCase().includes(query)
    ).length;
    return totalMatches > 5;
  }, [snippets, slashCommandState.isOpen, slashCommandState.query]);

  // Reset selected index when filtered results change
  useEffect(() => {
    setSlashSelectedIndex(0);
  }, [filteredSlashSnippets.length]);

  // Apply text snippet with placeholder replacement
  const applyTextSnippet = useCallback(async (snippet: Snippet) => {
    try {
      // Get assignee (owner) info from agents data
      const ownerAgent = assignedUser && agentsData?.data?.agents
        ? agentsData.data.agents.find((a: any) => a.id === assignedUser)
        : null;

      // Get organization name
      const organizationName = ticket?.organization?.name ||
        (ticket?.organizationId && orgsResponse?.data
          ? orgsResponse.data.find((o: any) => o.id === ticket.organizationId)?.name
          : '') || '';

      // Define all placeholders (Zammad-compatible #{...} format)
      const placeholders: Record<string, string> = {
        // TICKET
        '#{ticket.number}': ticket?.ticketNumber || ticket?.ticket_number || ticket?.id?.toString() || '',
        '#{ticket.title}': ticket?.subject || '',
        '#{ticket.tags}': (ticket?.tags || []).join(', '),
        '#{ticket.created_at}': ticket?.createdAt ? formatDate(ticket.createdAt) : '',
        '#{ticket.updated_at}': ticket?.updatedAt ? formatDate(ticket.updatedAt) : '',

        // TICKET > STATUS & PRIORITÄT
        '#{ticket.state.name}': ticket?.status || '',
        '#{ticket.priority.name}': ticket?.priority || '',

        // TICKET > ORGANISATION
        '#{ticket.organization.name}': organizationName,

        // TICKET > KUNDE
        '#{ticket.customer.firstname}': ticket?.requester?.name?.split(' ')[0] || '',
        '#{ticket.customer.lastname}': ticket?.requester?.name?.split(' ').slice(1).join(' ') || '',
        '#{ticket.customer.email}': ticket?.requester?.email || customerEmail || '',

        // TICKET > BESITZER (Assignee)
        '#{ticket.owner.firstname}': ownerAgent?.name?.split(' ')[0] || '',
        '#{ticket.owner.lastname}': ownerAgent?.name?.split(' ').slice(1).join(' ') || '',
        '#{ticket.owner.email}': ownerAgent?.email || '',

        // AKTUELLER BENUTZER (Agent)
        '#{user.firstname}': currentUser?.firstName || '',
        '#{user.lastname}': currentUser?.lastName || '',
        '#{user.email}': currentUser?.email || '',

        // SHOP ORDER
        '#{order.number}': orderDetails?.orderNumber || '',
        '#{order.customer.name}': orderDetails?.customer?.name || `${orderDetails?.customer?.firstName || ''} ${orderDetails?.customer?.lastName || ''}`.trim() || '',
        '#{order.date}': orderDetails?.date || '',
        '#{order.total}': orderDetails?.total || '',
        '#{order.payment_status}': orderDetails?.paymentStatus || '',
        '#{order.fulfillment_status}': orderDetails?.fulfillmentStatus || '',
        '#{order.shipping_address}': orderDetails?.shippingAddress?.fullAddress || '',
        '#{order.tracking_url}': orderDetails?.trackingNumbers?.[0]?.url || orderDetails?.trackingNumbers?.[0]?.trackingUrl || '',
      };

      // Replace placeholders in content
      let processedContent = snippet.content;
      Object.entries(placeholders).forEach(([placeholder, value]) => {
        const escaped = placeholder.replace(/[#{}().]/g, '\\$&');
        const regex = new RegExp(escaped, 'gi');
        processedContent = processedContent.replace(regex, value);
      });

      // Still call backend to increment usage count
      await useSnippetMutation.mutateAsync({
        id: snippet.id,
        variables: {}
      });

      // Determine which editor to use based on context
      const isExternalContext = showTextSnippets.context === 'external';

      // Append the processed content to the existing message
      if (isExternalContext) {
        setExternalMessage(prev => {
          if (!prev || prev.trim() === '') return processedContent;
          return `${prev}\n\n${processedContent}`;
        });
      } else {
        setCustomerMessage(prev => {
          if (!prev || prev.trim() === '') return processedContent;
          return `${prev}\n\n${processedContent}`;
        });
      }

      // Fetch and add snippet attachments to the reply
      if (snippet.attachmentIds && snippet.attachmentIds.length > 0) {
        const attachmentPromises = snippet.attachmentIds.map(attachmentId =>
          ticketsService.getAttachment(attachmentId)
        );

        const attachmentResponses = await Promise.all(attachmentPromises);
        const newAttachments = attachmentResponses.map(res => res.data);

        if (isExternalContext) {
          setExternalUploadedFiles(prev => [
            ...prev,
            ...newAttachments.map(att => ({
              id: att.id,
              name: att.filename,
              size: att.size
            }))
          ]);
        } else {
          setUploadedFiles(prev => [
            ...prev,
            ...newAttachments.map(att => ({
              id: att.id,
              filename: att.filename,
              size: att.size,
              contentType: att.contentType
            }))
          ]);

          setAttachmentIds(prev => [...prev, ...newAttachments.map(att => att.id)]);
        }
      }

      setShowTextSnippets({ isOpen: false, context: 'customer' });
    } catch (error) {
      console.error('Failed to apply text snippet:', error);
      setCustomerMessage(prev => {
        if (!prev || prev.trim() === '') return snippet.content;
        return `${prev}\n\n${snippet.content}`;
      });
      setShowTextSnippets({ isOpen: false, context: 'customer' });
    }
  }, [ticket, customerEmail, assignedUser, agentsData, orgsResponse, currentUser, orderDetails, showTextSnippets]);

  // Handle slash command snippet selection
  const handleSlashSnippetSelect = useCallback(async (snippet: Snippet) => {
    // Remove the slash command text from editor
    if ((window as any).__richTextEditorRemoveSlash) {
      (window as any).__richTextEditorRemoveSlash();
    }

    // Close the popover
    setSlashCommandState({ isOpen: false, query: '', position: { top: 0, left: 0 } });

    // Apply the snippet (reusing existing logic)
    await applyTextSnippet(snippet);
  }, [applyTextSnippet]);

  // Keyboard navigation for slash command popover
  useEffect(() => {
    if (!slashCommandState.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashSelectedIndex(prev =>
          prev < filteredSlashSnippets.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredSlashSnippets.length - 1
        );
      } else if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        if (filteredSlashSnippets[slashSelectedIndex]) {
          handleSlashSnippetSelect(filteredSlashSnippets[slashSelectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setSlashCommandState({ isOpen: false, query: '', position: { top: 0, left: 0 } });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slashCommandState.isOpen, filteredSlashSnippets, slashSelectedIndex, handleSlashSnippetSelect]);

  const aiSuggestions = [
    "Es tut mir leid wegen des fehlenden Displayschutzes in Ihrer Bestellung #12345. Ich habe unsere Unterlagen überprüft und bestätigt, dass dieser Artikel hätte enthalten sein sollen. Ich werde veranlassen, dass Ihnen umgehend kostenlos ein Ersatz zugesendet wird. Sollen wir ihn an dieselbe Adresse senden?",
    "Es tut mir leid wegen des fehlenden Displayschutzes. Ich habe bestätigt, dass dieser Artikel Teil Ihrer Bestellung #12345 war und hätte enthalten sein sollen. Wir werden umgehend einen Ersatz an Ihre hinterlegte Adresse versenden. Gibt es noch etwas anderes, womit ich Ihnen bezüglich Ihrer Bestellung helfen kann?",
  ];

  // Format history data for timeline
  const formatHistoryForTimeline = (history: any[]) => {
    return history.map((item, index) => ({
      id: item.id || index,
      action: formatHistoryAction(item.field_name, item.old_value, item.new_value),
      timestamp: item.created_at,
      user: item.changed_by_first_name && item.changed_by_last_name
        ? `${item.changed_by_first_name} ${item.changed_by_last_name}`
        : item.changed_by_email || 'System'
    }));
  };

  // Helper function to format history actions in German
  const formatHistoryAction = (fieldName: string, oldValue: string, newValue: string) => {
    switch (fieldName) {
      case 'status':
        return `Status geändert zu ${translateStatus(newValue)}`;
      case 'priority':
        return `Priorität geändert zu ${translatePriority(newValue)}`;
      case 'assignee_id':
        return newValue ? 'Ticket zugewiesen' : 'Zuweisung entfernt';
      case 'reply':
        return 'Antwort gesendet';
      case 'subject':
        return 'Betreff geändert';
      case 'description':
        return 'Beschreibung aktualisiert';
      default:
        return `${fieldName} geändert`;
    }
  };

  // Helper functions for German translations
  const translateStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'open': 'Offen',
      'pending': 'Wartend',
      'closed': 'Geschlossen'
    };
    return statusMap[status] || status;
  };

  const translatePriority = (priority: string) => {
    const priorityMap: { [key: string]: string } = {
      'low': 'Niedrig',
      'normal': 'Normal',
      'high': 'Hoch',
      'urgent': 'Dringend'
    };
    return priorityMap[priority] || priority;
  };

  // Get timeline data from API or fallback to mock data
  const getTicketTimeline = () => {
    if (ticketData?.data?.history && ticketData.data.history.length > 0) {
      return formatHistoryForTimeline(ticketData.data.history);
    }

    // Fallback to mock data
    return [
      {
        id: 1,
        action: "Ticket erstellt",
        timestamp: ticketData?.data?.ticket?.created_at || "2023-05-18T10:30:00",
        user: "System",
      },
      {
        id: 2,
        action: "Status geändert zu Offen",
        timestamp: "2023-05-18T10:45:00",
        user: owner.name,
      },
    ];
  };

  const ticketTimeline = getTicketTimeline();

  const handleSubjectSave = async () => {
    if (!subject.trim()) {
      alert(t('ticket:msg.subjectEmpty', 'Betreff darf nicht leer sein'));
      return;
    }

    try {
      // Debug logging
      const currentTicketId = activeTab || ticketId;
      // // console.log('Sending subject update:', { ticketId: currentTicketId, subject: subject.trim() });

      // Update ticket subject via API
      await updateTicketMutation.mutateAsync({ ticketId: currentTicketId, subject: subject.trim() });
      setIsEditingSubject(false);
      // Success message could be shown here with toast
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Betreffs:', error);
      // Revert to original subject on error
      if (ticket?.subject) {
        setSubject(ticket.subject);
      }
    }
  };

  const handleSubjectCancel = () => {
    setSubject(ticket?.subject || subject);
    setIsEditingSubject(false);
  };

  const handleCustomerMessageSend = async (newStatus?: string, forceAsNote?: boolean) => {
    if (isCustomerMessageEmpty) {
      return;
    }

    // forceAsNote parametresi state'ten öncelikli (async state update sorunu için)
    const shouldSendAsNote = forceAsNote !== undefined ? forceAsNote : sendAsNote;

    try {
      // Eğer "Send as Note" seçiliyse, not olarak kaydet
      if (shouldSendAsNote) {
        // Son comment'i bul (parent_comment_id için)
        const lastCommentId = apiComments.length > 0
          ? apiComments[apiComments.length - 1].id
          : null;

        // // console.log('Creating note with parent_comment_id:', lastCommentId);
        // // console.log('Total apiComments:', apiComments.length);
        if (apiComments.length > 0) {
          // // console.log('Last comment:', apiComments[apiComments.length - 1]);
        }

        const noteData = {
          ticket_id: ticket?.id || '',
          title: 'Internal Note',
          content: customerMessage,
          parent_comment_id: lastCommentId,
          attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined
        };

        // // console.log('Note data being sent:', noteData);
        // // console.log('Attachments count:', attachmentIds.length);

        await createNoteMutation.mutateAsync(noteData);

        setCustomerMessage("");
        setAttachmentIds([]);
        setUploadedFiles([]);
        setSendAsNote(false); // Reset after sending note
        setSignaturePanelOpen(false);
        setSignatureEditMode(false);
        setSignatureEditContent(signatureHtml || '');
      } else {
        // Normal reply gönder - status değişmez, append signature to content
        const currentTicketId = activeTab || ticketId;
        await replyMutation.mutateAsync({
          ticketId: currentTicketId,
          content: getContentWithSignature(),
          sendEmail: true,
          attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined
        });
        setCustomerMessage("");
        setAttachmentIds([]);
        setUploadedFiles([]);
        setSignaturePanelOpen(false);
        setSignatureEditMode(false);
        setSignatureEditContent(signatureHtml || '');
      }

      // Handle post-send navigation based on options
      // Priority: goToNextAfterSend > goToViewAfterSend
      if (goToNextAfterSend && adjacentTicketsData?.data?.next) {
        const nextId = adjacentTicketsData.data.next.id;
        // Mark as navigating to replace tab instead of adding new (use sessionStorage to survive page reload)
        sessionStorage.setItem('ticket-tabs-navigating', 'true');
        sessionStorage.setItem('ticket-tabs-replace-id', activeTab || ticketId);
        // Store view context in sessionStorage for clean URLs
        if (fromViewId) {
          sessionStorage.setItem('ticketViewContext', JSON.stringify({
            fromViewId,
            fromViewName,
            sortField: listSortField,
            sortDirection: listSortDirection,
            listPosition: listPosition ? listPosition + 1 : undefined,
            totalTickets,
            viewFilterConfig
          }));
        }
        window.location.href = `/tickets/${nextId}`;
      } else if (goToViewAfterSend && fromViewId) {
        // Navigate back to the custom view's ticket list
        // Clear the view context since we're going back to the list
        sessionStorage.removeItem('ticketViewContext');
        window.location.href = `/tickets/custom/${fromViewId}`;
      }
    } catch (error) {
      console.error('Error sending message/note:', error);
    }
  };

  const handleAiSuggestionAccept = () => {
    // AI response is already in HTML format, no need to convert \n to <br/>
    setCustomerMessage(aiSuggestion || '');
    setShowAiSuggestionCard(false);
    setShowAiPreviewInConversation(false); // Close preview in conversation
  };

  // Tag handlers
  const toggleTag = (tagName: string) => {
    const newTags = selectedTags.includes(tagName)
      ? selectedTags.filter(t => t !== tagName)
      : [...selectedTags, tagName];

    setSelectedTags(newTags);

    // Otomatik kaydet
    const currentTicketId = activeTab || ticketId;
    updateTicketMutation.mutate({
      ticketId: currentTicketId,
      tags: newTags
    });
  };

  // Reminder handlers
  const handleOpenReminderDialog = (date?: Date, reminder?: Reminder) => {
    if (date) {
      setSelectedReminderDate(date);
    }
    if (reminder) {
      setEditingReminder(reminder);
    }
    setReminderDialogOpen(true);
  };

  const handleReminderSubmit = async (data: {
    title: string;
    description: string;
    reminderDate: Date;
    urgency: 'low' | 'medium' | 'high' | 'urgent';
    color: string;
    notifyBeforeMinutes: number;
  }) => {
    try {
      if (editingReminder) {
        // Update existing reminder
        await updateReminderMutation.mutateAsync({
          id: editingReminder.id,
          data: {
            title: data.title,
            description: data.description,
            reminderDate: data.reminderDate.toISOString(),
            urgency: data.urgency,
            color: data.color,
            notifyBeforeMinutes: data.notifyBeforeMinutes,
          },
        });
      } else {
        // Create new reminder
        await createReminderMutation.mutateAsync({
          ticketId: activeTab || ticketId,
          title: data.title,
          description: data.description,
          reminderDate: data.reminderDate.toISOString(),
          urgency: data.urgency,
          color: data.color,
          notifyBeforeMinutes: data.notifyBeforeMinutes,
        });
      }
      setReminderDialogOpen(false);
      setEditingReminder(undefined);
      setSelectedReminderDate(undefined);
    } catch (error) {
      console.error('Failed to save reminder:', error);
    }
  };

  const handleDeleteReminder = async (reminderId: string) => {
    try {
      await deleteReminderMutation.mutateAsync(reminderId);
    } catch (error) {
      console.error('Failed to delete reminder:', error);
    }
  };

  const handleToggleReminderComplete = async (reminderId: string, isCompleted: boolean) => {
    try {
      if (isCompleted) {
        await completeReminderMutation.mutateAsync(reminderId);
      } else {
        await uncompleteReminderMutation.mutateAsync(reminderId);
      }
    } catch (error) {
      console.error('Failed to toggle reminder completion:', error);
    }
  };

  // Memoize reminder dates for calendar modifiers (prevents re-render issues)
  const reminderDates = React.useMemo(() => {
    return reminders.map(r => new Date(r.reminder_date));
  }, [reminders]);

  const handleCalendarSelect = (date: Date | undefined) => {
    if (!date) return;

    // Close popover first
    setReminderPopoverOpen(false);

    // Check if there are reminders on this date
    const dateStr = date.toDateString();
    const dateReminders = reminders.filter(
      (r) => new Date(r.reminder_date).toDateString() === dateStr
    );

    if (dateReminders.length > 0) {
      // Show reminder list
      setSelectedReminderDate(date);
      setReminderListOpen(true);
    } else {
      // Open dialog to create new reminder
      handleOpenReminderDialog(date);
    }
  };

  // Load recent languages on mount
  useEffect(() => {
    const recent = getRecentLanguages();
    setRecentLanguages(recent.map(r => ({
      code: r.code,
      name: r.name,
      nativeName: r.nativeName,
      countryCode: r.countryCode
    })));
  }, []);

  // Translation handlers
  const handleTranslate = async (messageId: string, content: string, targetLanguage: string) => {
    try {
      setTranslatingMessageId(messageId);

      // // console.log('Translation started:', { messageId, targetLanguage, contentLength: content.length });

      const response = await aiService.translateContent(content, targetLanguage);

      // console.log('Translation response received:', {
      //   success: response.success,
      //   hasData: !!response.data,
      //   translatedLength: response.data?.translatedContent?.length
      // });

      if (response.success && response.data?.translatedContent) {
        // Store translated content
        setTranslatedMessages(prev => ({
          ...prev,
          [messageId]: response.data.translatedContent
        }));

        // Add to recent languages
        const language = SUPPORTED_LANGUAGES.find(l => l.code === targetLanguage);
        if (language) {
          addRecentLanguage(language);

          // Update recent languages state
          const updated = getRecentLanguages();
          setRecentLanguages(updated.map(r => ({
            code: r.code,
            name: r.name,
            nativeName: r.nativeName,
            countryCode: r.countryCode
          })));
        }

        // Close popover
        setTranslatePopoverOpen(prev => ({
          ...prev,
          [messageId]: false
        }));

        // // console.log('Translation completed successfully');
      } else {
        console.warn('Translation response invalid:', response);
        alert(t('ticket:msg.translationInvalid', 'Übersetzung fehlgeschlagen. Ungültige Antwort vom Server.'));
      }
    } catch (error: any) {
      console.error('Translation error:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        code: error?.code
      });

      if (error?.code === 'ECONNABORTED') {
        alert(t('ticket:msg.translationTimeout', 'Übersetzung Zeitüberschreitung. Bitte versuchen Sie es mit kürzerem Text erneut.'));
      } else if (error?.response?.status === 500) {
        alert(t('ticket:msg.translationServer', 'Server-Fehler bei der Übersetzung. Bitte versuchen Sie es später erneut.'));
      } else {
        alert(t('ticket:msg.translationFailed', 'Übersetzung fehlgeschlagen. Bitte versuchen Sie es erneut.'));
      }
    } finally {
      // // console.log('Translation cleanup - resetting loading state');
      setTranslatingMessageId(null);
    }
  };

  const toggleTranslation = (messageId: string) => {
    setTranslatedMessages(prev => {
      const newState = { ...prev };
      delete newState[messageId];
      return newState;
    });
  };

  const openTranslatePopover = (messageId: string) => {
    setTranslatePopoverOpen(prev => ({
      ...prev,
      [messageId]: true
    }));
  };

  const closeTranslatePopover = (messageId: string) => {
    setTranslatePopoverOpen(prev => ({
      ...prev,
      [messageId]: false
    }));
  };

  // Translation handler for reply editor
  const handleTranslateReplyEditor = async (targetLanguage: string) => {
    if (!customerMessage || customerMessage.trim() === '' || customerMessage === '<p></p>') {
      alert(t('ticket:msg.enterMessageFirst', 'Bitte geben Sie zuerst eine Nachricht ein.'));
      return;
    }

    try {
      setTranslatingReplyEditor(true);

      const response = await aiService.translateContent(customerMessage, targetLanguage);

      if (response.success) {
        // Update editor with translated content
        setCustomerMessage(response.data.translatedContent);

        // Add to recent languages
        const language = SUPPORTED_LANGUAGES.find(l => l.code === targetLanguage);
        if (language) {
          addRecentLanguage(language);

          // Update recent languages state
          const updated = getRecentLanguages();
          setRecentLanguages(updated.map(r => ({
            code: r.code,
            name: r.name,
            nativeName: r.nativeName,
            countryCode: r.countryCode
          })));
        }

        // Close popover
        setReplyTranslatePopoverOpen(false);
      }
    } catch (error) {
      console.error('Translation error:', error);
      alert(t('ticket:msg.translationFailed', 'Übersetzung fehlgeschlagen. Bitte versuchen Sie es erneut.'));
    } finally {
      setTranslatingReplyEditor(false);
    }
  };

  // Delete ticket handler
  const handleDeleteTicket = () => {
    if (ticketId) {
      deleteTicketMutation.mutate(ticketId);
    }
  };

  // Get real attachment count for a message
  const getRealAttachmentCount = (message: any) => {
    if (!message.attachmentsUrl) return message.attachmentCount;

    const urlKey = message.attachmentsUrl;
    if (realAttachmentCounts[urlKey] !== undefined) {
      return realAttachmentCounts[urlKey];
    }

    return message.attachmentCount;
  };


  // File upload handler
  const handleFileUpload = async (files: FileList | File[] | null) => {
    // // console.log('=== FILE UPLOAD START ===');
    // // console.log('Files received:', files);

    // Immediate safety checks
    if (!files) {
      // // console.log('No files provided, returning');
      return;
    }

    if (files.length === 0) {
      // // console.log('Empty file list, returning');
      return;
    }

    try {
      // // console.log('Setting uploading state to true');
      setIsUploading(true);

      // // console.log('Initializing arrays');
      const newAttachmentIds: string[] = [];
      const newUploadedFiles: typeof uploadedFiles = [];

      // File processing loop
      // // console.log(`Processing ${files.length} files...`);

      for (let i = 0; i < files.length; i++) {
        try {
          const file = files[i];
          // // console.log(`Processing file ${i + 1}/${files.length}:`, file.name, file.type, file.size);

          // Size validation
          if (file.size > 5 * 1024 * 1024) {
            // File too large
            console.warn(`File too large: ${file.name}`);
            alert(t('ticket:msg.fileTooBig', '{{name}}: Datei zu groß (max. 5MB)', { name: file.name }));
            continue;
          }

          // Uploading file
          // // console.log(`Uploading file: ${file.name}`);
          const response = await ticketsService.uploadAttachment(file);
          // // console.log('Upload response received:', response);

          if (response && response.success) {
            // Adding to arrays
            // // console.log(`Upload successful, adding to arrays. ID: ${response.data.id}`);
            newAttachmentIds.push(response.data.id);
            newUploadedFiles.push({
              id: response.data.id,
              filename: response.data.filename,
              size: response.data.size,
              contentType: response.data.contentType
            });
          }
        } catch (fileError: any) {
          console.error(`Error processing file ${i + 1}:`, fileError);
          alert(t('ticket:msg.uploadFailed', '{{name}}: Upload fehlgeschlagen', { name: files[i].name }));
        }
      }

      // Updating state with new attachments
      // // console.log('Updating state with new attachments:', newAttachmentIds);
      setAttachmentIds(prev => [...prev, ...newAttachmentIds]);
      setUploadedFiles(prev => [...prev, ...newUploadedFiles]);

    } catch (outerError: any) {
      // console.error('=== FATAL ERROR IN FILE UPLOAD ===', outerError);
      alert(t('ticket:msg.uploadFatal', 'Fataler Fehler beim Datei-Upload'));
    } finally {
      // Setting uploading state to false
      setIsUploading(false);
      // // // console.log('=== FILE UPLOAD END ===');
    }
  };

  // Drag & drop handlers for file upload
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingFile(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingFile(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  };

  // External file upload handler (for external tickets)
  const handleExternalFileUpload = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;

    setIsUploadingExternal(true);

    for (const file of Array.from(files)) {
      try {
        // Size validation
        if (file.size > 5 * 1024 * 1024) {
          alert(t('ticket:msg.fileTooBig', '{{name}}: Datei zu groß (max. 5MB)', { name: file.name }));
          continue;
        }

        const response = await ticketsService.uploadAttachment(file);
        if (response.success) {
          setExternalUploadedFiles(prev => [...prev, {
            id: response.data.id,
            name: response.data.filename,
            size: response.data.size
          }]);
        }
      } catch (error) {
        console.error('Failed to upload file:', error);
        alert(t('ticket:msg.uploadFailed', '{{name}}: Upload fehlgeschlagen', { name: file.name }));
      }
    }

    setIsUploadingExternal(false);
  };

  // Remove attachment handler
  const removeAttachment = (attachmentId: string) => {
    setAttachmentIds(prev => prev.filter(id => id !== attachmentId));
    setUploadedFiles(prev => prev.filter(file => file.id !== attachmentId));
  };

  // Get file icon based on content type
  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) {
      return <img src="/icons/image-icon.png" alt="Image" className="h-4 w-4" />;
    }
    if (contentType === 'application/pdf') return '📄';
    if (contentType.includes('word')) return '📝';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊';
    if (contentType.includes('text')) return '📄';
    return '📎';
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Open note attachment preview
  const openNoteAttachmentPreview = async (attachments: any[], index: number) => {
    const attachment = attachments[index];

    setNoteAttachmentPreview({
      isOpen: true,
      attachments,
      currentIndex: index,
      content: null
    });

    try {
      // Fetch attachment detail from backend to get download URL
      const response = await ticketsService.getAttachment(attachment.id);
      const attachmentDetail = response.data;

      if (attachmentDetail.contentType.startsWith('image/') || attachmentDetail.contentType === 'application/pdf') {
        // Fetch and create blob URL for preview
        const previewUrl = attachmentDetail.previewUrl || attachmentDetail.downloadUrl;
        const url = await fetchAttachmentObjectUrl(previewUrl);
        setNoteAttachmentPreview(prev => ({
          ...prev,
          content: url
        }));
      }
    } catch (error) {
      console.error('Failed to load attachment preview:', error);
    }
  };

  // Navigate note attachment preview
  const navigateNoteAttachmentPreview = async (direction: 'prev' | 'next') => {
    const { attachments, currentIndex, content } = noteAttachmentPreview;
    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < attachments.length) {
      // Revoke old URL
      if (content) {
        URL.revokeObjectURL(content);
      }

      // Open new attachment
      await openNoteAttachmentPreview(attachments, newIndex);
    }
  };

  // Close note attachment preview
  const closeNoteAttachmentPreview = () => {
    if (noteAttachmentPreview.content) {
      URL.revokeObjectURL(noteAttachmentPreview.content);
    }
    setNoteAttachmentPreview({
      isOpen: false,
      attachments: [],
      currentIndex: 0,
      content: null
    });
  };

  // Send reply with attachments
  const handleSendReply = () => {
    if (!customerMessagePlainText) {
      alert(t('ticket:msg.enterMessage', 'Bitte geben Sie eine Nachricht ein'));
      return;
    }

    const currentTicketId = activeTab || ticketId;
    replyMutation.mutate({
      ticketId: currentTicketId,
      content: getContentWithSignature(),
      sendEmail: true,
      attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined
    });
  };

  // --- Sesli agent: ticket detay action'ları ---
  useVoiceActions([
    {
      id: 'read_current_ticket',
      description: 'Liest ALLES zum aktuell geöffneten Ticket: Betreff, Status, Kundendaten, kompletten Konversationsverlauf und zugehörige Bestellungen. IMMER zuerst aufrufen, bevor du antwortest.',
      run: () => {
        const kunde = `${ticket?.requester?.firstName || ''} ${ticket?.requester?.lastName || ''}`.trim();
        const info = [
          `Betreff: ${ticket?.subject || ''}`,
          `Nr: ${ticket?.ticketNumber || ''}`,
          `Status: ${ticket?.status || ''}`,
          `Priorität: ${ticket?.priority || ''}`,
          `Kunde: ${kunde} <${ticket?.requester?.email || ''}>`,
        ].join('\n');
        const convo = (conversationForAI || [])
          .map((m: any) => `[${m.role || m.sender || 'msg'}] ${extractPlainText(String(m.content || m.text || ''))}`)
          .filter((s: string) => s.trim())
          .join('\n---\n');
        let ordersTxt = '';
        try {
          const allOrders = getAllOrders?.() || [];
          if (allOrders.length) {
            ordersTxt = '\n\nBestellungen:\n' + allOrders.slice(0, 5).map((o: any) => {
              const items = (o.lineItems || []).slice(0, 8).map((i: any) => `${i.quantity || 1}× ${i.name || i.title || ''}`).join(', ');
              return `#${o.orderNumber || ''} | ${o.fulfillmentStatus || o.status || ''} | ${o.total || ''} ${o.currency || ''} | ${items}`;
            }).join('\n');
          }
        } catch { /* siparişler opsiyonel */ }
        return `${info}\n\nKonversation:\n${convo}${ordersTxt}`.slice(0, 8000);
      },
    },
    {
      id: 'read_ai_suggestion',
      description: 'Liest den vorhandenen KI-Antwortvorschlag für dieses Ticket (aus dem KI-Tab). Gibt den Vorschlagstext zurück.',
      run: async () => {
        if (aiSuggestion && aiSuggestion.trim()) return extractPlainText(aiSuggestion);
        try {
          const r = await aiService.getSuggestion(ticketId);
          const s = (r as any)?.data?.suggestion_text || '';
          return s ? extractPlainText(s) : 'Kein KI-Vorschlag vorhanden.';
        } catch { return 'Kein KI-Vorschlag vorhanden.'; }
      },
    },
    {
      id: 'read_similar_tickets',
      description: 'Liest ähnliche/frühere Tickets zu diesem Fall (Nummer + Betreff). Nützlich, um den Kunden und den Kontext besser zu verstehen, bevor du antwortest.',
      run: async () => {
        try {
          const r = await aiService.getTicketAIDraft(ticketId);
          const sims = (r as any)?.data?.ai_similar_tickets as any[] | null;
          if (!sims || sims.length === 0) return 'Keine ähnlichen Tickets gefunden.';
          return 'Ähnliche Tickets:\n' + sims.map((s: any) => `${s.ticket_number} — ${s.subject} (Ähnlichkeit: ${s.similarity})`).join('\n');
        } catch { return 'Ähnliche Tickets nicht verfügbar.'; }
      },
    },
    {
      id: 'revise_in_editor',
      description: 'Startet die echte KI-Überarbeitung des Editor-Textes (klickt sichtbar „für KI ⌘K“, zeigt die Editor-Animation und wartet auf die Antwort). WICHTIG: Schreibe VORHER mit type_in_editor deine Anweisung/den Entwurf in den Editor, denn dieser Text ist der Prompt an die KI.',
      getTarget: () => document.querySelector('[data-voice="ai-revise"]') as HTMLElement | null,
      run: async () => {
        const plain = extractPlainText(customerMessage) || '';
        if (!plain.trim()) return 'Editor ist leer — zuerst mit type_in_editor eine Anweisung/den Entwurf schreiben.';
        // Echten ⌘K-Flow auslösen (Editor-Animation sichtbar)
        handleAIChat();
        const spinning = () => !!document.querySelector('[data-voice="ai-revise"] .animate-spin');
        // auf Start warten
        for (let i = 0; i < 15 && !spinning(); i++) { await new Promise(r => setTimeout(r, 200)); }
        // auf Ende warten (KI kann langsam sein)
        for (let i = 0; i < 300 && spinning(); i++) { await new Promise(r => setTimeout(r, 200)); }
        const el = document.querySelector('[data-voice="reply-editor"] .ProseMirror') as HTMLElement | null;
        const out = (el?.innerText || '').trim();
        return out ? `KI-Überarbeitung fertig:\n${out.slice(0, 600)}` : 'KI-Überarbeitung abgeschlossen.';
      },
    },
    {
      id: 'open_ai_assistant',
      description: 'Öffnet den KI-Assistent-Tab, um den KI-Vorschlag und die Analyse anzuzeigen. Zuverlässig über den Panel-Zustand.',
      getTarget: () => document.querySelector('[data-voice="ai-assistant-tab"]') as HTMLElement | null,
      run: () => {
        setRightPanelTab('ai-assistant');
        return 'KI-Assistent-Tab geöffnet.';
      },
    },
    {
      id: 'apply_ai_suggestion',
      description: 'Übernimmt den angezeigten KI-Vorschlag in den Editor über den Button „In Editor übernehmen“. Vorher open_ai_assistant aufrufen.',
      getTarget: () => document.querySelector('[data-voice="apply-suggestion"]') as HTMLElement | null,
      run: async () => {
        // KI-Tab sicher offen
        setRightPanelTab('ai-assistant');
        // Button erscheint erst nach dem Laden des Entwurfs → kurz darauf warten
        const findBtn = () => document.querySelector('[data-voice="apply-suggestion"]') as HTMLElement | null;
        let btn = findBtn();
        for (let i = 0; i < 20 && !btn; i++) { await new Promise(r => setTimeout(r, 200)); btn = findBtn(); }
        if (btn) { btn.click(); return 'KI-Vorschlag über den Button in den Editor übernommen.'; }
        // Fallback: Vorschlag direkt holen und setzen
        let s = aiSuggestion;
        if (!s || !s.trim()) { try { const r = await aiService.getSuggestion(ticketId); s = (r as any)?.data?.suggestion_text || ''; } catch { s = ''; } }
        if (s && s.trim()) {
          setCustomerMessage(s.trim().startsWith('<') ? s : `<p>${extractPlainText(s).replace(/\n/g, '<br/>')}</p>`);
          return 'KI-Vorschlag in den Editor übernommen.';
        }
        return 'Kein KI-Vorschlag vorhanden.';
      },
    },
    {
      id: 'add_agent_note',
      description: 'Speichert eine INTERNE Notiz am Ticket (nur für das Team, wird NICHT an den Kunden gesendet). Erscheint in VynDesk als „VynDesk Agent“. Nutze es NUR wenn eine Notiz wirklich hilfreich ist (z. B. System-Mail/MAILER-DAEMON, wichtiger Hinweis für den menschlichen Agenten).',
      params: [{ name: 'text', type: 'string', description: 'Notiztext', required: true }],
      run: async ({ text }) => {
        const t = String(text || '').trim();
        if (!t) return 'Kein Notiztext angegeben.';
        try {
          await createNoteMutation.mutateAsync({ ticket_id: ticketId, title: 'Agent-Notiz', content: t, is_agent: true } as any);
          return 'Interne Notiz (als VynDesk Agent) gespeichert.';
        } catch (e: any) { return `Notiz fehlgeschlagen: ${e?.message || 'unbekannt'}`; }
      },
    },
    {
      id: 'go_to_next_ticket',
      description: 'Öffnet das nächste Ticket in der aktuellen Liste ( OHNE zu senden). Für den autonomen/Batch-Betrieb: nach save_agent_reply hiermit zum nächsten Ticket wechseln.',
      getTarget: () => document.querySelector('[data-voice="next-ticket"]') as HTMLElement | null,
      run: () => {
        const next = (adjacentTicketsData as any)?.data?.next;
        if (!next?.id) return 'Kein nächstes Ticket vorhanden — Liste abgearbeitet.';
        navigate(`/tickets/${next.id}`);
        return `Nächstes Ticket geöffnet: ${next.ticketNumber || ''}`;
      },
    },
    {
      id: 'save_agent_reply',
      description: 'Speichert die AKTUELLE Antwort im Editor als geprüften Entwurf für dieses Ticket (statt zu senden). Ein menschlicher Agent kann sie später prüfen und mit einem Klick senden. Nutze dies im autonomen/Batch-Betrieb, wenn keine sofortige Freigabe erfolgt.',
      run: async () => {
        const html = customerMessage || '';
        if (!extractPlainText(html).trim()) return 'Editor ist leer — nichts zu speichern.';
        try {
          await aiService.saveAgentDraft(ticketId, html);
          setAgentDraft(html);
          return 'Antwort als geprüfter Entwurf gespeichert (Mensch kann sie senden).';
        } catch (e: any) { return `Speichern fehlgeschlagen: ${e?.message || 'unbekannt'}`; }
      },
    },
    {
      id: 'type_in_editor',
      description:
        'Schreibt den angegebenen Text in das Antwort-Eingabefeld (überschreibt den aktuellen Entwurf).',
      params: [{ name: 'text', type: 'string', description: 'Der zu schreibende Text', required: true }],
      getTarget: () =>
        document.querySelector('[data-voice="reply-editor"] .ProseMirror') as HTMLElement | null,
      run: ({ text }) => {
        const safe = String(text || '')
          .replace(/\\r\\n|\\n|\\r/g, '\n') // model literal "\n" gönderirse gerçek satır sonuna çevir
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br/>');
        setCustomerMessage(`<p>${safe}</p>`);
        return 'Text wurde in den Editor geschrieben.';
      },
    },
    {
      id: 'append_to_editor',
      description: 'Hängt Text an den aktuellen Entwurf im Antwort-Eingabefeld an.',
      params: [{ name: 'text', type: 'string', description: 'Anzuhängender Text', required: true }],
      getTarget: () =>
        document.querySelector('[data-voice="reply-editor"] .ProseMirror') as HTMLElement | null,
      run: ({ text }) => {
        const t = String(text || '')
          .replace(/\\r\\n|\\n|\\r/g, '\n')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br/>');
        setCustomerMessage((prev) => (prev && prev.trim() ? `${prev}<p>${t}</p>` : `<p>${t}</p>`));
        return 'Text wurde angehängt.';
      },
    },
    {
      id: 'clear_editor',
      description: 'Leert das Antwort-Eingabefeld.',
      getTarget: () =>
        document.querySelector('[data-voice="reply-editor"] .ProseMirror') as HTMLElement | null,
      run: () => {
        setCustomerMessage('');
        return 'Editor geleert.';
      },
    },
    {
      id: 'send_reply',
      description: 'Sendet den aktuellen Entwurf als E-Mail-Antwort an den Kunden.',
      destructive: true,
      getTarget: () => document.querySelector('[data-voice="send-reply"]') as HTMLElement | null,
      run: async () => {
        if (isCustomerMessageEmpty) return 'Der Editor ist leer, es gibt nichts zu senden.';
        await handleCustomerMessageSend(undefined, false);
        return 'Antwort wurde an den Kunden gesendet.';
      },
    },
    {
      id: 'add_internal_note',
      description: 'Speichert den aktuellen Entwurf als interne Notiz (für den Kunden nicht sichtbar).',
      destructive: true,
      getTarget: () => document.querySelector('[data-voice="send-reply"]') as HTMLElement | null,
      run: async () => {
        if (isCustomerMessageEmpty) return 'Der Editor ist leer, es gibt keine Notiz zu speichern.';
        await handleCustomerMessageSend(undefined, true);
        return 'Interne Notiz wurde gespeichert.';
      },
    },
    {
      id: 'set_priority',
      description: 'Ändert die Priorität des aktuellen Tickets.',
      params: [
        {
          name: 'priority',
          type: 'string',
          description: t('ticketList:column.priority', 'Priorität'),
          required: true,
          enum: ['low', 'normal', 'high', 'urgent'],
        },
      ],
      run: ({ priority }) => {
        const map: Record<string, string> = {
          niedrig: 'low',
          low: 'low',
          normal: 'normal',
          mittel: 'normal',
          hoch: 'high',
          high: 'high',
          dringend: 'urgent',
          urgent: 'urgent',
        };
        const p = map[String(priority || '').toLowerCase().trim()] || '';
        if (!['low', 'normal', 'high', 'urgent'].includes(p)) return 'Unbekannte Priorität.';
        handlePriorityChange(p);
        return `Priorität auf ${p} gesetzt.`;
      },
    },
  ]);


  const handleAiSuggestionRevise = () => {
    setShowRevisionDialog(true);
  };

  const handleRevisionSubmit = async () => {
    if (!revisionPrompt.trim() || !aiSuggestion) return;

    try {
      const result = await revisionMutation.mutateAsync({
        ticket_id: ticketId,
        original_response: aiSuggestion,
        revision_prompt: revisionPrompt.trim()
      });

      if (result.data.revised_response) {
        setAiSuggestion(result.data.revised_response);
        setShowRevisionDialog(false);
        setRevisionPrompt('');
      }
    } catch (error) {
      console.error('Error creating revision:', error);
    }
  };

  // Inline revision submit handler - now works with current editor content
  const handleInlineRevisionSubmit = async () => {
    if (!inlineRevisionPrompt.trim() || !customerMessage.trim()) return;

    try {
      setIsRegenerating(true); // Show loading state
      const result = await revisionMutation.mutateAsync({
        ticket_id: ticketId,
        original_response: customerMessage, // Use current editor content instead of AI suggestion
        revision_prompt: inlineRevisionPrompt.trim()
      });

      if (result.data.revised_response) {
        setCustomerMessage(result.data.revised_response); // Update editor content
        setShowInlineRevision(false);
        setInlineRevisionPrompt('');
        setIsRegenerating(false);
        setRevisionPopoverOpen(false); // Close the popover after successful revision
      }
    } catch (error) {
      console.error('Error creating inline revision:', error);
      setIsRegenerating(false);
    }
  };

  const handleAiSuggestionReprompt = () => {
    // Start regeneration loading
    setIsRegenerating(true);

    // Show preview in conversation
    setShowAiPreviewInConversation(true);

    // Invalidate AI suggestion query to regenerate
    queryClient.invalidateQueries({
      queryKey: ['ai-response-suggestion', conversationForAI],
    });
  };

  const handleTicketNavigation = (direction: "prev" | "next") => {
    if (direction === "prev" && currentTicketIndex > 0) {
      setCurrentTicketIndex(currentTicketIndex - 1);
    } else if (
      direction === "next" &&
      currentTicketIndex < openTickets.length - 1
    ) {
      setCurrentTicketIndex(currentTicketIndex + 1);
    }
  };

  const handleExternalMessageSend = async () => {
    if (externalMessage.trim() && currentExternalConversation?.id) {
      try {
        await sendExternalMessageMutation.mutateAsync({
          conversationId: currentExternalConversation.id,
          data: { content: externalMessage }
        });
        setExternalMessage("");
      } catch (error) {
        console.error('Failed to send external message:', error);
      }
    }
  };

  // External ticket reply mutation
  const externalReplyMutation = useMutation({
    mutationFn: (data: { ticketId: string; content: string; sendEmail?: boolean; attachment_ids?: string[]; status?: string }) =>
      ticketsService.replyToTicket(data.ticketId, {
        content: data.content,
        sendEmail: data.sendEmail,
        attachment_ids: data.attachment_ids,
        status: data.status
      }),
    onSuccess: () => {
      setExternalMessage("");
      setExternalUploadedFiles([]);
      // Refresh active external ticket data to show new comment
      queryClient.invalidateQueries(['ticket', activeExternalTicketId]);
      // // console.log('✅ External ticket reply sent successfully');
    },
    onError: (error) => {
      console.error('❌ Failed to send external ticket reply:', error);
    }
  });

  // Handle external ticket tab change (NO auto-status update)
  const handleExternalTicketTabChange = (newTicketId: string) => {
    setActiveExternalTicketId(newTicketId);
  };

  // Handle external panel toggle - mark all open tickets as pending
  const handleExternalPanelToggle = () => {
    const wasCollapsed = isNebenkonversationCollapsed;

    // Toggle panel
    setIsNebenkonversationCollapsed(!wasCollapsed);

    // If opening panel and there are open tickets, mark them all as pending
    if (wasCollapsed && openExternalTicketsCount > 0) {
      const openTickets = externalTickets.filter(t => t.status === 'open');
      openTickets.forEach(ticket => {
        updateExternalTicketMutation.mutate({
          ticketId: ticket.id,
          status: 'pending'
        });
      });
    }
  };

  const handleExternalTicketReply = async () => {
    // Strip HTML tags for validation
    const cleanContent = externalMessage.replace(/<[^>]*>/g, '').trim();
    if (cleanContent && activeExternalTicketId) {
      try {
        if (isInternalExternal) {
          // Send as internal note using createNoteMutation
          const noteData = {
            ticket_id: activeExternalTicketId,
            title: 'Internal Note',
            content: externalMessage,
            parent_comment_id: null, // External ticket notes don't need parent
            attachment_ids: externalUploadedFiles.length > 0 ? externalUploadedFiles.map(f => f.id) : undefined
          };

          await createNoteMutation.mutateAsync(noteData);

          setExternalMessage("");
          setExternalUploadedFiles([]);
          setIsInternalExternal(false);
          // Refresh active external ticket data to show new note
          queryClient.invalidateQueries(['ticket', activeExternalTicketId]);
          // // console.log('✅ External ticket internal note saved successfully');
        } else {
          // Normal reply
          externalReplyMutation.mutate({
            ticketId: activeExternalTicketId,
            content: appendSignature(externalMessage),
            sendEmail: true,
            attachment_ids: externalUploadedFiles.map(f => f.id),
            status: 'pending' // ← Agent external ticket'a reply attığında otomatik pending yap
          });
        }
      } catch (error) {
        console.error('Error sending external ticket message/note:', error);
      }
    }
  };

  const applyAiSuggestion = (suggestion: string) => {
    setCustomerMessage(suggestion);
    setShowAiSuggestions(false);
  };

  const copyCustomerTextToExternal = (messageContent: string) => {
    const quotedText = `> ${messageContent.replace(/\n/g, "\n> ")}`;
    const currentExternal = externalMessage;
    const newContent = currentExternal
      ? `${currentExternal}\n\n${quotedText}`
      : quotedText;
    setExternalMessage(newContent);
    setShowCustomerTextMenu(false);
  };

  const handleCloseTab = (closingTicketId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    // Don't allow closing the original ticket from URL
    if (closingTicketId === ticketId) {
      return;
    }

    // Get next tab before removing
    const closingIndex = openTickets.findIndex(t => t.id === closingTicketId);
    const isActiveTabClosing = activeTab === closingTicketId;

    // Remove the tab using store action
    removeTab(closingTicketId);

    // If we closed the active tab, navigate to the new active tab
    if (isActiveTabClosing && openTickets.length > 1) {
      const remainingTabs = openTickets.filter(t => t.id !== closingTicketId);
      if (remainingTabs.length > 0) {
        const newActiveTab = remainingTabs[remainingTabs.length - 1];
        onTicketSelect?.(newActiveTab.id);
      }
    }
  };

  const handleTicketClick = (clickedTicketId: string) => {
    setActiveTab(clickedTicketId);
    navigate(`/tickets/${clickedTicketId}`);
    onTicketSelect?.(clickedTicketId);
  };

  const addTicketToTabs = (ticketData: any) => {
    // Add ticket to tabs using store action
    addTab({
      id: ticketData.id,
      subject: ticketData.subject,
      status: ticketData.status,
      ticket_number: ticketData.ticket_number || ticketData.ticketNumber,
    });

    setShowAddTicketModal(false);

    // Navigate to the selected ticket
    onTicketSelect?.(ticketData.id);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isResizing) {
      const container = document.querySelector(".conversation-container");
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const containerWidth = containerRect.width;

      const newLeftWidth = (mouseX / containerWidth) * 100;
      const newRightWidth = 100 - newLeftWidth - orderPanelWidth;

      if (newLeftWidth >= 20 && newLeftWidth <= 60 && newRightWidth >= 15) {
        setLeftPanelWidth(newLeftWidth);
        setRightPanelWidth(newRightWidth);
      }
    }

    if (isResizingOrder) {
      const container = document.querySelector(".conversation-container");
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const containerWidth = containerRect.width;

      const newOrderWidth = ((containerWidth - mouseX) / containerWidth) * 100;
      const conversationWidth = 100 - newOrderWidth;

      if (
        newOrderWidth >= 20 &&
        newOrderWidth <= 40 &&
        conversationWidth >= 60
      ) {
        setOrderPanelWidth(newOrderWidth);
        setLeftPanelWidth(
          (leftPanelWidth / (leftPanelWidth + rightPanelWidth)) *
          conversationWidth,
        );
        setRightPanelWidth(
          (rightPanelWidth / (leftPanelWidth + rightPanelWidth)) *
          conversationWidth,
        );
      }
    }

    if (isResizingCustomerInput) {
      const container = document.querySelector(".customer-conversation");
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const mouseY = e.clientY - containerRect.top;
      const containerHeight = containerRect.height;
      const summaryHeight = conversationSummaryHeight + 60; // Include summary and header
      const availableHeight = containerHeight - summaryHeight;

      const newInputHeight = containerHeight - mouseY;
      if (newInputHeight >= 100 && newInputHeight <= 600) {
        setCustomerInputHeight(newInputHeight);
      }
    }

    if (isResizingExternalInput) {
      const container = document.querySelector(".external-conversation");
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const mouseY = e.clientY - containerRect.top;
      const containerHeight = containerRect.height;
      const summaryHeight = conversationSummaryHeight + 60; // Include summary and header
      const availableHeight = containerHeight - summaryHeight;

      const newInputHeight = containerHeight - mouseY;
      if (newInputHeight >= 100 && newInputHeight <= 400) {
        setExternalInputHeight(newInputHeight);
      }
    }

    if (isResizingConversationSummary) {
      const container = document.querySelector(".conversation-container");
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const mouseY = e.clientY - containerRect.top;

      const newSummaryHeight = mouseY - 60; // Account for header height
      if (newSummaryHeight >= 80 && newSummaryHeight <= 300) {
        setConversationSummaryHeight(newSummaryHeight);
      }
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    setIsResizingOrder(false);
    if (isResizingCustomerInput) {
      try { localStorage.setItem('ticketComposerHeight', String(customerInputHeight)); } catch { /* dolu olabilir */ }
    }
    setIsResizingCustomerInput(false);
    setIsResizingExternalInput(false);
    setIsResizingConversationSummary(false);
  };

  React.useEffect(() => {
    if (
      isResizing ||
      isResizingOrder ||
      isResizingCustomerInput ||
      isResizingExternalInput ||
      isResizingConversationSummary
    ) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [
    isResizing,
    isResizingOrder,
    isResizingCustomerInput,
    isResizingExternalInput,
    isResizingConversationSummary,
    customerInputHeight,
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800";
      case "open":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "in_progress":
        return "bg-purple-100 text-purple-800";
      case "solved":
        return "bg-teal-100 text-teal-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };


  if (isLoading) {
    // Skelett spiegelt das ECHTE Layout (Topbar → Konversation + Composer →
    // Info-Rail 420px, keine linke Spalte mehr). Bewusst in der
    // .ticket-detail-shell verpackt: damit greifen sämtliche Dark-Overrides
    // (bg-white/slate-Mapping) automatisch auch hier — kein weißer Blitz im
    // Dark-Modus.
    return (
      <div className="ticket-detail-shell bg-white h-full flex flex-col overflow-hidden">
        {/* Topbar: Betreff links, Aktions-Icons rechts */}
        <div className="border-b px-4 py-2.5 flex items-center justify-between gap-4 flex-shrink-0">
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-5 w-72 max-w-full" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
          </div>
        </div>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Konversation: Nachrichten in voller Breite + Composer unten */}
          <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
            <div className="flex-1 overflow-hidden p-4 space-y-6">
              {[0, 1].map((i) => (
                <div key={i} className="space-y-2.5 border-b border-slate-100 pb-6">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-11/12" />
                  <Skeleton className="h-3 w-4/5" />
                  {i === 0 && <Skeleton className="h-3 w-2/3" />}
                </div>
              ))}
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-3 w-10/12" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            </div>

            {/* Composer */}
            <div className="border-t p-3 flex items-center gap-2 flex-shrink-0">
              <Skeleton className="h-9 w-9 rounded-md" />
              <Skeleton className="h-9 w-9 rounded-md" />
              <div className="flex-1" />
              <Skeleton className="h-9 w-9 rounded-md" />
              <Skeleton className="h-9 w-9 rounded-md" />
              <Skeleton className="h-9 w-28 rounded-md" />
            </div>
          </div>

          {/* Info-Rail rechts (Ticket Info / Cloud-Agent) */}
          <div className="ticket-info-sidebar w-[420px] border-l flex-col shrink-0 overflow-hidden p-4 space-y-5 hidden lg:flex">
            {/* Tab-Pills */}
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 flex-1 rounded-md" />
              <Skeleton className="h-8 flex-1 rounded-md" />
            </div>

            {/* Kunde / Referenz */}
            <div className="space-y-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-9 w-full rounded-lg" />
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-9 w-full rounded-lg" />
            </div>

            {/* Status / Priorität / Zuweisung */}
            <div className="grid grid-cols-2 gap-2.5">
              <Skeleton className="h-9 rounded-lg" />
              <Skeleton className="h-9 rounded-lg" />
              <Skeleton className="h-9 rounded-lg" />
              <Skeleton className="h-9 rounded-lg" />
            </div>

            {/* Bestelldetails-Karte */}
            <div className="space-y-2.5">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-24 w-full rounded-xl" />
            </div>

            {/* Akkordeons (Bestellungen / SLA / Zeitlinie) */}
            <div className="space-y-2.5">
              <Skeleton className="h-8 w-full rounded-md" />
              <Skeleton className="h-8 w-full rounded-md" />
              <Skeleton className="h-8 w-full rounded-md" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white h-full flex flex-col overflow-hidden items-center justify-center">
        <AlertTriangle className="h-8 w-8 text-red-400 mb-2" />
        <p className="text-red-500">{t('errors:fix.loadFailed', 'Ticket konnte nicht geladen werden')}</p>
      </div>
    );
  }

  return (
    <div className="ticket-detail-shell bg-white h-full flex flex-col overflow-hidden">
      {/* Ticket Header */}
      <div className="ticket-topbar border-b px-4 py-2.5 flex-shrink-0">
        {/* Simplified Ticket Header */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: Subject only */}
          <div className="ticket-subject-area min-w-0 flex-1">
            {isEditingSubject ? (
              <form
                className="ticket-subject-editor"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleSubjectSave();
                }}
              >
                <Input
                  autoFocus
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      handleSubjectCancel();
                    }
                  }}
                  className="ticket-subject-input"
                  aria-label="Ticket-Betreff"
                />
                <div className="ticket-subject-editor-actions">
                  <Button
                    type="submit"
                    variant="ghost"
                    size="icon"
                    className="ticket-subject-action ticket-subject-save"
                    disabled={updateTicketMutation.isPending || !subject.trim()}
                    aria-label={updateTicketMutation.isPending ? 'Betreff wird gespeichert' : 'Betreff speichern'}
                    title="Speichern (Enter)"
                  >
                    {updateTicketMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="ticket-subject-action ticket-subject-cancel"
                    onClick={handleSubjectCancel}
                    disabled={updateTicketMutation.isPending}
                    aria-label="Bearbeitung abbrechen"
                    title="Abbrechen (Esc)"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            ) : (
              <button
                type="button"
                className="ticket-subject-trigger"
                onClick={() => setIsEditingSubject(true)}
                title="Betreff bearbeiten"
                aria-label={`Betreff bearbeiten: ${subject}`}
              >
                <h1 className="ticket-subject-title">
                  {subject}
                </h1>
              </button>
            )}
          </div>

          {/* Right: All controls */}
          <div className="ticket-header-actions flex min-w-0 items-center gap-2 flex-shrink-0">
            {/* Parent Ticket Badge (if this is a merged child ticket) */}
            {ticket?.parent_ticket_number && (
              <Badge
                variant="outline"
                className="h-6 rounded-full border border-orange-300 bg-orange-50/80 px-3 text-xs font-medium text-orange-700 shadow-sm cursor-pointer hover:bg-orange-100/80 transition"
                onClick={() => window.open(`/tickets/${ticket.parent_ticket_id_actual}`, '_blank')}
                title={`Combined main ticket: ${ticket.parent_ticket_subject}`}
              >
                <Merge className="h-3 w-3 mr-1 inline" />
                {ticket.parent_ticket_number}
              </Badge>
            )}

            {/* Merged Children Count Badge (if this is a parent ticket with merged children) */}
            {ticket?.merged_ticket_count > 0 && !ticket?.parent_ticket_number && (
              <Badge
                variant="outline"
                className="h-6 rounded-full border border-orange-300 bg-orange-50/80 px-3 text-xs font-medium text-orange-700 shadow-sm"
                title={`${ticket.merged_ticket_count} ticket bu ana ticket'a birleştirilmiş`}
              >
                <Merge className="h-3 w-3 mr-1 inline" />
                {ticket.merged_ticket_count} Birleştirilmiş
              </Badge>
            )}

            {/* KI-Kategorien - Header'da yatay gösterim */}
            {systemTagsData?.data?.tags && systemTagsData.data.tags.length > 0 && (() => {
              // Kategori tag'leri (parent yok, Produkt değil)
              const categoryTags = systemTagsData.data.tags.filter(t => !t.parent_id && t.name !== 'Produkt' && t.tag_level !== 3);

              // Ürünler: parent_name === 'Produkt' olanlar (eski sistem)
              const productTags = systemTagsData.data.tags.filter(t => t.parent_name === 'Produkt');

              // Renk paleti
              const colors = {
                category: { border: '#6366F1', bg: '#EEF2FF', text: '#4F46E5' },      // Indigo
                subcategory: { border: '#8B5CF6', bg: '#F5F3FF', text: '#7C3AED' },   // Violet
                product: { border: '#10B981', bg: '#ECFDF5', text: '#059669' }        // Emerald
              };

              return (
                <div className="ticket-header-taxonomy flex items-center gap-1">
                  {/* Kanal rozeti: agent, cevabın E-Mail değil WhatsApp'a
                      gideceğini başlıkta görmeli. */}
                  {(ticket as any)?.channel === 'whatsapp' && (
                    <Badge
                      variant="outline"
                      className="ticket-header-taxonomy-badge h-6 rounded-md px-2.5 text-[11px] font-semibold"
                      style={{ borderColor: '#bbe8cf', backgroundColor: '#eafaf1', color: '#1d8a5f' }}
                    >
                      <WhatsAppIcon className="mr-1 h-3 w-3" />
                      WhatsApp
                    </Badge>
                  )}
                  {(ticket as any)?.channel === 'messenger' && (
                    <Badge
                      variant="outline"
                      className="ticket-header-taxonomy-badge h-6 rounded-md px-2.5 text-[11px] font-semibold"
                      style={{ borderColor: '#bcd4f5', backgroundColor: '#e9f1fd', color: '#1d5fb8' }}
                    >
                      Messenger
                    </Badge>
                  )}
                  {(ticket as any)?.channel === 'instagram' && (
                    <Badge
                      variant="outline"
                      className="ticket-header-taxonomy-badge h-6 rounded-md px-2.5 text-[11px] font-semibold"
                      style={{ borderColor: '#f3c3dd', backgroundColor: '#fdeaf4', color: '#c02579' }}
                    >
                      Instagram
                    </Badge>
                  )}
                  {categoryTags.map((parentTag, pIdx) => {
                    const childTags = systemTagsData.data.tags.filter(t => t.parent_id === parentTag.id && t.tag_level !== 3);

                    return (
                      <div key={parentTag.id} className="flex items-center">
                        {pIdx > 0 && <span className="ticket-header-taxonomy-separator">·</span>}

                        {/* Category Badge */}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="ticket-header-taxonomy-badge h-6 rounded-md px-2.5 text-[11px] font-medium cursor-help"
                                style={{
                                  borderColor: colors.category.border,
                                  backgroundColor: colors.category.bg,
                                  color: colors.category.text
                                }}
                              >
                                {parentTag.name}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p className="text-xs font-medium">{t('common:x3.categoryColon', 'Kategorie:')} {parentTag.name}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        {/* Subcategories */}
                        {childTags.map((childTag) => {
                          const productTags = systemTagsData.data.tags.filter(t => t.parent_id === childTag.id && t.tag_level === 3);

                          return (
                            <React.Fragment key={childTag.id}>
                              <ChevronRight className="h-3 w-3 text-slate-400 mx-0.5" />
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className="ticket-header-taxonomy-badge h-6 rounded-md px-2.5 text-[11px] font-medium cursor-help"
                                      style={{
                                        borderColor: colors.subcategory.border,
                                        backgroundColor: colors.subcategory.bg,
                                        color: colors.subcategory.text
                                      }}
                                    >
                                      {childTag.name}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom">
                                    <p className="text-xs font-medium">{t('common:x3.subcategoryColon', 'Unterkategorie:')} {childTag.name}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>

                              {/* Products under subcategory */}
                              {productTags.map((productTag) => (
                                <React.Fragment key={productTag.id}>
                                  <ChevronRight className="h-3 w-3 text-slate-400 mx-0.5" />
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge
                                          variant="outline"
                                          className="ticket-header-taxonomy-badge h-6 rounded-md px-2.5 text-[11px] font-medium cursor-help"
                                          style={{
                                            borderColor: colors.product.border,
                                            backgroundColor: colors.product.bg,
                                            color: colors.product.text
                                          }}
                                        >
                                          {productTag.name}
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom">
                                        <p className="text-xs font-medium">{t('common:x1.productColon', 'Produkt:')} {productTag.name}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </React.Fragment>
                              ))}
                            </React.Fragment>
                          );
                        })}

                        {/* Direct products under category (no subcategory) */}
                        {systemTagsData.data.tags
                          .filter(t => t.parent_id === parentTag.id && t.tag_level === 3)
                          .map((productTag) => (
                            <React.Fragment key={productTag.id}>
                              <ChevronRight className="h-3 w-3 text-slate-400 mx-0.5" />
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className="ticket-header-taxonomy-badge h-6 rounded-md px-2.5 text-[11px] font-medium cursor-help"
                                      style={{
                                        borderColor: colors.product.border,
                                        backgroundColor: colors.product.bg,
                                        color: colors.product.text
                                      }}
                                    >
                                      {productTag.name}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom">
                                    <p className="text-xs font-medium">{t('common:x1.productColon', 'Produkt:')} {productTag.name}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </React.Fragment>
                          ))}
                      </div>
                    );
                  })}

                  {/* Ürünler (parent_name === 'Produkt' olanlar) */}
                  {productTags.length > 0 && (
                    <>
                      {categoryTags.length > 0 && <span className="ticket-header-taxonomy-separator">·</span>}
                      {productTags.map((prodTag, idx) => (
                        <React.Fragment key={prodTag.id}>
                          {idx > 0 && <span className="ticket-header-taxonomy-separator">·</span>}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className="ticket-header-taxonomy-badge h-6 rounded-md px-2.5 text-[11px] font-medium cursor-help"
                                  style={{
                                    borderColor: colors.product.border,
                                    backgroundColor: colors.product.bg,
                                    color: colors.product.text
                                  }}
                                >
                                  {prodTag.name}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                <p className="text-xs font-medium">{t('common:x1.productColon', 'Produkt:')} {prodTag.name}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </div>
              );
            })()}

            <div className="ticket-header-utilities flex items-center gap-0.5 rounded-xl border border-slate-200 bg-slate-50/80 p-1">
            {/* Internal Notes Popover */}
            <Popover open={notesPopoverOpen} onOpenChange={setNotesPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="relative h-7 w-7 rounded-full border border-slate-200 bg-white/80 p-0 text-slate-600 shadow-sm transition hover:border-amber-400 hover:text-amber-600"
                >
                  <StickyNote className="h-4 w-4" />
                  {notesQuery.data?.data?.notes && notesQuery.data.data.notes.length > 0 && (
                    <span className="ticket-header-count absolute -top-1 -right-1 w-4 h-4 text-[10px] font-bold rounded-full flex items-center justify-center">
                      {notesQuery.data.data.notes.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 rounded-xl border border-slate-200 bg-white p-0 shadow-lg" align="end">
                <div className="bg-amber-50 px-3 py-2 border-b border-amber-200 rounded-t-xl">
                  <h3 className="text-xs font-semibold text-amber-800 flex items-center gap-2">
                    <StickyNote className="h-3.5 w-3.5 text-amber-600" />
                    {t('common:x1.internalNotesCount', 'Interne Notizen (')}{notesQuery.data?.data?.notes?.length || 0})
                  </h3>
                </div>
                <ScrollArea className="max-h-[300px]">
                  <div className="p-3 space-y-2">
                    {notesQuery.data?.data?.notes && notesQuery.data.data.notes.length > 0 ? (
                      notesQuery.data.data.notes.map((note) => (
                        <div key={note.id} className={cn("border rounded-md p-2.5", (note as any).is_agent ? "bg-[#0F99FD]/5 border-[#0F99FD]/30" : "bg-amber-50/50 border-amber-200")}>
                          <div className="flex items-start justify-between mb-1.5">
                            <span className={cn("text-[10px] font-medium flex items-center gap-1", (note as any).is_agent ? "text-[#0F99FD]" : "text-amber-700")}>
                              {(note as any).is_agent && <Sparkles className="h-2.5 w-2.5" />}
                              {(note as any).is_agent ? 'VynDesk Agent' : (note.author_name || note.author_email || 'Unbekannt')}
                            </span>
                            <span className="text-[9px] text-slate-400">
                              {formatDateTime(note.created_at)}
                            </span>
                          </div>
                          <div
                            className="text-[11px] text-slate-700 break-words leading-relaxed [&_p]:my-1 [&_strong]:font-semibold"
                            dangerouslySetInnerHTML={{ __html: note.content }}
                          />
                          {note.attachments && note.attachments.length > 0 && (
                            <div className="flex items-center gap-1 mt-2 text-[10px] text-amber-600">
                              <PaperclipIcon className="h-3 w-3" />
                              <span>{note.attachments.length} Anhang{note.attachments.length > 1 ? 'e' : ''}</span>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-slate-400 text-xs">
                        {t('common:x3.noInternalNotes', 'Keine internen Notizen vorhanden')}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>

            <Popover open={reminderPopoverOpen} onOpenChange={setReminderPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="relative h-7 w-7 rounded-full border border-slate-200 bg-white/80 p-0 text-slate-600 shadow-sm transition hover:border-[#0F99FD]/40 hover:text-[#0F99FD]"
                >
                  <CalendarIcon className="h-4 w-4" />
                  {reminders.length > 0 && (
                    <span className="ticket-header-count absolute -top-1 -right-1 w-4 h-4 text-[10px] font-bold rounded-full flex items-center justify-center">
                      {reminders.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto rounded-xl border border-slate-200 bg-white p-0 shadow-lg data-[state=open]:animate-none data-[state=closed]:animate-none">
                <Calendar
                  mode="single"
                  selected={undefined}
                  onSelect={handleCalendarSelect}
                  weekStartsOn={1}
                  modifiers={{
                    hasReminder: reminderDates,
                  }}
                  modifiersStyles={{
                    hasReminder: {
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                      color: '#3b82f6',
                    },
                  }}
                />
                <div className="border-t border-slate-200 bg-slate-50 px-3 py-2">
                  <Button
                    size="sm"
                    className="w-full rounded-full bg-[#0F99FD] text-white hover:bg-[#0c84e0]"
                    onClick={() => {
                      setReminderPopoverOpen(false);
                      handleOpenReminderDialog(new Date());
                    }}
                  >
                    {t('common:x1.newReminder', 'Neue Erinnerung')}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 rounded-full border border-slate-200 bg-white/80 p-0 text-slate-600 shadow-sm transition hover:border-[#0F99FD]/40 hover:text-[#0F99FD] relative"
              onClick={handleExternalPanelToggle}
              title={isNebenkonversationCollapsed ? "Nebenkonversation anzeigen" : "Nebenkonversation ausblenden"}
            >
              <MessageSquare className="h-4 w-4" />
              {openExternalTicketsCount > 0 && (
                <span className="ticket-header-count absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold">
                  {openExternalTicketsCount}
                </span>
              )}
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 rounded-full border border-orange-200 bg-white/80 p-0 text-orange-600 shadow-sm transition hover:border-orange-500 hover:bg-orange-50"
              onClick={() => setShowMergeTicketModal(true)}
              title={t('ticket:action.mergeTickets', 'Tickets zusammenführen')}
            >
              <Merge className="h-4 w-4" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              className={cn(
                "h-7 w-7 rounded-full border bg-white/80 p-0 shadow-sm transition",
                ticket?.isSpam
                  ? "border-blue-500 text-blue-600 hover:border-blue-600 hover:bg-blue-50"
                  : "border-slate-300 text-slate-600 hover:border-slate-400 hover:bg-slate-50"
              )}
              onClick={() => setSpamDialogOpen(true)}
              title={ticket?.isSpam ? "Archiviert" : "Nicht archiviert"}
            >
              <Archive className="h-4 w-4" />
            </Button>

            {/* Anheften — UI ausgeblendet (Backend bleibt), bei Bedarf einkommentieren:
            <Button ... onClick={() => (isTicketPinned ? unpinTicketMutation.mutate() : pinTicketMutation.mutate())}>
              {isTicketPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            </Button> */}

            {currentUser?.role === 'admin' && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 rounded-full border border-red-200 bg-white/80 p-0 text-red-500 shadow-sm transition hover:border-red-500 hover:bg-red-50 hover:text-red-600"
                onClick={() => setDeleteDialogOpen(true)}
                title={t('ticket:action.deleteTicket', 'Ticket löschen')}
              >
                <Trash className="h-4 w-4" />
              </Button>
            )}
            </div>

          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 min-h-0 conversation-container overflow-hidden">
        {/* Customer Conversation */}
        <div
          className="flex flex-col customer-conversation transition-all duration-300 h-full flex-1 min-w-0 overflow-hidden"
        >
          <div className="flex-1 min-h-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="pb-4">
                {(() => {
                  // Transform comments
                  const transformedComments = apiComments.length > 0
                    ? apiComments.map(comment => transformApiCommentToFrontend(comment, ticket?.requester_id || '', undefined, !!((ticket as any)?.internalTargetOrgId || (ticket as any)?.internal_target_org_id)))
                    : customerMessages;

                  // Get notes and mark them
                  const notes = (notesQuery.data?.data?.notes || []).map((note: Note) => ({
                    ...note,
                    isNote: true
                  }));

                  // Merge comments and notes
                  const merged = [...transformedComments, ...notes];

                  // Sort by creation time
                  merged.sort((a, b) => {
                    const timeA = a.isNote
                      ? new Date(a.created_at).getTime()
                      : new Date(a.timestamp).getTime();
                    const timeB = b.isNote
                      ? new Date(b.created_at).getTime()
                      : new Date(b.timestamp).getTime();
                    return timeA - timeB;
                  });

                  return merged;
                })().map((message: any) => {
                  // Eğer not ise özel render
                  if (message.isNote) {
                    return (
                      <div
                        key={`note-${message.id}`}
                        className="group relative w-full border-b border-slate-100 px-4 py-4 transition bg-red-50/70 hover:bg-red-50/90"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700 border border-red-200">
                                Internal Note
                              </Badge>
                              <span className="text-xs text-slate-500">
                                {formatDateTime(message.created_at)}
                              </span>
                              {(message.author_name || message.author_email) && (
                                <span className="text-xs font-medium text-slate-600">
                                  · {message.author_name || message.author_email?.split('@')[0]}
                                </span>
                              )}
                            </div>
                            <div
                              className="text-sm leading-relaxed text-slate-700"
                              dangerouslySetInnerHTML={{ __html: sanitizeContent(message.content) }}
                            />

                            {/* Note Attachments - Use same component as replies */}
                            {message.attachments_url && message.attachment_count > 0 && (
                              <div className="mt-3 text-sm">
                                <InlineAttachmentPreview
                                  attachmentsUrl={message.attachments_url}
                                  attachmentCount={message.attachment_count}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Normal comment render
                  const isCustomer = message.sender === "customer";
                  const isInternal = message.isInternal;
                  return (
                    <div
                      key={message.id}
                      className={cn(
                        "group relative w-full border-b border-slate-100 px-4 py-4 transition",
                        isInternal
                          ? "bg-amber-50/50"
                          : isCustomer
                            ? "bg-white hover:bg-slate-50/50"
                            : "bg-blue-50/40 hover:bg-blue-50/60"
                      )}
                    >
                      <div className="flex items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-slate-900">
                              {message.author?.name || message.name}
                            </span>
                            {!isCustomer && (
                              <Badge
                                className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 border border-blue-200"
                              >
                                {t('common:x1.agent', 'Agent')}
                              </Badge>
                            )}
                            {isInternal && (
                              <Badge
                                className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 border border-amber-200"
                              >
                                {t('common:x1.internalNote', 'Interne Notiz')}
                              </Badge>
                            )}
                            <span className="text-xs text-slate-500">
                              {formatDateTime(message.timestamp)}
                            </span>

                            {/* Translate button inline with header */}
                            <Popover
                              open={translatePopoverOpen[message.id] || false}
                              onOpenChange={(open) => {
                                if (open) {
                                  openTranslatePopover(message.id);
                                } else {
                                  closeTranslatePopover(message.id);
                                }
                              }}
                            >
                              <PopoverTrigger asChild>
                                <button
                                  className={cn(
                                    "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] transition",
                                    translatingMessageId === message.id
                                      ? "text-red-500"
                                      : translatedMessages[message.id]
                                        ? "text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                        : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                                  )}
                                  disabled={translatingMessageId === message.id}
                                >
                                  {translatingMessageId === message.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Languages className="h-3 w-3" />
                                  )}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 p-3" align="start">
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-medium text-sm">{t('ticket:ui.selectLanguage', 'Sprache wählen')}</h4>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => closeTranslatePopover(message.id)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>

                                  {translatedMessages[message.id] && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="w-full h-8 text-xs justify-center"
                                      onClick={() => { toggleTranslation(message.id); closeTranslatePopover(message.id); }}
                                    >
                                      <X className="h-3 w-3 mr-1" />
                                      {t('common:x3.showOriginal', 'Original anzeigen')}
                                    </Button>
                                  )}

                                  {recentLanguages.length > 0 && (
                                    <div className="space-y-1.5">
                                      <p className="text-xs text-slate-500 font-medium">{t('ticket:section.recentlyUsed', 'Zuletzt verwendet')}</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {recentLanguages.map((lang) => (
                                          <Button
                                            key={lang.code}
                                            variant="outline"
                                            size="sm"
                                            className="h-8 text-xs"
                                            onClick={() => handleTranslate(message.id, message.content, lang.code)}
                                            disabled={translatingMessageId === message.id}
                                          >
                                            <img src={`https://flagcdn.com/w20/${lang.countryCode}.png`} alt={lang.name} className="mr-1.5 h-3 w-4 object-cover rounded-sm" />
                                            {lang.nativeName}
                                          </Button>
                                        ))}
                                      </div>
                                      <Separator className="my-2" />
                                    </div>
                                  )}

                                  <div className="space-y-1.5">
                                    <p className="text-xs text-slate-500 font-medium">{t('ticket:section.allLanguages', 'Alle Sprachen')}</p>
                                    <ScrollArea className="h-48">
                                      <div className="grid grid-cols-2 gap-1.5 pr-3">
                                        {SUPPORTED_LANGUAGES.map((lang) => (
                                          <Button
                                            key={lang.code}
                                            variant="ghost"
                                            size="sm"
                                            className="h-9 justify-start text-xs hover:bg-blue-50"
                                            onClick={() => handleTranslate(message.id, message.content, lang.code)}
                                            disabled={translatingMessageId === message.id}
                                          >
                                            <img src={`https://flagcdn.com/w20/${lang.countryCode}.png`} alt={lang.name} className="mr-2 h-3 w-4 object-cover rounded-sm" />
                                            <span className="truncate">{lang.nativeName}</span>
                                          </Button>
                                        ))}
                                      </div>
                                    </ScrollArea>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>

                          {(() => {
                            const content = translatedMessages[message.id] || message.content;
                            const isLong = isCommentLong(content);
                            const isExpanded = expandedComments.has(message.id);
                            const displayContent = isLong && !isExpanded ? getTruncatedContent(content) : content;

                            return (
                              <>
                                <div className="relative">
                                  <div
                                    className={cn(
                                      "text-sm leading-relaxed text-slate-700",
                                      isLong && !isExpanded && "max-h-[300px] overflow-hidden"
                                    )}
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeContent(displayContent)
                                    }}
                                  />
                                  {isLong && !isExpanded && (
                                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                                  )}
                                </div>
                                {isLong && (
                                  <button
                                    onClick={() => toggleCommentExpanded(message.id)}
                                    className="mt-2 text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
                                  >
                                    {isExpanded ? (
                                      <>
                                        <ChevronUp className="h-3 w-3" />
                                        {t('common:action.showLess', 'Weniger anzeigen')}
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="h-3 w-3" />
                                        {t('common:x1.showMore', 'Mehr anzeigen (')}{Math.round(content.length / 1000)}K Zeichen)
                                      </>
                                    )}
                                  </button>
                                )}
                              </>
                            );
                          })()}

                          {/* Note Attachments (for internal notes) */}
                          {isInternal && message.attachments && message.attachments.length > 0 && (
                            <div className="mt-2">
                              <div className="flex flex-wrap gap-1.5">
                                {message.attachments.map((attachment: any) => (
                                  <div
                                    key={attachment.id}
                                    className="ticket-attachment-chip inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200/80 bg-transparent px-2.5 py-1.5 text-slate-600"
                                  >
                                    <span className="flex flex-shrink-0 items-center justify-center text-slate-400">
                                      {attachment.content_type?.startsWith('image/')
                                        ? <ImageIcon className="h-3.5 w-3.5" />
                                        : attachment.content_type === 'application/pdf' ||
                                          attachment.content_type?.includes('word') ||
                                          attachment.content_type?.includes('excel')
                                          ? <FileText className="h-3.5 w-3.5" />
                                          : <Paperclip className="h-3.5 w-3.5" />}
                                    </span>
                                    <span className="max-w-36 truncate text-xs font-medium">{attachment.filename}</span>
                                    <span className="flex-shrink-0 text-[10px] font-normal tabular-nums text-slate-400">
                                      {attachment.file_size > 1024 * 1024
                                        ? `${(attachment.file_size / (1024 * 1024)).toFixed(1)} MB`
                                        : `${(attachment.file_size / 1024).toFixed(1)} KB`
                                      }
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {message.hasAttachments && (
                            <div className="mt-3 text-sm">
                              <InlineAttachmentPreview
                                attachmentsUrl={message.attachmentsUrl}
                                attachmentCount={getRealAttachmentCount(message)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

              </div>
            </ScrollArea>
          </div>


          {/* Mesajlar ↕ composer ayırıcısı: sürükle → yükseklik; localStorage'da kalıcı */}
          <div
            className="ticket-composer-resizer group flex-shrink-0"
            onMouseDown={(e) => { e.preventDefault(); setIsResizingCustomerInput(true); }}
            title={t('ticket:action.resizeComposer', 'Höhe anpassen — ziehen')}
          >
            <span className="ticket-composer-resizer-grip" />
          </div>
          <div
            className="ticket-reply-composer border-t bg-white flex-shrink-0"
            style={{ height: `${customerInputHeight}px`, minHeight: `${customerInputHeight}px` }}
          >
            <div className="h-full flex flex-col">
              <div className="space-y-3 flex-1">
                <div
                  className={cn(
                    "relative flex-1 transition-all duration-200",
                    isDraggingFile && "ring-2 ring-[#0F99FD] ring-offset-2 rounded-lg bg-[#0F99FD]/5"
                  )}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {isDraggingFile && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-[#0F99FD]/10 border-2 border-dashed border-[#0F99FD] pointer-events-none">
                      <div className="flex flex-col items-center gap-2 text-[#0F99FD]">
                        <Paperclip className="h-8 w-8" />
                        <span className="font-medium text-sm">{t('ticket:ui.dropFiles', 'Dateien hier ablegen')}</span>
                      </div>
                    </div>
                  )}

                  {/* Pre-generated AI Suggestion Card - TEMPORARILY DISABLED */}
                  {/* TODO: Re-enable when AI suggestion feature is ready
                  {preGeneratedSuggestion && !suggestionDismissed && !preGeneratedSuggestion.is_used && (
                    <div className="mb-3 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-3 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
                            <Sparkles className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-emerald-800">{t('ticket:ui.aiSuggestion', 'KI-Antwortvorschlag')}</h4>
                            <p className="text-[10px] text-emerald-600">
                              {preGeneratedSuggestion.trigger_type === 'new_ticket' ? 'Für neues Ticket' : 'Für neue Antwort'} • {preGeneratedSuggestion.conversation_length} Nachrichten analysiert
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRegeneratePreSuggestion}
                            disabled={isRegenerating}
                            className="h-7 px-2 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700"
                            title={t('ticket:action.regenerate', 'Neu generieren')}
                          >
                            <RefreshCw className={cn("h-3.5 w-3.5", isRegenerating && "animate-spin")} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSuggestionDismissed(true)}
                            className="h-7 px-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                            title={t('ticket:action.hide', 'Ausblenden')}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div
                        className="mt-2 max-h-24 overflow-y-auto rounded-lg bg-white/70 p-2.5 text-xs text-slate-700 border border-emerald-100"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            preGeneratedSuggestion.suggestion_text.length > 300
                              ? preGeneratedSuggestion.suggestion_text.substring(0, 300) + '...'
                              : preGeneratedSuggestion.suggestion_text
                          )
                        }}
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={handleUsePreGeneratedSuggestion}
                          className="h-7 bg-emerald-600 px-3 text-xs font-medium text-white hover:bg-emerald-700"
                        >
                          <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
                          {t('common:x3.applySuggestion', 'Vorschlag übernehmen')}
                        </Button>
                        <span className="text-[10px] text-emerald-500">
                          {t('common:x4.generated', 'Generiert')} {formatTime(preGeneratedSuggestion.generated_at)}
                        </span>
                      </div>
                    </div>
                  )}
                  */}

                  <div className="ticket-reply-editor-host relative" data-voice="reply-editor">
                  {isGeneratingAI && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/75 backdrop-blur-[1px] pointer-events-none">
                      <div className="flex flex-col items-center gap-1">
                        <Lottie
                          animationData={aiProvider === 'gpt' ? openaiAnimation : claudeAnimation}
                          loop
                          autoplay
                          style={{ width: 96, height: 96 }}
                        />
                        <span className="text-xs font-medium text-slate-500">
                          {aiProvider === 'gpt' ? 'GPT-5.5 denkt nach…' : 'Claude denkt nach…'}
                        </span>
                      </div>
                    </div>
                  )}
                  {((ticket as any)?.internalTargetOrgId || (ticket as any)?.internal_target_org_id) && (
                    <div className="mb-1.5 flex items-center gap-2 rounded-md bg-indigo-50 px-3 py-1.5 text-[11px] font-medium text-indigo-700 ring-1 ring-indigo-600/10">
                      <span className="rounded bg-indigo-600 px-1.5 py-0.5 text-[9px] font-bold text-white">{t('ticket:ui.internal', 'INTERN')}</span>
                      {t('ticket:ui.internalConv', 'Interne Konversation')}{((ticket as any)?.internalTargetOrgName || (ticket as any)?.internal_target_org_name) ? ` mit „${(ticket as any).internalTargetOrgName || (ticket as any).internal_target_org_name}“` : ''} — keine E-Mail; die Antwort erscheint direkt beim Gegenüber.
                    </div>
                  )}
                  <RichTextEditor
                    className="ticket-reply-editor"
                    value={customerMessage}
                    onChange={setCustomerMessage}
                    placeholder={t('ticket:ph.reply', 'Nachricht an Kunden eingeben... Tippe / für Textbausteine')}
                    onSubmit={handleCustomerMessageSend}
                    disabled={replyMutation.isPending}
                    isLoading={isGeneratingAI || isRegenerating}
                    onImagePaste={(files) => handleFileUpload(files)}
                    slashCommandState={slashCommandState}
                    onSlashCommandChange={setSlashCommandState}
                    onSlashCommandSelect={() => {}}
                    enableAutocomplete={false}
                    autocompleteContext={{ ticketSubject: ticket?.subject, customerName: ticket?.customer?.name }}
                    actionButton={
                      <div className="ticket-reply-toolbar-actions flex items-center gap-1.5">
                        {agentDraft && (
                          <button
                            type="button"
                            onClick={() => {
                              let t = (agentDraft || '')
                                .replace(/&amp;/g, '&')
                                .replace(/&lt;\s*(\/?)(br|strong|b|p|ul|ol|li|em|i|div)\s*\/?\s*&gt;/gi, '<$1$2>')
                                .replace(/\r\n/g, '\n').replace(/\n{2,}/g, '<br><br>').replace(/\n/g, '<br>')
                                .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
                              setCustomerMessage(t);
                            }}
                            title={t('ticket:action.toEditor', 'Gespeicherte KI-Antwort in den Editor übernehmen')}
                            className="ticket-ai-draft-button"
                          >
                            <span className="ticket-ai-draft-icon" aria-hidden="true">
                              <img src={agentIcon} alt="" />
                            </span>
                            <span>{t('common:x1.aiReplyReady', 'KI-Antwort bereit')}</span>
                          </button>
                        )}
                        {showActionButtons && (
                          <>
                            {/* AI Shortcut Hint */}
                            <div
                              className={cn(
                                "flex items-center gap-1 px-2 py-1 rounded-md text-[10px] transition cursor-pointer select-none",
                                isGeneratingAI
                                  ? "bg-[#0F99FD]/10 text-[#0F99FD]"
                                  : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                              )}
                              onClick={handleAIChat}
                              title={t('ticket:section.aiAssistant', 'KI-Assistent')}
                              data-voice="ai-revise"
                            >
                              {isGeneratingAI && <Loader2 className="h-3 w-3 animate-spin" />}
                              <span className="font-medium">für KI ⌘K</span>
                            </div>

                            {/* Signature Toggle */}
                            {signatureHtml && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSignaturePanelOpen(prev => !prev)}
                                className={cn(
                                  "flex items-center justify-center rounded-md p-1.5 transition",
                                  signaturePanelOpen
                                    ? "text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700"
                                    : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                )}
                                title={signaturePanelOpen ? "Signatur ausblenden" : "Signatur anzeigen"}
                              >
                                <FileSignature className="h-4 w-4" />
                              </Button>
                            )}

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowTextSnippets({ isOpen: true, context: 'customer' })}
                              className="flex items-center justify-center rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                              title={t('ticket:action.insertSnippet', 'Textbaustein einfügen')}
                            >
                              <Zap className="h-4 w-4" />
                            </Button>

                            <Popover open={showAttachmentChoice} onOpenChange={setShowAttachmentChoice}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={isUploading}
                                  className="flex items-center justify-center rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                                  title={t('ticket:action.addAttachment', 'Anhang hinzufügen')}
                                >
                                  {isUploading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <PaperclipIcon className="h-4 w-4" />
                                  )}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-48 p-2" align="end">
                                <div className="space-y-1">
                                  <button
                                    onClick={() => {
                                      setShowAttachmentChoice(false);
                                      const input = document.createElement('input');
                                      input.type = 'file';
                                      input.multiple = true;
                                      input.accept = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv';
                                      input.onchange = (e) => {
                                        const files = (e.target as HTMLInputElement).files;
                                        if (files && files.length > 0) {
                                          handleFileUpload(Array.from(files));
                                        }
                                      };
                                      input.click();
                                    }}
                                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-slate-100"
                                  >
                                    <Upload className="h-3.5 w-3.5 text-slate-500" />
                                    <span>{t('ticket:action.uploadFile', 'Datei hochladen')}</span>
                                  </button>
                                  <button
                                    onClick={async () => {
                                      setShowAttachmentChoice(false);
                                      setGalleryTarget('main');
                                      setIsLoadingGallery(true);
                                      setShowGalleryModal(true);
                                      try {
                                        const response = await ticketsService.getTicketGallery(ticketId);
                                        if (response.success) {
                                          setGalleryData(response.data.gallery);
                                        }
                                      } catch (error) {
                                        console.error('Failed to load gallery:', error);
                                      } finally {
                                        setIsLoadingGallery(false);
                                      }
                                    }}
                                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-slate-100"
                                  >
                                    <ImageIcon className="h-3.5 w-3.5 text-slate-500" />
                                    <span>{t('ticket:action.fromGallery', 'Aus Galerie wählen')}</span>
                                  </button>
                                </div>
                              </PopoverContent>
                            </Popover>

                            <Popover
                              open={replyTranslatePopoverOpen}
                              onOpenChange={setReplyTranslatePopoverOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex items-center justify-center rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                                  disabled={translatingReplyEditor}
                                  title={t('ticket:action.translate', 'Übersetzen')}
                                >
                                  {translatingReplyEditor ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Languages className="h-4 w-4" />
                                  )}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 p-3" align="end">
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-medium text-sm">{t('ticket:ui.translateMsg', 'Nachricht übersetzen')}</h4>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => setReplyTranslatePopoverOpen(false)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>

                                  {recentLanguages.length > 0 && (
                                    <div className="space-y-1.5">
                                      <p className="text-xs text-slate-500 font-medium">{t('ticket:section.recentlyUsed', 'Zuletzt verwendet')}</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {recentLanguages.map((lang) => (
                                          <Button
                                            key={lang.code}
                                            variant="outline"
                                            size="sm"
                                            className="h-8 text-xs"
                                            onClick={() => handleTranslateReplyEditor(lang.code)}
                                            disabled={translatingReplyEditor}
                                          >
                                            <img src={`https://flagcdn.com/w20/${lang.countryCode}.png`} alt={lang.name} className="mr-1.5 h-3 w-4 object-cover rounded-sm" />
                                            {lang.nativeName}
                                          </Button>
                                        ))}
                                      </div>
                                      <Separator className="my-2" />
                                    </div>
                                  )}

                                  <div className="space-y-1.5">
                                    <p className="text-xs text-slate-500 font-medium">{t('ticket:section.allLanguages', 'Alle Sprachen')}</p>
                                    <ScrollArea className="h-48">
                                      <div className="grid grid-cols-2 gap-1.5 pr-3">
                                        {SUPPORTED_LANGUAGES.map((lang) => (
                                          <Button
                                            key={lang.code}
                                            variant="ghost"
                                            size="sm"
                                            className="h-9 justify-start text-xs hover:bg-blue-50"
                                            onClick={() => handleTranslateReplyEditor(lang.code)}
                                            disabled={translatingReplyEditor}
                                          >
                                            <img src={`https://flagcdn.com/w20/${lang.countryCode}.png`} alt={lang.name} className="mr-2 h-3 w-4 object-cover rounded-sm" />
                                            <span className="truncate">{lang.nativeName}</span>
                                          </Button>
                                        ))}
                                      </div>
                                    </ScrollArea>
                                  </div>

                                  <div className="mt-2 text-xs text-slate-500 italic">
                                    {t('common:x4.editorContentTranslated', 'Der Inhalt des Editors wird übersetzt und ersetzt.')}
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>

                          </>
                        )}

                        {/* Next Ticket Button - Quick navigation */}
                        <button
                          onClick={() => {
                            if (adjacentTicketsData?.data?.next) {
                              // Mark as navigating to replace tab instead of adding new (use sessionStorage to survive page reload)
                              sessionStorage.setItem('ticket-tabs-navigating', 'true');
                              sessionStorage.setItem('ticket-tabs-replace-id', activeTab || ticketId);
                              // Store view context in sessionStorage for clean URLs
                              if (fromViewId) {
                                sessionStorage.setItem('ticketViewContext', JSON.stringify({
                                  fromViewId,
                                  fromViewName,
                                  sortField: listSortField,
                                  sortDirection: listSortDirection,
                                  listPosition: listPosition ? listPosition + 1 : undefined,
                                  totalTickets,
                                  viewFilterConfig
                                }));
                              }
                              window.location.href = `/tickets/${adjacentTicketsData.data.next.id}`;
                            }
                          }}
                          disabled={!adjacentTicketsData?.data?.next}
                          data-voice="next-ticket"
                          className={cn(
                            "flex h-7 w-7 items-center justify-center rounded-full border transition mr-2",
                            adjacentTicketsData?.data?.next
                              ? "border-slate-200 bg-white text-slate-600 shadow-sm hover:border-[#0F99FD]/40 hover:bg-[#0F99FD] hover:text-white"
                              : "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
                          )}
                          title={adjacentTicketsData?.data?.next ? `Nächstes Ticket (${adjacentTicketsData.data.next.ticketNumber})` : 'Kein nächstes Ticket'}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>

                        {/* Notiz Badge Button */}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => {
                                  if (!isCustomerMessageEmpty) {
                                    handleCustomerMessageSend(undefined, true); // forceAsNote=true
                                  }
                                }}
                                disabled={createNoteMutation.isPending || isCustomerMessageEmpty}
                                className={cn(
                                  "flex items-center justify-center h-8 w-8 rounded-full border transition-all",
                                  createNoteMutation.isPending
                                    ? "bg-amber-100 border-amber-300 text-amber-600"
                                    : isCustomerMessageEmpty
                                    ? "bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed"
                                    : "bg-amber-50 border-amber-200 text-amber-600 hover:bg-amber-100 hover:border-amber-300"
                                )}
                              >
                                {createNoteMutation.isPending ? (
                                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <FileText className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              {t('common:x4.saveInternalNote', 'Interne Notiz speichern')}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <DropdownMenu>
                          <div className="ticket-send-group group flex items-stretch">
                            <Button
                              size="sm"
                              data-voice="send-reply"
                              className="ticket-send-primary flex items-center gap-1 rounded-l-full rounded-r-none px-3 py-1.5 text-[11px] font-semibold transition"
                              disabled={replyMutation.isPending || isCustomerMessageEmpty}
                              onClick={() => {
                                handleCustomerMessageSend(undefined, false); // forceAsNote=false
                              }}
                            >
                              <span className="text-[11px] whitespace-nowrap">
                                {replyMutation.isPending ? 'SENDEN...' : 'SENDEN'}
                              </span>
                            </Button>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                className="ticket-send-menu rounded-r-full rounded-l-none px-2 py-1.5 transition"
                                disabled={replyMutation.isPending || isCustomerMessageEmpty}
                              >
                                <ChevronDown className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                          </div>
                          <DropdownMenuContent align="end" className="rounded-2xl border border-slate-200/80 bg-white shadow-xl min-w-[200px]">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.preventDefault();
                                // "Stay on ticket" - deselect all navigation options
                                setGoToNextAfterSend(false);
                                setGoToViewAfterSend(false);
                              }}
                              className="cursor-pointer"
                            >
                              <div className="flex items-center gap-2 w-full">
                                <div className={cn(
                                  "h-4 w-4 rounded-full border flex items-center justify-center",
                                  !goToNextAfterSend && !goToViewAfterSend ? "bg-[#0F99FD] border-[#0F99FD]" : "border-slate-300"
                                )}>
                                  {!goToNextAfterSend && !goToViewAfterSend && <div className="h-2 w-2 rounded-full bg-white" />}
                                </div>
                                <Check className="h-3.5 w-3.5" />
                                <span>{t('ticket:ui.stayOnTicket', 'Beim Ticket bleiben')}</span>
                              </div>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.preventDefault();
                                setGoToNextAfterSend(true);
                                setGoToViewAfterSend(false);
                              }}
                              className="cursor-pointer"
                            >
                              <div className="flex items-center gap-2 w-full">
                                <div className={cn(
                                  "h-4 w-4 rounded-full border flex items-center justify-center",
                                  goToNextAfterSend ? "bg-[#0F99FD] border-[#0F99FD]" : "border-slate-300"
                                )}>
                                  {goToNextAfterSend && <div className="h-2 w-2 rounded-full bg-white" />}
                                </div>
                                <ChevronRight className="h-3.5 w-3.5" />
                                <span>{t('ticket:action.toNextTicket', 'Zum nächsten Ticket')}</span>
                              </div>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.preventDefault();
                                setGoToViewAfterSend(true);
                                setGoToNextAfterSend(false);
                              }}
                              className="cursor-pointer"
                            >
                              <div className="flex items-center gap-2 w-full">
                                <div className={cn(
                                  "h-4 w-4 rounded-full border flex items-center justify-center",
                                  goToViewAfterSend ? "bg-[#0F99FD] border-[#0F99FD]" : "border-slate-300"
                                )}>
                                  {goToViewAfterSend && <div className="h-2 w-2 rounded-full bg-white" />}
                                </div>
                                <ChevronLeft className="h-3.5 w-3.5" />
                                <span>{t('ticket:action.toTicketView', 'Zur Ticketansicht')}</span>
                              </div>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    }
                  />

                  {/* Slash Command Snippet Popover - Opens upward */}
                  {slashCommandState.isOpen && filteredSlashSnippets.length > 0 && (
                    <div
                      ref={slashPopoverRef}
                      className="absolute z-50 w-72 rounded-lg border border-slate-200 bg-white shadow-xl"
                      style={{
                        bottom: '100%',
                        left: 0,
                        marginBottom: '8px',
                      }}
                    >
                      <div className="py-1">
                        {filteredSlashSnippets.map((snippet, index) => (
                          <button
                            key={snippet.id}
                            onClick={() => handleSlashSnippetSelect(snippet)}
                            className={cn(
                              "w-full text-left px-3 py-2 flex items-start gap-3 transition-colors",
                              index === slashSelectedIndex
                                ? "bg-[#0F99FD]/10 text-slate-900"
                                : "hover:bg-slate-50 text-slate-700"
                            )}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              <div className={cn(
                                "w-5 h-5 rounded flex items-center justify-center text-[10px] font-medium",
                                index === slashSelectedIndex
                                  ? "bg-[#0F99FD] text-white"
                                  : "bg-slate-100 text-slate-500"
                              )}>
                                {snippet.shortcut.slice(0, 2).toUpperCase()}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm truncate">{snippet.name}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                                  /{snippet.shortcut}
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                      {hasMoreSnippets && (
                        <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 rounded-b-lg">
                          <p className="text-xs text-slate-400 text-center">
                            {t('common:x4.keepTypingToFilter', 'Weiter tippen um zu filtern...')}
                          </p>
                        </div>
                      )}
                      <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50 rounded-b-lg">
                        <div className="flex items-center justify-between text-[10px] text-slate-400">
                          <span>↑↓ Navigation</span>
                          <span>{t('ticket:ui.tabSelect', 'Tab Auswählen')}</span>
                          <span>{t('ticket:ui.escClose2', 'Esc Schließen')}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Empty state when no snippets match */}
                  {slashCommandState.isOpen && slashCommandState.query && filteredSlashSnippets.length === 0 && (
                    <div
                      className="absolute z-50 w-64 rounded-lg border border-slate-200 bg-white shadow-xl p-3"
                      style={{
                        bottom: '100%',
                        left: 0,
                        marginBottom: '8px',
                      }}
                    >
                      <div className="text-center text-xs text-slate-500">
                        <p>Keine Textbausteine für "/{slashCommandState.query}"</p>
                      </div>
                    </div>
                  )}
                  </div>

                  {/* Signature Preview/Edit Panel */}
                  {signatureHtml && signaturePanelOpen && (
                    <div className="mt-1 rounded-md border border-slate-200 bg-slate-50/50">
                      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{t('ticket:ui.signature', 'Signatur')}</span>
                          <button
                            onClick={() => setSignatureEnabled(prev => !prev)}
                            className={cn(
                              "relative inline-flex h-4 w-7 items-center rounded-full transition-colors",
                              signatureEnabled ? "bg-blue-500" : "bg-slate-300"
                            )}
                          >
                            <span className={cn(
                              "inline-block h-3 w-3 rounded-full bg-white transition-transform",
                              signatureEnabled ? "translate-x-3.5" : "translate-x-0.5"
                            )} />
                          </button>
                        </div>
                        <div className="flex items-center gap-1">
                          {signatureEnabled && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!signatureEditMode) {
                                  setSignatureEditContent(signatureHtml);
                                }
                                setSignatureEditMode(prev => !prev);
                              }}
                              className="h-5 px-1.5 text-[10px] text-slate-400 hover:text-slate-600"
                            >
                              {signatureEditMode ? (
                                <><Eye className="h-3 w-3 mr-1" />{t('ticket:section.preview', 'Vorschau')}</>
                              ) : (
                                <><FileSignature className="h-3 w-3 mr-1" />{t('agent:drafts.btnEdit', 'Bearbeiten')}</>
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSignaturePanelOpen(false)}
                            className="h-5 px-1.5 text-[10px] text-slate-400 hover:text-slate-500"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {signatureEnabled && (
                        signatureEditMode ? (
                          <textarea
                            value={signatureEditContent}
                            onChange={(e) => setSignatureEditContent(e.target.value)}
                            className="w-full px-3 py-2 text-xs font-mono bg-white border-0 resize-none focus:outline-none focus:ring-0 min-h-[60px]"
                            placeholder={t('ticket:ph.signatureHtml', 'Signatur HTML...')}
                          />
                        ) : (
                          <div
                            className="px-3 py-2 text-xs prose prose-sm max-w-none [&_*]:text-xs [&_p]:my-0.5 [&_img]:max-h-16"
                            dangerouslySetInnerHTML={{ __html: signatureHtml }}
                          />
                        )
                      )}
                    </div>
                  )}
                </div>

                {/* File Upload Section - Horizontal minimal design */}
                {uploadedFiles.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {uploadedFiles.map((file) => (
                      <div
                        key={file.id}
                        className="group inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 rounded-full pl-2.5 pr-1.5 py-1 text-xs transition-colors"
                      >
                        <span className="text-[10px]">{getFileIcon(file.contentType)}</span>
                        <span className="font-medium text-slate-700 max-w-[120px] truncate">
                          {file.filename}
                        </span>
                        <span className="text-slate-500 text-[10px]">
                          {formatFileSize(file.size)}
                        </span>
                        <button
                          onClick={() => removeAttachment(file.id)}
                          className="ml-0.5 p-0.5 rounded-full hover:bg-red-100 transition-colors"
                        >
                          <X className="h-3 w-3 text-slate-400 hover:text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Order Information Sidebar */}
        <div
          className="ticket-info-sidebar w-[420px] border-l flex flex-col shrink-0 overflow-x-hidden"
        >
            <Tabs value={rightPanelTab} onValueChange={setRightPanelTab} className="flex flex-col h-full">
              <div className="ticket-info-tabbar flex items-center shrink-0 px-3 py-2.5">
                <TabsList className="ticket-info-segment flex-1 h-9 p-1">
                  <span
                    aria-hidden="true"
                    className={cn(
                      "ticket-info-segment-slider",
                      rightPanelTab === "ai-assistant" && "is-right"
                    )}
                  >
                    <span className="ticket-info-segment-slider-face" />
                  </span>
                  <TabsTrigger
                    value="ticket-info"
                    className="ticket-info-segment-trigger flex-1 h-7 text-xs font-semibold"
                  >
                    <span className="ticket-info-segment-face">
                      {t('common:x4.ticketInfo', 'Ticket Info')}
                      {isLoadingShopify && (
                        <RefreshCw className="h-3 w-3 ml-1 animate-spin text-slate-400" />
                      )}
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="ai-assistant"
                    data-voice="ai-assistant-tab"
                    className="ticket-info-segment-trigger flex-1 h-7 text-xs font-semibold"
                  >
                    <span className="ticket-info-segment-face">
                      <StrobiAvatar
                        state="excited"
                        tone="adaptive"
                        size={18}
                        className="ticket-info-cloud-avatar"
                      />
                      {t('common:x1.cloudAgent', 'Cloud-Agent')}
                    </span>
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="ticket-info" className="flex-1 overflow-auto mt-0">
                <ScrollArea className="h-full">
                  <TicketInfoGlass>
                  <div className="ticket-info-liquid-stack px-4 py-2 w-full break-words">
                {/* Ticket Information Section */}
                <section className="ticket-info-section">
                  <div className="ticket-info-section-header">
                    <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('ticket:section.info', 'Ticket Informationen')}</h3>
                  </div>
                  <div className="ticket-info-primary-fields">
                    {/* Customer Email */}
                    {((ticket as any)?.internalTargetOrgId || (ticket as any)?.internal_target_org_id) ? (
                      <div className="ticket-info-primary-field ticket-info-primary-field--wide">
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('ticket:ui.internalConv', 'Interne Konversation')}</label>
                        <div className="flex h-7 w-full items-center rounded-md border border-indigo-200 bg-indigo-50 px-2.5 text-xs font-semibold text-indigo-700">
                          {(ticket as any)?.organization?.name || 'Quelle'} ↔ {((ticket as any)?.internalTargetOrgName || (ticket as any)?.internal_target_org_name) || 'Ziel'}
                        </div>
                      </div>
                    ) : ticket?.requester?.email && (
                      <div className="ticket-info-primary-field ticket-info-primary-field--wide">
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('ticket:td.customerLabel', 'Kunde')}</label>
                        <div className="ticket-info-plain-control ticket-info-plain-control--static">
                          <Badge
                            variant="outline"
                            className="w-full justify-start h-7 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600 cursor-pointer hover:bg-slate-50 transition"
                            onClick={() => {
                              setParentTicketForm({
                                email: ticket?.requester?.email || '',
                                customerName: `${ticket?.requester?.firstName || ''} ${ticket?.requester?.lastName || ''}`.trim(),
                                subject: '',
                                priority: 'normal',
                                organizationId: ticket?.organizationId || ''
                              });
                              setIsCreateParentTicketModalOpen(true);
                            }}
                            title={t('ticket:action.newParentTicket', 'Neues übergeordnetes Ticket erstellen')}
                          >
                            <Mail className="h-3 w-3 mr-2 text-slate-400" />
                            {ticket.requester.email}
                          </Badge>
                        </div>
                      </div>
                    )}

                    {/* Reference ID */}
                    {ticket?.referenceId && (
                      <div className="ticket-info-primary-field ticket-info-primary-field--wide">
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('ticket:ui.referenceId', 'Referenz-ID')}</label>
                        <div className="ticket-info-plain-control ticket-info-plain-control--static">
                          <Badge
                            variant="outline"
                            className="w-full justify-start h-7 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600"
                          >
                            <Hash className="h-3 w-3 mr-1.5 text-slate-400" />
                            {ticket.referenceId}
                          </Badge>
                        </div>
                      </div>
                    )}

                    {/* Status */}
                    <div className="ticket-info-primary-field">
                      <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('common:field.status', 'Status')}</label>
                      <Select
                        value={ticket?.status || 'new'}
                        onValueChange={(value) => handleStatusChange(value)}
                      >
                        <div className="ticket-info-plain-control ticket-info-plain-control--status">
                          <SelectTrigger
                            className={cn(
                              "w-full h-8 rounded-md border px-3 text-xs font-semibold",
                              getStatusColor(ticket?.status || 'new')
                            )}
                          >
                            {getStatusLabel(ticket?.status || 'new')}
                          </SelectTrigger>
                        </div>
                        <SelectContent className="ticket-info-select-content">
                          <SelectItem value="new" className="px-3 py-2 text-xs">{t('domain:status.new', 'Neu')}</SelectItem>
                          <SelectItem value="open" className="px-3 py-2 text-xs">{t('domain:status.open', 'Offen')}</SelectItem>
                          <SelectItem value="pending" className="px-3 py-2 text-xs">{t('domain:status.pending', 'Wartend')}</SelectItem>
                          <SelectItem value="in_progress" className="px-3 py-2 text-xs">{t('domain:status.in_progress', 'In Bearbeitung')}</SelectItem>
                          <SelectItem value="closed" className="px-3 py-2 text-xs">{t('domain:status.closed', 'Geschlossen')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Priority */}
                    <div className="ticket-info-primary-field">
                      <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('ticketList:column.priority', 'Priorität')}</label>
                      <Select
                        value={ticket?.priority || 'normal'}
                        onValueChange={(value) => handlePriorityChange(value)}
                      >
                        <div className="ticket-info-plain-control ticket-info-plain-control--priority">
                          <SelectTrigger
                            className={cn(
                              "w-full h-8 rounded-md border px-3 text-xs font-semibold",
                              getPriorityColor(ticket?.priority || 'normal')
                            )}
                          >
                            {getPriorityLabel(ticket?.priority || 'normal')}
                          </SelectTrigger>
                        </div>
                        <SelectContent className="ticket-info-select-content">
                          <SelectItem value="low" className="px-3 py-2 text-xs">{t('domain:priority.low', 'Niedrig')}</SelectItem>
                          <SelectItem value="normal" className="px-3 py-2 text-xs">{t('domain:priority.normal', 'Normal')}</SelectItem>
                          <SelectItem value="high" className="px-3 py-2 text-xs">{t('domain:priority.high', 'Hoch')}</SelectItem>
                          <SelectItem value="urgent" className="px-3 py-2 text-xs">{t('domain:priority.urgent', 'Dringend')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Organization */}
                    <div className="ticket-info-primary-field">
                      <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('common:field.organization', 'Organisation')}</label>
                      <Select
                        value={ticket?.organizationId || ticket?.organization?.id || ""}
                        onValueChange={(value) => {
                          const currentTicketId = activeTab || ticketId;
                          updateTicketMutation.mutate({
                            ticketId: currentTicketId,
                            organizationId: value
                          });
                        }}
                        disabled={updateTicketMutation.isPending}
                      >
                        <div className="ticket-info-plain-control">
                          <SelectTrigger className="w-full h-8 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600">
                            <SelectValue placeholder={t('ticket:ph.selectOrg', 'Organisation wählen')}>
                              {(() => {
                                const orgId = ticket?.organizationId || ticket?.organization?.id;
                                const org = orgId && orgsResponse?.data
                                  ? orgsResponse.data.find((o: any) => o.id === orgId)
                                  : null;
                                return org?.name || "Keine Organisation";
                              })()}
                            </SelectValue>
                          </SelectTrigger>
                        </div>
                        <SelectContent className="ticket-info-select-content">
                          {orgsResponse?.data?.map((org: any) => (
                            <SelectItem key={org.id} value={org.id} className="px-3 py-2 text-xs">
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Assignee */}
                    <div className="ticket-info-primary-field">
                      <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('ticket:section.assignedTo', 'Zugewiesen an')}</label>
                      <Select
                        value={assignedUser || "unassigned"}
                        onValueChange={(value) => {
                          const newValue = value === "unassigned" ? null : value;
                          setAssignedUser(newValue);
                          const currentTicketId = activeTab || ticketId;
                          updateTicketMutation.mutate({
                            ticketId: currentTicketId,
                            assigneeId: newValue
                          });
                        }}
                        disabled={updateTicketMutation.isPending}
                      >
                        <div className="ticket-info-plain-control">
                          <SelectTrigger className="w-full h-8 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600">
                            <SelectValue placeholder={t('ticket:ph.unassigned', 'Nicht zugewiesen')}>
                              {(() => {
                                const agent = assignedUser && agentsData?.data?.agents ?
                                  agentsData.data.agents.find(a => a.id === assignedUser) : null;
                                return agent?.name || "Nicht zugewiesen";
                              })()}
                            </SelectValue>
                          </SelectTrigger>
                        </div>
                        <SelectContent className="ticket-info-select-content">
                          <SelectItem value="unassigned" className="px-3 py-2 text-xs">{t('ticket:ph.unassigned', 'Nicht zugewiesen')}</SelectItem>
                          {isLoadingAgents ? (
                            <SelectItem value="loading" disabled className="px-3 py-2 text-xs">{t('ticket:ui.loading', 'Lädt...')}</SelectItem>
                          ) : (
                            agentsData?.data?.agents?.map((agent) => (
                              <SelectItem key={agent.id} value={agent.id} className="px-3 py-2 text-xs">
                                <div className="flex w-full items-center justify-between">
                                  <span>{agent.name}</span>
                                  {agent.stats && (
                                    <span className="ml-2 text-[10px] text-slate-400">
                                      {agent.stats.activeTickets} aktiv
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Beobachter (Watchers) */}
                    <div className="ticket-info-primary-field ticket-info-primary-field--wide">
                      <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('common:field.watcher', 'Beobachter')}</label>
                      <Select
                        value=""
                        onValueChange={(value) => { if (value && value !== 'none') addWatcherMutation.mutate(value); }}
                        disabled={addWatcherMutation.isPending}
                      >
                        <div className="ticket-info-plain-control">
                          <SelectTrigger className="w-full h-8 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600">
                            <SelectValue placeholder={t('ticket:ph.addWatcher', 'Beobachter hinzufügen…')} />
                          </SelectTrigger>
                        </div>
                        <SelectContent className="ticket-info-select-content">
                          {(() => {
                            const watchingIds = new Set((watchers as Watcher[]).map((w) => w.id));
                            const available = (agentsData?.data?.agents || []).filter((a) => !watchingIds.has(a.id));
                            if (available.length === 0) {
                              return <SelectItem value="none" disabled className="px-3 py-2 text-xs">{t('ticket:ui.noMoreMembers', 'Keine weiteren Mitglieder')}</SelectItem>;
                            }
                            return available.map((agent) => (
                              <SelectItem key={agent.id} value={agent.id} className="px-3 py-2 text-xs">{agent.name}</SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                      {(watchers as Watcher[]).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(watchers as Watcher[]).map((w) => (
                            <Badge
                              key={w.id}
                              variant="outline"
                              className="ticket-info-plain-chip h-6 rounded-md px-2 text-[10px] font-medium flex items-center gap-1 border-slate-200 bg-slate-50 text-slate-600"
                            >
                              {watcherName(w)}
                              <button
                                type="button"
                                onClick={() => removeWatcherMutation.mutate(w.id)}
                                className="ml-0.5 text-slate-400 hover:text-red-500"
                                title={t('ticket:action.remove', 'Entfernen')}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Tags */}
                    <div className="ticket-info-primary-field ticket-info-primary-field--wide">
                      <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('ticket:ui.tags', 'Tags')}</label>
                      <div className="flex flex-wrap gap-1.5">
                        {ticket?.tags && ticket.tags.length > 0 ? (
                          ticket.tags.map((tagName) => {
                            const tag = tagsData?.data?.tags.find(t => t.name === tagName);
                            return (
                              <Badge
                                key={tagName}
                                variant="outline"
                                className="ticket-info-plain-chip h-6 rounded-md px-2 text-[10px] font-medium flex items-center gap-1"
                                style={{
                                  borderColor: tag?.color || '#6B7280',
                                  backgroundColor: tag?.color ? `${tag.color}10` : '#F3F4F6',
                                  color: tag?.color || '#6B7280'
                                }}
                              >
                                <Tag className="h-2.5 w-2.5" style={{ color: tag?.color || '#6B7280' }} />
                                {tagName}
                              </Badge>
                            );
                          })
                        ) : (
                          <span className="text-[10px] text-slate-400">{t('ticket:td.noTags', 'Keine Tags')}</span>
                        )}
                        <Popover open={showTagPopover} onOpenChange={setShowTagPopover}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 rounded-md border border-dashed border-slate-300 p-0 text-slate-400 hover:border-[#0F99FD]/40 hover:text-[#0F99FD]"
                              title={t('ticket:action.editTags', 'Tags bearbeiten')}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-2" align="start">
                            <div className="max-h-64 overflow-y-auto space-y-1">
                              {tagsData?.data?.tags && tagsData.data.tags.length > 0 ? (
                                tagsData.data.tags.map((tag) => (
                                  <button
                                    key={tag.id}
                                    onClick={() => toggleTag(tag.name)}
                                    className={cn(
                                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                                      selectedTags.includes(tag.name)
                                        ? "bg-slate-100 font-medium"
                                        : "hover:bg-slate-50"
                                    )}
                                  >
                                    <div
                                      className="h-3 w-3 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: tag.color }}
                                    />
                                    <span className="flex-1 text-left">{tag.name}</span>
                                    {selectedTags.includes(tag.name) && (
                                      <Check className="h-3 w-3 text-slate-600" />
                                    )}
                                  </button>
                                ))
                              ) : (
                                <p className="text-xs text-slate-500 text-center py-2">
                                  {t('common:x4.noTagsAvailable', 'Keine Tags verfügbar')}
                                </p>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                  </div>
                </section>

                <hr className="ticket-info-section-divider" />

                {/* SLA Widget */}
                {ticket?.id && (
                  <>
                    <Accordion type="multiple" defaultValue={[]} className="ticket-info-accordion ticket-info-post-accordion">
                      <AccordionItem value="sla" className="ticket-info-accordion-item ticket-info-clean-section ticket-info-clean-section--sla border-0">
                        <AccordionTrigger className="ticket-info-clean-trigger text-xs font-semibold text-slate-700 hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span className="ticket-info-section-icon ticket-info-section-icon--green" aria-hidden="true" />
                            SLA
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="ticket-sla-accordion-content">
                          <SLAWidget ticketId={ticket.id} flat />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                    <hr className="ticket-info-section-divider" />
                  </>
                )}

                {/* Merged Tickets Section */}
                {mergedTicketsData?.data?.count > 0 && (
                  <>
                  <Accordion type="multiple" defaultValue={[]} className="ticket-info-accordion ticket-info-post-accordion">
                    <AccordionItem value="merged-tickets" className="ticket-info-accordion-item ticket-info-clean-section ticket-info-clean-section--merged border-0">
                      <AccordionTrigger className="ticket-info-clean-trigger text-xs font-semibold text-slate-700 hover:no-underline">
                        <div className="flex items-center gap-2">
                          <span className="ticket-info-section-icon ticket-info-section-icon--orange" aria-hidden="true" />
                          {t('common:x4.mergedTicketsCount', 'Zusammengeführte Tickets (')}{mergedTicketsData.data.count})
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="ticket-info-merged-content">
                    {isLoadingMergedTickets ? (
                      <div className="space-y-2">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {mergedTicketsData.data.mergedTickets.map((mergedTicket: any) => (
                          <div
                            key={mergedTicket.id}
                            className="ticket-info-merged-row cursor-pointer"
                            onClick={() => window.open(`/tickets/${mergedTicket.id}`, '_blank')}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-orange-700">{mergedTicket.ticket_number}</span>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (confirm('Möchten Sie dieses Ticket zum neuen Parent befördern?\n\nDas aktuelle Parent-Ticket wird zum Child und alle anderen Child-Tickets werden diesem Ticket zugeordnet.')) {
                                      promoteToParentMutation.mutate(mergedTicket.id);
                                    }
                                  }}
                                  className="p-1 text-slate-400 hover:text-green-600 transition"
                                  title={t('ticket:action.promoteParent', 'Zum Parent befördern')}
                                >
                                  <ArrowUpCircle className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (confirm(t('ticket:msg.unmergeConfirm', 'Möchten Sie dieses Ticket wirklich trennen?'))) {
                                      unmergeMutation.mutate(mergedTicket.id);
                                    }
                                  }}
                                  className="p-1 text-slate-400 hover:text-red-600 transition"
                                  title={t('ticket:action.unmerge', 'Ticket trennen')}
                                >
                                  <Link2Off className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                            <p className="text-[11px] text-slate-600 line-clamp-1">{mergedTicket.subject}</p>
                          </div>
                        ))}
                      </div>
                    )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                  <hr className="ticket-info-section-divider" />
                  </>
                )}

                {/* Order Details */}
                {orderDetails && (
                  <>
                  <section className="ticket-info-section ticket-info-clean-section ticket-info-clean-section--order-overview">
                    <div className="ticket-info-section-header ticket-info-clean-heading flex items-center gap-2">
                      <span className="ticket-info-section-icon ticket-info-section-icon--violet"><Package className="h-3.5 w-3.5" /></span>
                      <div>
                        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('ticket:ui.orderDetails', 'Bestelldetails')}</h3>
                      </div>
                    </div>
                    <div className="space-y-3 ticket-info-order-overview-grid">
                      {/* Primary Info Row */}
                      <div className="grid grid-cols-2 gap-3 ticket-info-order-meta">
                        <div>
                          <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-0.5">{t('ticket:ui.shop', 'Shop')}</label>
                          <p className="text-xs font-medium text-slate-800">{shopifyData?.data?.meta?.shop || orderDetails.shopType || "N/A"}</p>
                        </div>
                        <div>
                          <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-0.5">{t('ticket:ui.date', 'Datum')}</label>
                          <p className="text-xs font-medium text-slate-800">{orderDetails.date}</p>
                        </div>
                      </div>

                      {/* Order Number */}
                      <div className="ticket-info-order-number-row">
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-0.5">{t('ticket:ui.orderNumber', 'Bestellnummer')}</label>
                        <div className="flex items-center gap-2">
                          <code className="text-xs font-mono font-semibold text-[#0F99FD] bg-blue-50 px-2 py-0.5 rounded">{orderDetails.orderNumber}</code>
                          <button
                            onClick={() => navigator.clipboard.writeText(orderDetails.orderNumber || '')}
                            className="p-1 hover:bg-slate-100 rounded transition"
                            title={t('ticket:action.copy', 'Kopieren')}
                          >
                            <Copy className="h-3 w-3 text-slate-400" />
                          </button>
                          {/* Shopify Admin Badge - Only for Shopify orders */}
                          {platformType === 'shopify' && orderDetails.adminUrl && (
                            <a
                              href={orderDetails.adminUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 hover:bg-green-100 text-green-700 rounded text-[10px] font-medium transition"
                              title={t('ticket:action.openInShopify', 'In Shopify öffnen')}
                            >
                              <ExternalLink className="h-3 w-3" />
                              Shopify
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Customer Name */}
                      <div className="ticket-info-order-customer">
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-0.5">{t('ticket:section.customerName', 'Kundenname')}</label>
                        <div className="flex items-center gap-2">
                          {(() => {
                            // Support both Shopify format (name) and JTL format (firstName + lastName)
                            const customerName = orderDetails.customer?.name ||
                              `${orderDetails.customer?.firstName || ''} ${orderDetails.customer?.lastName || ''}`.trim() ||
                              "N/A";
                            return (
                              <>
                                <div className="h-6 w-6 rounded-full bg-[#0F99FD]/10 flex items-center justify-center text-[#0F99FD] font-semibold text-[10px]">
                                  {customerName.charAt(0) || '?'}
                                </div>
                                <p className="text-xs font-medium text-slate-800">{customerName}</p>
                                {customerName !== "N/A" && (
                                  <button
                                    onClick={() => navigator.clipboard.writeText(customerName)}
                                    className="p-1 hover:bg-slate-100 rounded transition"
                                    title={t('ticket:action.copyCustomerName', 'Kundenname kopieren')}
                                  >
                                    <Copy className="h-3 w-3 text-slate-400" />
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Customer ID */}
                      <div className="ticket-info-order-stat">
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-0.5">{t('ticket:ui.customerNo', 'Kundennummer')}</label>
                        <div className="flex items-center gap-2">
                          {(() => {
                            // Support both Shopify format (id) and JTL format (customerNumber)
                            const customerId = orderDetails.customer?.id ||
                              orderDetails.customer?.customerNumber ||
                              "N/A";
                            return (
                              <>
                                <code className="text-xs font-mono text-slate-700">{customerId}</code>
                                {customerId !== "N/A" && (
                                  <button
                                    onClick={() => navigator.clipboard.writeText(customerId)}
                                    className="p-1 hover:bg-slate-100 rounded transition"
                                    title={t('ticket:action.copyCustomerNumber', 'Kundennummer kopieren')}
                                  >
                                    <Copy className="h-3 w-3 text-slate-400" />
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Contact */}
                      <div className="ticket-info-order-stat">
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-0.5">{t('ticket:ui.phone', 'Telefon')}</label>
                        <p className="text-xs text-slate-800">
                          {orderDetails.customer?.phone ||
                            orderDetails.shippingAddress?.phone ||
                            orderDetails.shippingAddress?.Phone ||
                            "N/A"}
                        </p>
                      </div>

                      {/* Addresses */}
                      <div className="ticket-info-order-address">
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('ticket:ui.shippingAddress', 'Lieferadresse')}</label>
                        <p className="text-[11px] text-slate-700 leading-relaxed">
                          {(() => {
                            // Support both Shopify format (fullAddress) and JTL format (separate fields)
                            if (orderDetails.shippingAddress?.fullAddress) {
                              return orderDetails.shippingAddress.fullAddress;
                            }
                            // Build address from JTL fields
                            const addr = orderDetails.shippingAddress;
                            if (!addr) return "N/A";

                            const parts = [
                              addr.address1 || addr.Street,
                              addr.address2,
                              `${addr.zip || addr.PostalCode} ${addr.city || addr.City}`,
                              addr.country || addr.Country
                            ].filter(Boolean);

                            return parts.length > 0 ? parts.join(', ') : "N/A";
                          })()}
                        </p>
                        {orderDetails.shippingAddress?.company && (
                          <Badge className="mt-1 text-[9px] bg-blue-50 text-blue-700 border-blue-200">{orderDetails.shippingAddress.company}</Badge>
                        )}
                      </div>

                      {/* Status Row - Only for Shopify, not JTL */}
                      {(() => {
                        const shopType = (shopifyData?.data?.meta?.shop || orderDetails.shopType || '').toLowerCase();
                        const isJTL = shopType.includes('jtl');

                        if (isJTL) return null; // Hide for JTL

                        return (
                          <div className="grid grid-cols-2 gap-3 ticket-info-order-status-grid">
                            <div>
                              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('ticket:ui.payment', 'Zahlung')}</label>
                              <span className={cn("inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border font-medium",
                                orderDetails.paymentStatus === 'Bezahlt' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                                  orderDetails.paymentStatus === 'Ausstehend' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                                    'bg-slate-100 text-slate-700 border-slate-200'
                              )}>
                                {orderDetails.paymentStatus}
                              </span>
                            </div>
                            <div>
                              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{t('agent:dept.shipping', 'Versand')}</label>
                              <span className={cn("inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border font-medium",
                                orderDetails.fulfillmentStatus === 'Versandt' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                                  orderDetails.fulfillmentStatus === 'Nicht versandt' ? 'bg-red-100 text-red-700 border-red-200' :
                                    'bg-slate-100 text-slate-700 border-slate-200'
                              )}>
                                {orderDetails.fulfillmentStatus}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </section>
                  <hr className="ticket-info-section-divider" />
                  </>
                )}

                {/* Orders List */}
                <Accordion
                  type="multiple"
                  defaultValue={[]}
                  className="ticket-info-accordion ticket-info-post-accordion"
                >
                  <AccordionItem value="order-items" className="ticket-info-accordion-item ticket-info-clean-section ticket-info-orders-section border-0">
                    <AccordionTrigger className="ticket-info-clean-trigger text-xs font-semibold text-slate-700 hover:no-underline">
                      <div className="flex items-center gap-2">
                        <span className="ticket-info-section-icon ticket-info-section-icon--violet" aria-hidden="true" />
                        {t('common:x1.ordersCount', 'Bestellungen (')}{getAllOrders()?.length || 0})
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="ticket-info-orders-content">
                      {getAllOrders() ? (
                        <div className="space-y-2">
                          {getAllOrders()!.map((order, orderIndex) => (
                            <div key={orderIndex} className="ticket-info-order-card">
                              <div className="ticket-info-order-card-header">
                                <div className="ticket-info-order-summary-main">
                                  <div className="ticket-info-order-summary-identity">
                                    <code className="text-[11px] font-mono font-semibold text-slate-700">{order.orderNumber}</code>
                                    <span className="text-[10px] text-slate-500">{order.date || (order.createdAt ? formatDate(order.createdAt) : '')}</span>
                                    {/* Shopify Admin Badge - Only for Shopify orders */}
                                    {platformType === 'shopify' && order.adminUrl && (
                                      <a
                                        href={order.adminUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 hover:bg-green-100 text-green-700 rounded text-[9px] font-medium transition"
                                        title={t('ticket:action.openInShopify', 'In Shopify öffnen')}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <ExternalLink className="h-2.5 w-2.5" />
                                        Shopify
                                      </a>
                                    )}
                                    {order.fulfillmentStatus && (
                                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${order.fulfillmentStatus === 'fulfilled'
                                        ? 'bg-green-50 text-green-700 border border-green-200'
                                        : order.fulfillmentStatus === 'cancelled'
                                          ? 'bg-red-50 text-red-700 border border-red-200'
                                          : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                        }`}>
                                        {order.fulfillmentStatus === 'fulfilled' ? '✓ Versandt' : order.fulfillmentStatus === 'cancelled' ? 'Storniert' : 'Ausstehend'}
                                      </span>
                                    )}
                                  </div>
                                  <span className="ticket-info-order-total">
                                    {(() => {
                                      // Handle both formatted total (Shopify) and separate fields (JTL)
                                      if (order.total) {
                                        const match = order.total.match(/^([\d.]+)\s*(.*)$/);
                                        if (match) {
                                          const price = parseFloat(match[1]).toFixed(2);
                                          const currency = match[2] || '';
                                          return `${price} ${currency}`.trim();
                                        }
                                        return order.total;
                                      }
                                      // JTL format: Calculate total from lineItems (main products only)
                                      const currency = (order as any).currency || 'EUR';

                                      // If lineItems exist, calculate from main products
                                      if ((order as any).lineItems && Array.isArray((order as any).lineItems)) {
                                        const mainProducts = (order as any).lineItems.filter((item: any) => item.isMainProduct);

                                        // Group by kAuftragPosition to avoid counting duplicates (split packages)
                                        const uniqueProducts = new Map();
                                        mainProducts.forEach((item: any) => {
                                          const key = item.kAuftragPosition || item.sku;
                                          if (!uniqueProducts.has(key)) {
                                            uniqueProducts.set(key, item);
                                          }
                                        });

                                        let total = 0;
                                        uniqueProducts.forEach((item: any) => {
                                          // Backend maps NetPrice to 'price' field (see jtl.js:188)
                                          const netPrice = parseFloat(item.price || 0);
                                          const quantity = item.orderedQuantity || item.quantity || 1;
                                          const taxRate = item.taxRate || 0;
                                          const discount = item.discount || 0;

                                          // Calculate: (NetPrice * Quantity) + Tax - Discount
                                          const itemTotal = netPrice * quantity;
                                          const taxAmount = itemTotal * (taxRate / 100);
                                          total += itemTotal + taxAmount - discount;
                                        });

                                        return `${total.toFixed(2)} ${currency}`;
                                      }

                                      // Fallback to totalPrice field
                                      const price = parseFloat((order as any).totalPrice || (order as any).currentTotalPrice || '0');
                                      return isNaN(price) ? '0.00 EUR' : `${price.toFixed(2)} ${currency}`;
                                    })()}
                                  </span>
                                </div>
                                <div className="ticket-info-order-meta-row">
                                  {(order as any).externalOrderNumber && (
                                    <div>{t('ticket:ui.externalNo', 'Externe Nr.:')} <span className="font-medium">{(order as any).externalOrderNumber}</span></div>
                                  )}
                                  {(order as any).kAuftrag && (
                                    <div>{t('ticket:ui.orderId', 'Auftrag-ID:')} <span className="font-medium">{(order as any).kAuftrag}</span></div>
                                  )}
                                  {(order as any).paymentMethod && (
                                    <div>{t('ticket:ui.payment2', 'Zahlung:')} <span className="font-medium">{(order as any).paymentMethod}</span></div>
                                  )}
                                  {(order as any).transactionId && (
                                    <PaymentOrderStatus transactionId={(order as any).transactionId} paymentMethod={(order as any).paymentMethod} total={order.total} currency={order.currency} orderNumber={order.orderNumber} organizationId={organizationId} />
                                  )}
                                  {/* Shopify siparişi → Shopify Admin API üzerinden ödeme durumu + iade */}
                                  {platformType === 'shopify' && (order as any).id && (
                                    <PaymentOrderStatus provider="shopify" transactionId={String((order as any).id)} total={order.total} currency={order.currency} orderNumber={order.orderNumber} organizationId={organizationId} />
                                  )}
                                  {/* Lexoffice fatura (müşteri + sipariş no ile bul → PDF) */}
                                  <div className="mt-1"><LexInvoiceButton email={customerEmail} orderNumber={order.orderNumber} amount={order.total} /></div>
                                  {(order as any).orderType && (
                                    <div>{t('ticket:ui.type', 'Typ:')} <span className="font-medium">{(order as any).orderType}</span></div>
                                  )}
                                  {(order as any).shippingCountry && (
                                    <div>{t('ticket:ui.country', 'Land:')} <span className="font-medium">{(order as any).shippingCountry}</span></div>
                                  )}
                                  {(order as any).expectedDeliveryDate && (
                                    <div>{t('ticket:ui.expDelivery', 'Erw. Lieferung:')} <span className="font-medium">{(order as any).expectedDeliveryDate}</span></div>
                                  )}
                                  {(order as any).deliveryDate && (
                                    <div>{t('ticket:ui.delivered', 'Geliefert:')} <span className="font-medium text-green-600">{(order as any).deliveryDate}</span></div>
                                  )}
                                  {(order as any).totalDiscounts && parseFloat((order as any).totalDiscounts) > 0 && (
                                    <div>{t('ticket:ui.totalDiscount', 'Gesamt-Rabatt:')} <span className="font-medium text-orange-600">-{parseFloat((order as any).totalDiscounts).toFixed(2)} {order.currency}</span></div>
                                  )}
                                </div>
                              </div>
                              {orderIndex === 0 && orderDetails && (
                                <div className="ticket-order-customer-context">
                                  {(() => {
                                    const customerName = orderDetails.customer?.name
                                      || `${orderDetails.customer?.firstName || ''} ${orderDetails.customer?.lastName || ''}`.trim()
                                      || 'N/A';
                                    const customerId = orderDetails.customer?.id || orderDetails.customer?.customerNumber || 'N/A';
                                    const phone = orderDetails.customer?.phone
                                      || orderDetails.shippingAddress?.phone
                                      || orderDetails.shippingAddress?.Phone
                                      || 'N/A';
                                    const address = orderDetails.shippingAddress?.fullAddress || (() => {
                                      const addr = orderDetails.shippingAddress;
                                      if (!addr) return 'N/A';
                                      return [
                                        addr.address1 || addr.Street,
                                        addr.address2,
                                        `${addr.zip || addr.PostalCode || ''} ${addr.city || addr.City || ''}`.trim(),
                                        addr.country || addr.Country,
                                      ].filter(Boolean).join(', ') || 'N/A';
                                    })();

                                    return (
                                      <>
                                        <div className="ticket-order-customer-primary">
                                          <span className="ticket-order-customer-avatar">{customerName.charAt(0)}</span>
                                          <span><small>{t('ticket:section.customerName', 'Kundenname')}</small><strong>{customerName}</strong></span>
                                        </div>
                                        <div className="ticket-order-customer-stat">
                                          <small>{t('ticket:ui.customerNo', 'Kundennummer')}</small>
                                          <strong>{customerId}</strong>
                                        </div>
                                        <div className="ticket-order-customer-stat">
                                          <small>{t('ticket:ui.phone', 'Telefon')}</small>
                                          <strong>{phone}</strong>
                                        </div>
                                        <div className="ticket-order-customer-address">
                                          <small>{t('ticket:ui.shippingAddress', 'Lieferadresse')}</small>
                                          <span>{address}</span>
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                              <TrackingTimeline trackingNumber={(order as any).trackingNumber} carrier={(order as any).carrier} email={customerEmail} orderNumber={order.orderNumber} />
                              <div className="ticket-info-order-card-content">
                                {/* JTL format: Main products with accessories accordion */}
                                {(order as any).lineItems && (order as any).lineItems.some((item: any) => item.isMainProduct) ? (
                                  <div className="space-y-4 pt-1">
                                    {/* Main Products Section */}
                                    <div className="space-y-3">
                                      {(() => {
                                        const allMainProducts = (order as any).lineItems.filter((item: any) => item.isMainProduct);
                                        const accessories = (order as any).lineItems.filter((item: any) => !item.isMainProduct);

                                        // Group main products by kAuftragPosition
                                        const groupedMainProducts = allMainProducts.reduce((acc: any, item: any) => {
                                          const key = item.kAuftragPosition || item.sku;
                                          if (!acc[key]) {
                                            acc[key] = {
                                              ...item,
                                              packages: []
                                            };
                                          }
                                          acc[key].packages.push({
                                            shippedQuantity: item.shippedQuantity,
                                            tracking: item.tracking
                                          });
                                          return acc;
                                        }, {});

                                        const mainProducts = Object.values(groupedMainProducts);

                                        return mainProducts.map((mainProduct: any, idx: number) => (
                                          <div key={idx} className="group relative bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                                            {/* Product Header */}
                                            <div className="p-3 flex items-start gap-3">
                                              {/* Icon Placeholder */}
                                              <div className="h-10 w-10 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 text-slate-400">
                                                <Package className="h-5 w-5" />
                                              </div>

                                              <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start gap-2">
                                                  <h4 className="text-xs font-semibold text-slate-800 leading-snug line-clamp-2">
                                                    {mainProduct.title}
                                                  </h4>
                                                  <span className="font-bold text-xs text-slate-900 whitespace-nowrap">
                                                    {(() => {
                                                      const netPrice = parseFloat(mainProduct.price || 0);
                                                      const quantity = mainProduct.orderedQuantity || 1;
                                                      const taxRate = mainProduct.taxRate || 0;
                                                      const discount = mainProduct.discount || 0;
                                                      const itemTotal = netPrice * quantity;
                                                      const taxAmount = itemTotal * (taxRate / 100);
                                                      const totalWithTax = itemTotal + taxAmount - discount;
                                                      return `${totalWithTax.toFixed(2)} ${(order as any).currency || 'EUR'}`;
                                                    })()}
                                                  </span>
                                                </div>

                                                <div className="flex items-center flex-wrap gap-2 mt-1.5">
                                                  <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 border-0">
                                                    {mainProduct.orderedQuantity} Stk.
                                                  </Badge>
                                                  {mainProduct.sku && (
                                                    <span className="text-[10px] text-slate-400 font-mono">
                                                      {mainProduct.sku}
                                                    </span>
                                                  )}
                                                  {(mainProduct as any).warehouse && (
                                                    <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-semibold bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border-0" title={t('ticket:section.warehouseProfile', 'Lager / Versandprofil (intern)')}>
                                                      {(mainProduct as any).warehouse}
                                                    </Badge>
                                                  )}
                                                </div>


                                              </div>
                                            </div>


                                            {/* Accessories List */}
                                            {accessories.length > 0 && (
                                              <div className="bg-slate-50/50 border-t border-slate-100 px-3 py-2.5">
                                                <div className="flex items-center gap-1.5 mb-2">
                                                  <div className="h-1 w-1 rounded-full bg-slate-300"></div>
                                                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{t('ticket:section.accessories', 'Zubehör & Extras')}</span>
                                                </div>
                                                <div className="space-y-2">
                                                  {accessories.map((acc: any, accIdx: number) => (
                                                    <div key={accIdx} className="flex items-start justify-between gap-3 pl-2 border-l-2 border-slate-200">
                                                      <div className="flex-1 min-w-0">
                                                        <p className="text-[11px] text-slate-700 font-medium leading-tight">{acc.title}</p>
                                                        {acc.sku && <p className="text-[9px] text-slate-400 mt-0.5 font-mono">{acc.sku}</p>}
                                                      </div>
                                                      <div className="flex flex-col items-end gap-1">
                                                        <span className="text-[10px] font-medium text-slate-600 bg-white px-1.5 py-0.5 rounded border border-slate-200">
                                                          ×{acc.orderedQuantity}
                                                        </span>
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                          {acc.tracking?.ShippedDate && (
                                                            <span className="text-[9px] text-green-600 font-medium whitespace-nowrap">
                                                              {t('ticket:ui.shipped', 'Versandt:')} {formatDate(acc.tracking.ShippedDate)}
                                                            </span>
                                                          )}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        ));
                                      })()}
                                    </div>

                                    {/* Packages Section */}
                                    {(order as any).packages && (order as any).packages.length > 0 && (
                                      <div className="bg-slate-50 rounded-xl border border-slate-200 p-3">
                                        <div className="flex items-center gap-2 mb-3">
                                          <div className="p-1 rounded bg-white border border-slate-200 shadow-sm">
                                            <Package className="h-3 w-3 text-slate-600" />
                                          </div>
                                          <h4 className="text-xs font-semibold text-slate-700">{t('common:x4.shippingPackagesCount', 'Versandpakete (')}{(order as any).packages.length})</h4>
                                        </div>

                                        <div className="grid gap-2">
                                          {(order as any).packages.map((pkg: any, pkgIdx: number) => (
                                            <div key={pkgIdx} className="bg-white rounded-lg border border-slate-200 p-2.5 flex items-center justify-between gap-3 shadow-sm">
                                              <div className="min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <span className="text-[11px] font-bold text-slate-800">{t('views:f.icoPackage', 'Paket')} {pkgIdx + 1}</span>
                                                  {pkg.weight && (
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                                                      {pkg.weight}kg
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="flex flex-col gap-0.5">
                                                  <span className="text-[10px] text-slate-500 font-medium">{pkg.carrierName}</span>
                                                  <span className="text-[10px] text-slate-400 font-mono">{pkg.trackingNumber}</span>
                                                </div>
                                                {pkg.shippedDate && (
                                                  <div className="mt-1 text-[9px] text-green-600 font-medium flex items-center gap-1">
                                                    <div className="w-1 h-1 rounded-full bg-green-500"></div>
                                                    {t('ticket:ui.shipped', 'Versandt:')} {formatDate(pkg.shippedDate)}
                                                  </div>
                                                )}
                                              </div>

                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  /* Fallback to regular items display (Shopify format) */
                                  (order.items || order.lineItems || []).map((item, itemIndex) => (
                                    <div key={itemIndex} className="ticket-order-product-row">
                                      <div className="flex items-start justify-between gap-2 text-[11px]">
                                        <span className="ticket-order-product-name">
                                          {item.name || item.title}
                                          {(item as any).orderedQuantity && (
                                            <span className="text-slate-400 ml-1">
                                              ×{(item as any).orderedQuantity}
                                              {(item as any).shippedQuantity !== undefined && (item as any).shippedQuantity !== (item as any).orderedQuantity && (
                                                <span className="text-orange-600"> ({(item as any).shippedQuantity} versandt)</span>
                                              )}
                                            </span>
                                          )}
                                          {!((item as any).orderedQuantity) && item.quantity && (
                                            <span className="text-slate-400 ml-1">×{item.quantity}</span>
                                          )}
                                          {(item as any).unit && (item as any).unit !== 'Stk' && (item as any).unit !== '' && (
                                            <span className="text-slate-400 ml-1">{(item as any).unit}</span>
                                          )}
                                        </span>
                                        {(item as any).tracking?.ShippedDate && (
                                          <span className="text-[9px] text-green-600 font-medium whitespace-nowrap">
                                            {t('ticket:ui.shipped', 'Versandt:')} {formatDate((item as any).tracking.ShippedDate)}
                                          </span>
                                        )}
                                      </div>
                                      {((item as any).sku || (item as any).note || (item as any).warehouse) && (
                                        <div className="ticket-order-product-meta">
                                          {(item as any).sku && (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-[9px] text-amber-700 font-medium border border-amber-200">
                                              <span className="text-amber-600">{t('ticket:ui.sku', 'SKU:')}</span>
                                              <span>{(item as any).sku}</span>
                                            </span>
                                          )}
                                          {(item as any).warehouse && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-50 text-[9px] text-indigo-600 font-semibold border border-indigo-200" title={t('ticket:section.warehouseProfile', 'Lager / Versandprofil (intern)')}>
                                              {(item as any).warehouse}
                                            </span>
                                          )}
                                          {(item as any).note && (
                                            <span className="text-[9px] text-slate-500 italic">{t('common:x4.noteColon', 'Hinweis:')} {(item as any).note}</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                              {(order as any).returns && Array.isArray((order as any).returns) && (order as any).returns.length > 0 && (
                                <div className="bg-orange-50 px-3 py-2 border-t border-orange-200">
                                  <div className="text-[10px] font-medium text-orange-700 mb-1.5">{t('common:x4.returnsExchangeCount', 'Retouren/Umtausch (')}{(order as any).returns.length})</div>
                                  <div className="space-y-1">
                                    {(order as any).returns.map((r: any, ri: number) => {
                                      const rs = String(r.ReturnStatusName || '').toLowerCase();
                                      const cls = rs.includes('abgeschlossen') ? 'bg-emerald-100 text-emerald-700'
                                        : rs.includes('eingetroffen') ? 'bg-blue-100 text-blue-700'
                                        : 'bg-amber-100 text-amber-700';
                                      return (
                                        <div key={ri} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px]">
                                          <span className="font-semibold text-orange-800">{r.ReturnNumber || 'Retoure'}</span>
                                          {r.ReturnStatusName && <span className={`rounded-full px-1.5 py-0.5 font-medium ${cls}`}>{r.ReturnStatusName}</span>}
                                          {r.ReturnDate && <span className="text-orange-500">{formatDate(r.ReturnDate)}</span>}
                                          {r.ReturnTrackingCode && <span className="font-mono text-orange-500">📦 {r.ReturnTrackingCode}</span>}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] text-slate-500 text-center py-3">{t('ticket:ui.noOrders', 'Keine Bestellungen')}</p>
                      )}
                    </AccordionContent>
                  </AccordionItem>

                  {/* Timeline */}
                  <AccordionItem value="timeline" className="ticket-info-accordion-item ticket-info-clean-section ticket-info-timeline-section border-0">
                    <AccordionTrigger className="ticket-info-clean-trigger text-xs font-semibold text-slate-700 hover:no-underline">
                      <div className="flex items-center gap-2">
                        <span className="ticket-info-section-icon" aria-hidden="true" />
                        {t('ticket:td.timeline', 'Zeitlinie')}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="ticket-info-timeline-content">
                      {isLoading ? (
                        <div className="space-y-2">
                          <Skeleton className="h-8 w-full" />
                          <Skeleton className="h-8 w-full" />
                        </div>
                      ) : (
                        <div className="space-y-0">
                          {ticketTimeline.slice(0, timelineVisibleCount).map((event, index) => (
                            <div key={event.id} className="flex gap-3">
                              <div className="flex flex-col items-center">
                                <div className={cn("w-2 h-2 rounded-full mt-1.5", index === 0 ? "bg-[#0F99FD]" : "bg-slate-300")} />
                                {index < Math.min(timelineVisibleCount, ticketTimeline.length) - 1 && (
                                  <div className="w-px flex-1 bg-slate-200 my-1" />
                                )}
                              </div>
                              <div className="flex-1 pb-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-medium text-slate-800">{event.action}</span>
                                  <span className="text-[10px] text-slate-400">
                                    {formatDayTime(event.timestamp)}
                                  </span>
                                </div>
                                <span className="text-[10px] text-slate-500">{event.user}</span>
                              </div>
                            </div>
                          ))}

                          {timelineVisibleCount < ticketTimeline.length && (
                            <button
                              onClick={() => setTimelineVisibleCount(prev => Math.min(prev + 5, ticketTimeline.length))}
                              className="w-full text-center text-[10px] text-[#0F99FD] hover:text-[#0c84e0] font-medium py-2"
                            >
                              {t('common:x1.showMore', 'Mehr anzeigen (')}{ticketTimeline.length - timelineVisibleCount})
                            </button>
                          )}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
                  </TicketInfoGlass>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="ai-assistant" className="flex-1 overflow-hidden mt-0">
                {/* Cloud-Agent Aktivität (Agent-Fall) dieses Tickets. Kein Fall → leer. */}
                <CaseDetail ticketId={ticket?.id || ticketId} />
              </TabsContent>
            </Tabs>
        </div>

        {/* Conversation Resizer */}
        {!isNebenkonversationCollapsed && (
          <div
            className="ticket-internal-resizer w-px cursor-col-resize transition-colors duration-200"
            onMouseDown={handleMouseDown}
          />
        )}

        {/* Nebenkonversation - Right Side */}
        <div
          className={`ticket-internal-panel flex flex-col external-conversation transition-all duration-300 max-w-[520px] ${isNebenkonversationCollapsed ? 'overflow-hidden' : ''}`}
          style={{
            width: isNebenkonversationCollapsed
              ? '0'
              : (() => {
                const conversationSpace = 100 - orderPanelWidth;
                return `${(rightPanelWidth / (leftPanelWidth + rightPanelWidth)) * conversationSpace}%`;
              })(),
            height: "100%",
            display: isNebenkonversationCollapsed ? 'none' : 'flex'
          }}
        >
          <div className="ticket-internal-header flex flex-col border-b">
            <div className="flex h-11 items-center justify-between px-3.5">
              <h2 className="text-xs font-semibold tracking-wide text-slate-700">{t('ticket:ui.internalComm', 'Interne Kommunikation')}</h2>
              <CreateExternalTicketDialog
                parentTicketId={ticket?.id || ''}
                parentTicketSubject={subject || ticket?.subject || ''}
                onSuccess={(newTicket) => {
                  setActiveExternalTicketId(newTicket.id);
                }}
              />
            </div>
            {/* External Tickets Tabs */}
            {externalTickets.length > 0 ? (
              <Tabs
                value={activeExternalTicketId || ''}
                onValueChange={handleExternalTicketTabChange}
                className="w-full"
              >
                <div
                  className="ticket-internal-tabs overflow-x-auto"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#e5e7eb transparent'
                  }}
                >
                  <TabsList className="inline-flex h-auto w-max min-w-full justify-start gap-1 rounded-none border-0 bg-transparent px-3 pb-2 pt-0">
                    {externalTickets.map((ticket) => {
                      // Extract content from brackets [...]
                      const extractBracketContent = (subject: string) => {
                        const match = subject.match(/\[([^\]]+)\]/);
                        return match ? match[1] : subject;
                      };

                      const displayTitle = extractBracketContent(ticket.subject);

                      return (
                        <TabsTrigger
                          key={ticket.id}
                          value={ticket.id}
                          className="ticket-internal-tab flex-shrink-0 rounded-md border border-transparent px-2.5 py-1 text-[10px] font-semibold text-slate-500 transition"
                          title={`${ticket.ticket_number} - ${ticket.subject}`}
                        >
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            <span className="font-medium text-xs">
                              {displayTitle}
                            </span>
                          </div>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </div>
              </Tabs>
            ) : (
              <div className="px-3 py-1.5 text-xs text-slate-500">
                {t('common:x4.noExternalTickets', 'Keine External Tickets')}
              </div>
            )}
          </div>
          {!isNebenkonversationCollapsed && (
            <div ref={externalConversationRef} className="flex-1 overflow-hidden flex flex-col">
              {/* External Ticket Info Header */}
              {activeExternalTicket && (
                <div className="ticket-internal-meta border-b px-3.5 py-2">
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
                    <ExternalLink className="h-3.5 w-3.5 text-slate-500" />
                    <span className="font-semibold text-slate-700">{activeExternalTicket.ticket_number}</span>
                    <span className="text-[10px] text-slate-400">•</span>
                    <span className="text-[11px]">{activeExternalTicket.metadata?.external_party_email}</span>
                    {activeExternalTicket.metadata?.external_type && (
                      <>
                        <span className="text-xs text-slate-400">•</span>
                        <span className="ticket-internal-type rounded-md px-2 py-0.5 text-[10px] font-semibold">
                          {activeExternalTicket.metadata.external_type === 'supplier' ? 'Lieferant' :
                            activeExternalTicket.metadata.external_type === 'warehouse' ? 'Lager' :
                              activeExternalTicket.metadata.external_type === 'shipping' ? 'Versand' :
                                activeExternalTicket.metadata.external_type === 'vendor' ? 'Anbieter' :
                                  activeExternalTicket.metadata.external_type === 'partner' ? 'Partner' :
                                    'Sonstiges'}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}
              <ScrollArea
                className="ticket-internal-thread flex-1"
              >
                <div>
                  {loadingActiveExternalTicket ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex justify-start">
                          <div className="max-w-[80%] bg-gray-100 rounded-lg p-3 animate-pulse">
                            <div className="h-4 bg-gray-300 rounded mb-2"></div>
                            <div className="h-3 bg-gray-300 rounded w-3/4"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (activeExternalTicketData?.data?.comments && activeExternalTicketData.data.comments.length > 0) || (externalNotesQuery.data?.data?.notes && externalNotesQuery.data.data.notes.length > 0) ? (
                    (() => {
                      // Transform comments
                      const transformedComments = (activeExternalTicketData?.data?.comments || []).map(comment =>
                        transformApiCommentToFrontend(
                          comment,
                          activeExternalTicketData?.data?.ticket?.requester_id || '',
                          true // isExternalTicket = true
                        )
                      );

                      // Get notes for external ticket and mark them
                      const externalNotes = (externalNotesQuery.data?.data?.notes || []).map((note: Note) => ({
                        ...note,
                        isNote: true
                      }));

                      // Merge comments and notes
                      const merged = [...transformedComments, ...externalNotes];

                      // Sort by creation time
                      merged.sort((a, b) => {
                        const timeA = a.isNote
                          ? new Date(a.created_at).getTime()
                          : new Date(a.timestamp).getTime();
                        const timeB = b.isNote
                          ? new Date(b.created_at).getTime()
                          : new Date(b.timestamp).getTime();
                        return timeA - timeB;
                      });

                      return merged;
                    })().map((message: any) => {
                      // If it's a note, render special note UI
                      if (message.isNote) {
                        return (
                          <div
                            key={`note-${message.id}`}
                            className="ticket-internal-message ticket-internal-message--note group relative w-full border-b px-4 py-3.5 transition"
                          >
                            <div className="flex items-start">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className="ticket-internal-note-badge rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                                    Internal Note
                                  </Badge>
                                  <span className="text-xs text-slate-500">
                                    {formatDateTime(message.created_at)}
                                  </span>
                                  {(message.author_name || message.author_email) && (
                                    <span className="text-xs font-medium text-slate-600">
                                      · {message.author_name || message.author_email?.split('@')[0]}
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="text-sm leading-relaxed text-slate-700"
                                  dangerouslySetInnerHTML={{ __html: sanitizeContent(message.content) }}
                                />

                                {/* Note Attachments */}
                                {message.attachments_url && message.attachment_count > 0 && (
                                  <div className="mt-3 text-sm">
                                    <InlineAttachmentPreview
                                      attachmentsUrl={message.attachments_url}
                                      attachmentCount={message.attachment_count}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Normal comment render
                      const comment = message;
                      const isCustomer = comment.sender === "customer";
                      const isInternal = comment.isInternal;
                      return (
                        <div
                          key={comment.id}
                          className={cn(
                            "ticket-internal-message group relative w-full border-b px-4 py-3.5 transition",
                            isInternal
                              ? "ticket-internal-message--internal"
                              : isCustomer
                                ? "ticket-internal-message--customer"
                                : "ticket-internal-message--agent"
                          )}
                        >
                          <div className="flex items-start">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-semibold text-slate-900">
                                  {comment.authorName || comment.author?.email?.split('@')[0]}
                                </span>
                                {!isCustomer && (
                                  <Badge
                                    className={(comment as any).authorRole === 'warehouse'
                                      ? "ticket-internal-role-badge rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                                      : "ticket-internal-role-badge rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"}
                                  >
                                    {(comment as any).authorRole === 'warehouse' ? 'Lager' : 'Agent'}
                                  </Badge>
                                )}
                                {isInternal && (
                                  <Badge
                                    className="ticket-internal-note-badge rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                                  >
                                    {t('common:x1.internalNote', 'Interne Notiz')}
                                  </Badge>
                                )}
                                <span className="text-xs text-slate-500">
                                  {comment.timestamp ? formatDateTime(comment.timestamp) : 'Datum unbekannt'}
                                </span>
                              </div>

                              <div
                                className="text-sm leading-relaxed text-slate-700"
                                dangerouslySetInnerHTML={{ __html: sanitizeContent(comment.content) }}
                              />

                              {comment.hasAttachments && (
                                <div className="mt-3 text-sm">
                                  <InlineAttachmentPreview
                                    attachmentsUrl={comment.attachmentsUrl}
                                    attachmentCount={getRealAttachmentCount(comment)}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : activeExternalTicket ? (
                    <div className="py-8 text-center text-slate-500">
                      <p>{t('common:x4.noMsgsInExtTicket', 'Noch keine Nachrichten in diesem External Ticket.')}</p>
                      <p className="text-sm">
                        {t('common:x4.startConversationWith', 'Starten Sie eine Unterhaltung mit')} {activeExternalTicket.metadata?.external_party_name || 'External Party'}.
                      </p>
                    </div>
                  ) : (
                    <div className="py-8 text-center text-slate-500">
                      <p>{t('ticket:ui.noExtConv', 'Keine externe Unterhaltung ausgewählt.')}</p>
                      <p className="text-sm">
                        Erstellen Sie eine neue Unterhaltung oder wählen Sie eine vorhandene aus.
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>

              {/* External Ticket Reply Section */}
              <div className="ticket-internal-composer border-t flex-shrink-0">
                <div className="space-y-1.5">
                  {/* Show uploaded attachments */}
                  {externalUploadedFiles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pb-1.5">
                      {externalUploadedFiles.map((file) => (
                        <div
                          key={file.id}
                          className="group flex items-center gap-1 rounded-full border border-slate-200 bg-white/80 px-2 py-0.5 text-[10px] text-slate-600 shadow-sm"
                        >
                          <PaperclipIcon className="h-3 w-3" />
                          <span className="max-w-[140px] truncate">{file.name}</span>
                          <button
                            onClick={() => {
                              setExternalUploadedFiles(prev => prev.filter(f => f.id !== file.id));
                            }}
                            className="ml-1 text-slate-400 transition hover:text-rose-500"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Message Input */}
                  <div>
                    {/* İç konuşmada bekleyen KI-Entwurf varsa — ana ticketteki 'KI-Antwort bereit' çipinin aynısı */}
                    {(() => {
                      const extT: any = (activeExternalTicketData as any)?.data?.ticket;
                      const extDraft = extT?.agent_draft_status === 'pending' ? extT?.agent_draft_reply : null;
                      if (!extDraft) return null;
                      return (
                        <button
                          type="button"
                          onClick={() => {
                            let t = String(extDraft || '')
                              .replace(/&amp;/g, '&')
                              .replace(/&lt;\s*(\/?)(br|strong|b|p|ul|ol|li|em|i|div)\s*\/?\s*&gt;/gi, '<$1$2>')
                              .replace(/\r\n/g, '\n').replace(/\n{2,}/g, '<br><br>').replace(/\n/g, '<br>')
                              .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
                            setExternalMessage(t);
                          }}
                          title={t('ticket:action.toEditorInternal', 'Gespeicherten KI-Entwurf (interne Kommunikation) in den Editor übernehmen')}
                          className="mb-1.5 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition"
                        >
                          <img src={agentIcon} alt="" className="h-3.5 w-3.5 object-contain" />
                          {t('common:x1.aiReplyReady', 'KI-Antwort bereit')}
                        </button>
                      );
                    })()}
                    <RichTextEditor
                      className="ticket-internal-editor"
                      value={externalMessage}
                      onChange={setExternalMessage}
                      placeholder={activeExternalTicket ?
                        `Nachricht an ${activeExternalTicket.metadata?.external_party_name || 'External Party'} eingeben... (Shift+Enter für neue Zeile)` :
                        'Wählen Sie ein External Ticket aus...'}
                      onSubmit={handleExternalTicketReply}
                      disabled={!activeExternalTicket || externalReplyMutation.isPending}
                      onImagePaste={(files) => handleExternalFileUpload(files)}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="ticket-internal-composer-actions flex items-center justify-between border-t px-2.5 py-2">
                    <div className="flex items-center gap-1.5">
                      {/* File Upload */}
                      <input
                        type="file"
                        multiple
                        id="external-file-upload"
                        className="hidden"
                        onChange={(e) => {
                          handleExternalFileUpload(e.target.files);
                          e.target.value = '';
                        }}
                      />
                      <Popover open={showExternalAttachmentChoice} onOpenChange={setShowExternalAttachmentChoice}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isUploadingExternal || !activeExternalTicket}
                            className="ticket-internal-action flex items-center justify-center rounded-md p-0 transition"
                            title={isUploadingExternal ? 'Upload läuft…' : 'Anhang'}
                            aria-label={isUploadingExternal ? 'Upload läuft…' : 'Anhang'}
                          >
                            {isUploadingExternal
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <PaperclipIcon className="h-4 w-4" />}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-2" align="start">
                          <div className="space-y-1">
                            <button
                              onClick={() => {
                                setShowExternalAttachmentChoice(false);
                                document.getElementById('external-file-upload')?.click();
                              }}
                              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-slate-100"
                            >
                              <Upload className="h-3.5 w-3.5 text-slate-500" />
                              <span>{t('ticket:action.uploadFile', 'Datei hochladen')}</span>
                            </button>
                            <button
                              onClick={async () => {
                                setShowExternalAttachmentChoice(false);
                                setGalleryTarget('external');
                                setIsLoadingGallery(true);
                                setShowGalleryModal(true);
                                try {
                                  const response = await ticketsService.getTicketGallery(ticketId);
                                  if (response.success) {
                                    setGalleryData(response.data.gallery);
                                  }
                                } catch (error) {
                                  console.error('Failed to load gallery:', error);
                                } finally {
                                  setIsLoadingGallery(false);
                                }
                              }}
                              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-slate-100"
                            >
                              <ImageIcon className="h-3.5 w-3.5 text-slate-500" />
                              <span>{t('ticket:action.fromGallery', 'Aus Galerie wählen')}</span>
                            </button>
                          </div>
                        </PopoverContent>
                      </Popover>

                      {/* Tools Menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="ticket-internal-action flex items-center justify-center rounded-md p-0 transition"
                            title={t('common:x4.tools', 'Tools')}
                            aria-label={t('common:x4.tools', 'Tools')}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56 rounded-xl border border-slate-200/80 bg-white shadow-lg">
                          <DropdownMenuItem onClick={() => setShowTextSnippets({ isOpen: true, context: 'external' })} className="text-xs">
                            <FileText className="mr-2 h-4 w-4" /> {t('common:x4.snippet', 'Textbaustein')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setShowKundentextPopover(true)}
                            className="text-xs"
                          >
                            <User className="mr-2 h-4 w-4" /> {t('common:x4.customerText', 'Kundentext')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {/* Interne Notiz Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsInternalExternal(!isInternalExternal)}
                        className={cn(
                          "ticket-internal-action flex items-center justify-center rounded-md p-0 transition",
                          isInternalExternal
                            ? "ticket-internal-action--active"
                            : ""
                        )}
                        title={t('common:x1.internalNote', 'Interne Notiz')}
                        aria-label={t('common:x1.internalNote', 'Interne Notiz')}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Send Button */}
                    <Button
                      size="sm"
                      className={cn(
                        "ticket-internal-send flex items-center justify-center rounded-full px-4 py-1.5 text-[11px] font-semibold transition",
                        isInternalExternal
                          ? "ticket-internal-send--note"
                          : ""
                      )}
                      onClick={handleExternalTicketReply}
                      disabled={!externalMessage.replace(/<[^>]*>/g, '').trim() || !activeExternalTicket || externalReplyMutation.isPending || createNoteMutation.isPending}
                    >
                      {(externalReplyMutation.isPending || createNoteMutation.isPending)
                        ? (isInternalExternal ? 'Speichern...' : 'Wird gesendet…')
                        : (isInternalExternal ? 'Notiz' : 'Senden')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* AI Suggestions Dialog */}
      <Dialog open={showAiSuggestions} onOpenChange={setShowAiSuggestions}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('ticket:ui.aiSuggestions', 'KI-Antwortvorschläge')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {aiSuggestions.map((suggestion, index) => (
              <Card key={index} className="overflow-hidden">
                <CardContent className="p-4">
                  <p className="text-sm">{suggestion}</p>
                  <div className="flex justify-end mt-3 space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => applyAiSuggestion(suggestion)}
                    >
                      {t('common:x4.useThisReply', 'Diese Antwort verwenden')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Text Snippets Dialog */}
      <Dialog
        open={showTextSnippets.isOpen}
        onOpenChange={(isOpen) => setShowTextSnippets({ isOpen, context: showTextSnippets.context })}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('ticket:section.snippets', 'Textbausteine')}</DialogTitle>
          </DialogHeader>
          <Input placeholder={t('ticket:ph.searchSnippets', 'Bausteine suchen...')} className="mb-4" />
          {isLoadingSnippets && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">{t('ticket:ui.loadingSnippets', 'Lade Textbausteine...')}</p>
            </div>
          )}
          <Tabs defaultValue="general">
            <TabsList className="mb-4">
              {[...new Set(snippets.map(s => s.category))].map(category => (
                <TabsTrigger key={category} value={category}>
                  {getCategoryLabel(category)}
                </TabsTrigger>
              ))}
            </TabsList>
            {[...new Set(snippets.map(s => s.category))].map(category => (
              <TabsContent key={category} value={category} className="space-y-4">
                {snippets
                  .filter((s) => s.category === category)
                  .map((snippet) => (
                    <Card key={snippet.id} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="font-medium mb-1">{snippet.name}</h3>
                            {snippet.shortcut && (
                              <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded inline-block mb-2">
                                {t('common:x4.shortcutColon', 'Shortcut:')} {snippet.shortcut}
                              </p>
                            )}
                            {snippet.description && (
                              <p className="text-xs text-gray-500 mb-2">{snippet.description}</p>
                            )}
                          </div>
                          <div className="text-xs text-gray-400">
                            {t('common:x4.usedColon', 'Verwendet:')} {snippet.usageCount || 0}x
                          </div>
                        </div>
                        <p className="text-sm border-l-4 border-gray-200 pl-3 py-2 bg-gray-50 rounded">
                          {snippet.content}
                        </p>
                        {snippet.tags && snippet.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {snippet.tags.map((tag, index) => (
                              <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        {snippet.attachmentIds && snippet.attachmentIds.length > 0 && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-gray-600">
                            <PaperclipIcon className="h-3 w-3" />
                            <span>{snippet.attachmentIds.length} Datei{snippet.attachmentIds.length > 1 ? 'en' : ''} angehängt</span>
                          </div>
                        )}
                        <div className="flex justify-end mt-3 space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => applyTextSnippet(snippet)}
                          >
                            {t('common:x4.insert', 'Einfügen')}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                {snippets.filter((s) => s.category === category).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>{t('common:x4.noSnippetsInCategory', 'Keine Textbausteine in dieser Kategorie gefunden.')}</p>
                  </div>
                )}
              </TabsContent>
            ))}
            {snippets.length === 0 && !isLoadingSnippets && (
              <div className="text-center py-8 text-gray-500">
                <p>{t('ticket:ui.noSnippets', 'Keine Textbausteine gefunden.')}</p>
              </div>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Kundentext Dialog */}
      <Dialog open={showKundentextPopover} onOpenChange={setShowKundentextPopover}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('ticket:ui.selectSnippet', 'Kundentext auswählen')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {(() => {
              // Use main ticket's customer messages (apiComments from parent ticket)
              // Filter by email_source_type='incoming' OR author_role='customer'
              const customerMsgs = apiComments
                .filter(c => c.email_source_type === 'incoming' || c.emailSourceType === 'incoming' || c.author_role === 'customer')
                .map(c => ({
                  id: c.id,
                  content: c.content || c.body_text || '',
                  timestamp: c.created_at
                }));

              if (customerMsgs.length === 0) {
                return (
                  <p className="text-sm text-slate-500 italic py-4 text-center">{t('common:x4.noCustomerMsgsInMain', 'Keine Kundennachrichten im Hauptticket vorhanden.')}</p>
                );
              }

              return customerMsgs.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    "rounded-lg border px-4 py-3 cursor-pointer transition",
                    selectedKundentexts.has(msg.id)
                      ? "border-[#0F99FD] bg-blue-50/50 ring-2 ring-[#0F99FD]/20"
                      : "border-slate-200 hover:border-[#0F99FD]/40 hover:bg-rose-50/30"
                  )}
                  onClick={() => {
                    setSelectedKundentexts(prev => {
                      const newSet = new Set(prev);
                      if (newSet.has(msg.id)) {
                        newSet.delete(msg.id);
                      } else {
                        newSet.add(msg.id);
                      }
                      return newSet;
                    });
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-500">
                      {formatDateTime(msg.timestamp)}
                    </span>
                    {selectedKundentexts.has(msg.id) && (
                      <Badge className="rounded-full bg-[#0F99FD] px-2 py-0.5 text-[10px] text-white">
                        {t('common:x4.selected', 'Ausgewählt')}
                      </Badge>
                    )}
                  </div>
                  <div
                    className="text-sm text-slate-700 line-clamp-4"
                    dangerouslySetInnerHTML={{ __html: sanitizeContent(msg.content.substring(0, 500) + (msg.content.length > 500 ? '...' : '')) }}
                  />
                </div>
              ));
            })()}
          </div>
          {selectedKundentexts.size > 0 && (
            <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedKundentexts(new Set());
                }}
              >
                {t('common:x4.deselect', 'Auswahl aufheben')}
              </Button>
              <Button
                className="bg-[#0F99FD] hover:bg-[#0c84e0] text-white"
                onClick={() => {
                  // Get selected customer messages from main ticket
                  const customerMsgs = apiComments
                    .filter(c => c.email_source_type === 'incoming' || c.emailSourceType === 'incoming' || c.author_role === 'customer')
                    .map(c => ({
                      id: c.id,
                      content: c.content || c.body_text || '',
                      timestamp: c.created_at
                    }));

                  const selectedMsgs = customerMsgs.filter(m => selectedKundentexts.has(m.id));

                  // Build the Kundentext log - subtle gray styling like a real log
                  let kundentextLog = '<br/><br/><div style="border-left: 3px solid #9ca3af; padding-left: 12px; margin-left: 4px;">';
                  kundentextLog += '<div style="color: #6b7280; font-size: 11px; font-weight: 500; margin-bottom: 6px;">--- Kundentext Log ---</div>';

                  selectedMsgs.forEach((msg) => {
                    const date = formatDateTime(msg.timestamp);
                    kundentextLog += `<div style="background: #f3f4f6; padding: 6px 8px; border-radius: 4px; margin: 4px 0; font-size: 11px;">`;
                    kundentextLog += `<div style="color: #9ca3af; font-size: 10px; margin-bottom: 2px;">[${date}]</div>`;
                    kundentextLog += `<div style="color: #6b7280; line-height: 1.4;">${msg.content}</div>`;
                    kundentextLog += `</div>`;
                  });

                  kundentextLog += '</div>';

                  // Append to external message
                  setExternalMessage(prev => prev + kundentextLog);

                  // Reset selection and close dialog
                  setSelectedKundentexts(new Set());
                  setShowKundentextPopover(false);
                }}
              >
                {selectedKundentexts.size} Text{selectedKundentexts.size !== 1 ? 'e' : ''} hinzufügen
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>


      {/* Add Ticket Modal */}
      <TicketListModal
        isOpen={showAddTicketModal}
        onClose={() => setShowAddTicketModal(false)}
        onSelectTicket={addTicketToTabs}
      />

      {/* Merge Tickets Modal */}
      <TicketListModalWithSelection
        isOpen={showMergeTicketModal}
        onClose={() => setShowMergeTicketModal(false)}
        onSelectMultiple={handleMergeTickets}
        excludeTicketId={activeTab || ticketId}
      />

      {/* AI Revision Dialog */}
      <Dialog open={showRevisionDialog} onOpenChange={setShowRevisionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('ticket:ui.aiRevise', 'KI-Antwort überarbeiten')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="revision-prompt">{t('ticket:section.revisionInstruction', 'Überarbeitungsanweisung')}</Label>
              <Textarea
                id="revision-prompt"
                placeholder={t('ticket:ph.revision', 'z.B. Höflicher formulieren, Kürzer machen, Ins Englische übersetzen')}
                value={revisionPrompt}
                onChange={(e) => setRevisionPrompt(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRevisionDialog(false);
                  setRevisionPrompt('');
                }}
              >
                {t('common:action.cancel', 'Abbrechen')}
              </Button>
              <Button
                onClick={handleRevisionSubmit}
                disabled={!revisionPrompt.trim() || revisionMutation.isPending}
              >
                {revisionMutation.isPending ? 'Wird überarbeitet...' : 'Überarbeiten'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Gallery Modal */}
      <Dialog open={showGalleryModal} onOpenChange={setShowGalleryModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-slate-600" />
              {t('common:x4.chooseAttachFromGallery', 'Anhang aus Galerie wählen')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {isLoadingGallery ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
                <span className="ml-2 text-sm text-slate-500">{t('ticket:ui.galleryLoading', 'Galerie wird geladen...')}</span>
              </div>
            ) : galleryData.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <ImageIcon className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>{t('ticket:ui.noAttachments', 'Keine Anhänge in diesem Ticket gefunden.')}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {galleryData.map((ticketGroup) => (
                  <div key={ticketGroup.ticketId} className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={ticketGroup.isExternal ? "secondary" : "default"} className="text-xs">
                        {ticketGroup.isExternal ? 'External' : 'Haupt-Ticket'}
                      </Badge>
                      <span className="text-sm font-semibold text-slate-700">
                        {ticketGroup.ticketNumber}
                      </span>
                      <span className="text-xs text-slate-500 truncate flex-1">
                        {ticketGroup.ticketSubject}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {ticketGroup.attachments.map((attachment) => {
                        const isSelected = selectedGalleryAttachments.has(attachment.id);
                        const isImage = attachment.contentType.startsWith('image/');

                        return (
                          <div
                            key={attachment.id}
                            onClick={() => {
                              setSelectedGalleryAttachments(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(attachment.id)) {
                                  newSet.delete(attachment.id);
                                } else {
                                  newSet.add(attachment.id);
                                }
                                return newSet;
                              });
                            }}
                            className={cn(
                              "relative border rounded-lg p-2 cursor-pointer transition-all hover:shadow-md",
                              isSelected
                                ? "border-[#0F99FD] bg-red-50 ring-2 ring-[#0F99FD]/30"
                                : "border-slate-200 hover:border-slate-300"
                            )}
                          >
                            {isSelected && (
                              <div className="absolute -top-2 -right-2 w-5 h-5 bg-[#0F99FD] rounded-full flex items-center justify-center">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                            )}
                            <div className="aspect-square bg-slate-100 rounded-md flex items-center justify-center overflow-hidden mb-2">
                              {isImage && attachment.previewUrl ? (
                                <img
                                  src={`${(import.meta.env.VITE_API_URL || '').replace(/\/api$/, '')}${attachment.previewUrl}`}
                                  alt={attachment.filename}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    const fallback = target.parentElement?.querySelector('.fallback-icon');
                                    target.style.display = 'none';
                                    if (fallback) fallback.classList.remove('hidden');
                                  }}
                                />
                              ) : null}
                              <div className={cn("flex flex-col items-center justify-center fallback-icon", isImage && attachment.previewUrl && "hidden")}>
                                {isImage ? (
                                  <ImageIcon className="h-8 w-8 text-slate-400" />
                                ) : (
                                  <FileText className="h-8 w-8 text-slate-400" />
                                )}
                                <span className="text-[10px] text-slate-500 mt-1">
                                  {attachment.contentType.split('/')[1]?.toUpperCase() || 'FILE'}
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-slate-700 truncate font-medium" title={attachment.filename}>
                              {attachment.filename}
                            </div>
                            <div className="text-[10px] text-slate-500">
                              {(attachment.sizeBytes / 1024).toFixed(1)} KB
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-slate-600">
              {selectedGalleryAttachments.size} Anhang{selectedGalleryAttachments.size !== 1 ? 'e' : ''} ausgewählt
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowGalleryModal(false);
                  setSelectedGalleryAttachments(new Set());
                }}
              >
                {t('common:action.cancel', 'Abbrechen')}
              </Button>
              <Button
                onClick={() => {
                  // Add selected attachments to uploadedFiles or externalUploadedFiles based on target
                  const selectedAttachments: Array<{
                    id: string;
                    filename: string;
                    size: number;
                    contentType: string;
                  }> = [];

                  galleryData.forEach(ticketGroup => {
                    ticketGroup.attachments.forEach(att => {
                      if (selectedGalleryAttachments.has(att.id)) {
                        selectedAttachments.push({
                          id: att.id,
                          filename: att.filename,
                          size: att.sizeBytes,
                          contentType: att.contentType
                        });
                      }
                    });
                  });

                  if (galleryTarget === 'main') {
                    setUploadedFiles(prev => [...prev, ...selectedAttachments]);
                    setAttachmentIds(prev => [...prev, ...selectedAttachments.map(att => att.id)]);
                  } else {
                    setExternalUploadedFiles(prev => [...prev, ...selectedAttachments.map(att => ({
                      id: att.id,
                      name: att.filename,
                      size: att.size
                    }))]);
                  }
                  setShowGalleryModal(false);
                  setSelectedGalleryAttachments(new Set());
                }}
                disabled={selectedGalleryAttachments.size === 0}
                className="bg-[#0F99FD] hover:bg-[#0c84e0]"
              >
                {selectedGalleryAttachments.size > 0
                  ? `${selectedGalleryAttachments.size} Anhang${selectedGalleryAttachments.size !== 1 ? 'e' : ''} hinzufügen`
                  : 'Hinzufügen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tags Dialog */}
      {/* Reminder Dialog */}
      <ReminderDialog
        open={reminderDialogOpen}
        onOpenChange={(open) => {
          setReminderDialogOpen(open);
          if (!open) {
            setEditingReminder(undefined);
            setSelectedReminderDate(undefined);
          }
        }}
        onSubmit={handleReminderSubmit}
        initialData={editingReminder}
        selectedDate={selectedReminderDate}
        isLoading={createReminderMutation.isPending || updateReminderMutation.isPending}
      />

      {/* Delete Ticket Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ticket:state.deleteConfirm', 'Ticket endgültig löschen?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common:x1.wantTicket', 'Möchten Sie Ticket')} <strong>#{ticket?.ticketNumber}</strong> wirklich löschen?
              <br /><br />
              Diese Aktion kann nicht rückgängig gemacht werden. Alle zugehörigen E-Mails, Notizen, Anhänge und Verlaufsdaten werden ebenfalls gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTicketMutation.isPending}>
              {t('common:action.cancel', 'Abbrechen')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTicket}
              disabled={deleteTicketMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteTicketMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t('common:x4.deletingDots', 'Wird gelöscht...')}
                </>
              ) : (
                <>
                  <Trash className="mr-2 h-4 w-4" />
                  {t('common:x4.deletePermanently', 'Endgültig löschen')}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Status Dialog */}
      <AlertDialog open={spamDialogOpen} onOpenChange={setSpamDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {ticket?.isSpam ? 'Aus dem Archiv entfernen?' : 'Ins Archiv verschieben?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {ticket?.isSpam ? (
                <>
                  {t('common:x1.wantTicket', 'Möchten Sie Ticket')} <strong>#{ticket?.ticketNumber}</strong> aus dem Archiv entfernen?
                  <br /><br />
                  {t('common:x4.ticketBackInList', 'Das Ticket wird wieder in der normalen Ticketliste angezeigt.')}
                </>
              ) : (
                <>
                  {t('common:x1.wantTicket', 'Möchten Sie Ticket')} <strong>#{ticket?.ticketNumber}</strong> ins Archiv verschieben?
                  <br /><br />
                  Das Ticket wird aus der normalen Liste entfernt und erscheint nur noch in der Archiv-Ansicht.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateSpamStatusMutation.isPending}>
              {t('common:action.cancel', 'Abbrechen')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (ticketId) {
                  updateSpamStatusMutation.mutate({
                    ticketId,
                    isSpam: !ticket?.isSpam
                  });
                }
              }}
              disabled={updateSpamStatusMutation.isPending}
              className={ticket?.isSpam ? "bg-slate-600 hover:bg-slate-700" : "bg-blue-600 hover:bg-blue-700"}
            >
              {updateSpamStatusMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t('common:x4.updatingDots', 'Wird aktualisiert...')}
                </>
              ) : (
                <>
                  <Archive className="mr-2 h-4 w-4" />
                  {ticket?.isSpam ? 'Aus dem Archiv entfernen' : 'Archivieren'}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reminder List Dialog */}
      <Dialog open={reminderListOpen} onOpenChange={setReminderListOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {t('common:x4.reminders', 'Erinnerungen')}{' '}
              {selectedReminderDate &&
                `- ${format(selectedReminderDate, 'PP', { locale: dfLocale() })}`}
            </DialogTitle>
          </DialogHeader>
          <ReminderList
            reminders={
              selectedReminderDate
                ? reminders.filter(
                  (r) =>
                    new Date(r.reminder_date).toDateString() ===
                    selectedReminderDate.toDateString()
                )
                : reminders
            }
            onEdit={(reminder) => {
              setReminderListOpen(false);
              handleOpenReminderDialog(undefined, reminder);
            }}
            onDelete={handleDeleteReminder}
            onToggleComplete={handleToggleReminderComplete}
            isLoading={
              deleteReminderMutation.isPending ||
              completeReminderMutation.isPending ||
              uncompleteReminderMutation.isPending
            }
          />
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setReminderListOpen(false)}
            >
              {t('common:action.close', 'Schließen')}
            </Button>
            <Button
              onClick={() => {
                setReminderListOpen(false);
                handleOpenReminderDialog(selectedReminderDate);
              }}
            >
              {t('common:x1.newReminder', 'Neue Erinnerung')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Parent Ticket Modal */}
      <Dialog open={isCreateParentTicketModalOpen} onOpenChange={setIsCreateParentTicketModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('ticket:action.newParentTicket', 'Neues übergeordnetes Ticket erstellen')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="parent-email">E-Mail *</Label>
              <Input
                id="parent-email"
                type="email"
                value={parentTicketForm.email}
                onChange={(e) => setParentTicketForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder={t('shell:ph.customerEmail', 'kunde@beispiel.de')}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="parent-customerName">{t('ticket:section.customerName', 'Kundenname')}</Label>
              <Input
                id="parent-customerName"
                value={parentTicketForm.customerName}
                onChange={(e) => setParentTicketForm(prev => ({ ...prev, customerName: e.target.value }))}
                placeholder={t('shell:ph.fullName', 'Max Mustermann')}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="parent-subject">{t('ticket:ui.subjectReq', 'Betreff *')}</Label>
              <Input
                id="parent-subject"
                value={parentTicketForm.subject}
                onChange={(e) => setParentTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder={t('ticket:ph.collectiveTicket', 'z.B. Sammelticket für Kundenanfragen')}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="parent-organization">{t('ticket:ui.organizationReq', 'Organization *')}</Label>
              <Select
                value={parentTicketForm.organizationId}
                onValueChange={(value) => setParentTicketForm(prev => ({ ...prev, organizationId: value }))}
              >
                <SelectTrigger id="parent-organization">
                  <SelectValue placeholder={t('shell:ph.org', 'Organization auswählen')} />
                </SelectTrigger>
                <SelectContent>
                  {orgsResponse?.data?.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="parent-priority">{t('ticketList:column.priority', 'Priorität')}</Label>
              <Select
                value={parentTicketForm.priority}
                onValueChange={(value) => setParentTicketForm(prev => ({ ...prev, priority: value as any }))}
              >
                <SelectTrigger id="parent-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t('domain:priority.low', 'Niedrig')}</SelectItem>
                  <SelectItem value="normal">{t('domain:priority.normal', 'Normal')}</SelectItem>
                  <SelectItem value="high">{t('domain:priority.high', 'Hoch')}</SelectItem>
                  <SelectItem value="urgent">{t('domain:priority.urgent', 'Dringend')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="flex gap-2 text-sm text-blue-900">
                <span>ℹ️</span>
                <div>
                  <p className="font-semibold mb-1">{t('ticket:state.whatHappens', 'Was passiert?')}</p>
                  <ul className="list-disc list-inside space-y-1 text-xs text-blue-800">
                    <li>{t('ticket:ui.newParentCreated', 'Ein neues übergeordnetes Ticket wird erstellt')}</li>
                    <li>{t('common:x4.currentTicketAsChild', 'Das aktuelle Ticket wird als untergeordnet zugeordnet')}</li>
                    <li>{t('ticket:ui.redirectNewTicket', 'Sie werden zum neuen Ticket weitergeleitet')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateParentTicketModalOpen(false);
                setParentTicketForm({ email: "", customerName: "", subject: "", priority: "normal", organizationId: "" });
              }}
              disabled={createParentTicketMutation.isPending}
            >
              {t('common:action.cancel', 'Abbrechen')}
            </Button>
            <Button
              onClick={() => createParentTicketMutation.mutate(parentTicketForm)}
              disabled={!parentTicketForm.email.trim() || !parentTicketForm.subject.trim() || !parentTicketForm.organizationId.trim() || createParentTicketMutation.isPending}
              className="bg-[#0F99FD] hover:bg-[#0c84e0]"
            >
              {createParentTicketMutation.isPending ? (
                <>
                  <span className="mr-2">⏳</span>
                  {t('common:state.creating', 'Wird erstellt...')}
                </>
              ) : (
                <>
                  <span className="mr-2">✓</span>
                  {t('common:x1.createTicket', 'Ticket erstellen')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketDetail;
